import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { UserCheck, FileText, Search, History, CheckCircle, XCircle, Loader2, LogIn, LogOut, Users, Building2, Briefcase, Cpu, Phone, Mail, Home, User, Ban, Clock, MapPin, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, ArrowLeft, RefreshCw, ShieldOff, Car, Tag, UserPlus, Edit2, Trash2, Download, Calendar, X, Camera, PawPrint, ZoomIn, DoorOpen, DoorClosed, Fingerprint, Check, Wifi, Shield, Monitor, Info, ScanFace, AlertTriangle, Radio } from 'lucide-react';
import { Plus } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { LicensePlate } from '../../ui/LicensePlate';
import { CondoType, getBlockLabel, getUnitLabel, formatUnitLabel, deviceTypeLabel, tagTypeLabel, getBlockArticle } from '../../../utils/terminology';
import { petSpeciesLabel, petBreedLabel } from '../../../utils/petLabels';
import { VEHICLE_PHOTO_ASPECT_CLASS } from '../../../utils/vehiclePhoto';
import { VehiclePhoto } from '../../vehicles/VehiclePhoto';
import { toThumbnailUrl, toOriginalUrl } from '../../../utils/thumbnailUrl';
import { Visitor, Device } from '../types';
import { useDeviceHistory } from '../../../hooks/useDeviceHistory';
import { useVisibleGridItems } from '../../../hooks/useVisibleGridItems';
import { fetchWithAuth, makeAbsolutePhotoUrl, condoApi } from '../../../services/api';
import { ensureOk } from '../../../services/httpError';
import api from '../../../services/api';
import { getPrimaryPhotoUrl } from '../../../utils/photoUtils';
import { historicoForPeople, liberacoesForPeople, LiberacaoRow } from '../../../utils/liberacoes';
import { normalizeCPF, isCPFFormat } from '../../../utils/cpfUtils';
import { createRequestSequence } from '../../../utils/requestSequence';
import { normalizeForSearch } from '../../../utils/textNormalization';
import CountdownTimer from '../../CountdownTimer';
import { VisitorDuration } from './VisitorDuration';
import { RefreshTimer } from './RefreshTimer';
import { StatusFilterDropdown, VisitorStatusFilter } from './StatusFilterDropdown';
import { VehicleFormModal } from './VehicleFormModal';
import { BlockFormModal } from './BlockFormModal';
import { VehicleDetailModal } from './VehicleDetailModal';
import PetFormModal from '../../PetFormModal';
import PetDetailModal from '../../PetDetailModal';
import { Pet } from '../../../services/api';
import { TagFormModal, PersonTag } from './TagFormModal';
import PhotoActionModal from '../../PhotoActionModal';
import PhotoUploadModal from '../../PhotoUploadModal';
import PhotoWebcamModal from '../../PhotoWebcamModal';
import PhotoIPCameraModal from '../../PhotoIPCameraModal';
import toast from 'react-hot-toast';
import { useDebounce } from '../../../hooks/useDebounce';
import { useForceUppercase } from '../../../hooks/useForceUppercase';
import { DeviceInfo, DeviceOperations, CommunicationMode, apiDeviceToDeviceInfo, deviceCommunicationService } from '../../../services/deviceCommunication';
import { DeviceIntegration } from '../../../integrations/devices';
import { syncVehicleRfid, fetchPersonVehicleDeviceIds, syncAllVehiclePlatesToLpr } from '../../../utils/vehicleRfidSync';
import { useSyncProgress } from '../../../contexts/SyncProgressContext';
import { SyncProgressItem } from '../../SyncProgressModal';
import MoradoresSelfRegSidebar from './MoradoresSelfRegSidebar';
import { FilterCombobox } from '../../../components/FilterCombobox';
import VisitorRegistrationModal from '../../VisitorRegistrationModal';
import { ScheduleView, ContractorsView, WindowEditModal, WindowEditState } from '../../../pages/ServiceProviders';
import Keys from '../../../pages/Keys';
import EventosGerais from '../../../pages/EventosGerais';
import { useConfirm } from '../../ui/ConfirmDialog';
import { appendIdentityPhoto } from '../../../services/identityPhotoUpload';
import { residentRelationChipLabel, personRelationLabel } from '../../../utils/relationType';
import { getAbsoluteImageUrl } from '../../../utils/imageUrl';
import { visitDestinationsLabel, visitDestinationsTitle } from '../utils/visitGroups';

// Module-level cache: survives component unmount/remount (e.g. navigating to location-detail and back)
const _unitLookupCache = new Map<string, Map<string, string>>();

// Custom scrollbar styles - Always visible
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #1f2937;
    border-radius: 5px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #4b5563;
    border-radius: 5px;
    border: 2px solid #1f2937;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
  }
  .custom-scrollbar {
    scrollbar-width: auto;
    scrollbar-color: #4b5563 #1f2937;
  }
`;

interface PaginationInfo {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface MainContentAreaProps {
  currentView: 'visitors' | 'cadastro' | 'history' | 'location-detail' | 'person-profile' | 'register-visitor' | 'register-resident' | 'expired-visitors' | 'device-audit' | 'encomendas' | 'eventos-gerais' | 'zone-occupancy' | 'pre-registrations' | 'areas-comuns' | 'normas-internas' | 'ocorrencias' | 'mural-anuncios' | 'livro' | 'solicitacoes';
  selectedCadastro: string | null;
  selectedCondo: string;
  activeVisitors: Visitor[];           // Current page
  allVisitors?: Visitor[];             // ALL visitors (for counts)
  selectedDeviceHistory: Device | null;
  isLoadingVisitors?: boolean;
  customerId?: string | null;
  customerType?: 'condominio' | 'empresa';
  mobileAppEnabled?: boolean;
  condoType?: CondoType;
  cadastroRefreshTrigger?: number;
  lastRefreshTime?: number;
  pagination?: PaginationInfo;
  onPageChange?: (page: number) => void;
  onNextPage?: () => void;
  onPrevPage?: () => void;
  onPersonClick?: (personId: string) => void;
  onDeviceClick?: (deviceId: string) => void;
  onLocationClick?: (locationId: string, locationType: 'unit' | 'department' | 'block') => void;
  onRevokeAccess?: (visitor: Visitor) => void;
  onManualCheckIn?: (visitor: Visitor) => void;
  onManualCheckOut?: (visitor: Visitor) => void;
  onApproveAccess?: (visitor: Visitor) => void;
  onBackFromDeviceHistory?: () => void;
  onDeviceAction?: (deviceId: string, action: string) => void | Promise<void>;
  onResetCadastro?: () => void;
  onRefreshVisitors?: () => Promise<void>;
  onRegisterResident?: () => void;
  onRegisterEmployee?: () => void;
  visitorWsStatus?: 'disconnected' | 'connecting' | 'connected' | 'reconnecting';
  getWsStats?: () => import('../../../services/visitorWebSocketClient').WsStats | null;
  onStatusFilterChange?: (filter: string) => void;
  onSearchModeChange?: (enabled: boolean) => void;
  petsEnabled?: boolean;
  /** False for a porteiro without porteiro_can_approve_self_registrations — hides the
   *  moradores auto-cadastro sidebar entirely, since every action in it would 403. */
  canApproveSelfRegistrations?: boolean;
  lightTheme?: boolean;
}

// Types for cadastro data
interface PersonRole {
  id: string;
  role_type: string;
  department_name?: string;
  unit_name?: string;
  role_metadata?: Record<string, any>;
}

interface Person {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  profile_picture_url?: string;
  identity_photo?: { photo_url: string };
  cpf?: string;
  roles?: PersonRole[];
  vehicles?: { id: string; plate: string }[];
}

interface Unit {
  id: string;
  name: string;
  block_name?: string;
  residents_count?: number;
}

interface Block {
  id: string;
  name: string;
  created_at?: string;
}

/** A unit as the flat search endpoint returns it — carries both addressings at once. */
interface FlatUnitRow {
  id: string;
  number: string;
  block_id?: string;
  block_name?: string;
  street_id?: string;
  street_name?: string;
  street_number?: string;
}

/** A street card in Cadastros → Unidades, "Ruas" mode. */
interface StreetRow {
  kind: 'street';
  id: string;
  name: string;
  unitCount: number;
}

/** A número card — one lot, addressed by its street number. */
interface StreetUnitRow {
  kind: 'street-unit';
  id: string;
  streetNumber: string;
  internalLabel: string;
}

interface Department {
  id: string;
  name: string;
  description?: string;
  employee_count?: number;
}

interface Vehicle {
  id: string;
  plate: string;
  rfid_tag?: string;
  brand?: string;
  model?: string;
  color?: string;
  year?: number;
  owner_type?: string;
  person_id?: string;
  person?: Person;
  unit_id?: string;
  unit?: { id: string; number: string; block?: { id: string; name: string }; street?: { id: string; name: string }; street_number?: string };
  // owner_unit is the OWNING PERSON's unit — distinct from unit/unit_id above, which is
  // only ever set for a vehicle registered directly against a unit with NO person owner.
  owner_unit?: { id: string; number: string; block?: { id: string; name: string }; street?: { id: string; name: string }; street_number?: string };
  is_active: boolean;
  photo_url?: string;
  created_at: string;
}

interface IoTDevice {
  id: string;
  name: string;
  device_type: string;
  status: string;
  ip_address?: string;
  local_ip_address?: string;
  zone_name?: string;
}

// Interface for historical visitor
interface HistoricalVisitor {
  id: string;
  name: string;
  photo: string | null;
  unit: string;
  lastVisit: string;
  lastVisitDate: string;
  personId: string;
  visitorState?: string; // awaiting_entry, inside, exited, expired, consumed
  roleType?: string; // visitor, service_provider
}

// Helper component to show remaining time for awaiting visitors
const TimeRemaining: React.FC<{ expiresAt?: string; label?: string }> = ({ expiresAt, label = '' }) => {
  const [timeLeft, setTimeLeft] = useState<string>('');
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!expiresAt) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();
      const diff = expiry - now;

      if (diff <= 0) {
        setTimeLeft('Expirado');
        setIsExpired(true);
        return;
      }

      setIsExpired(false);
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}min`);
      } else {
        setTimeLeft(`${minutes}min ${seconds}s`);
      }
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000); // Update every second

    return () => clearInterval(interval);
  }, [expiresAt]);

  if (!expiresAt || !timeLeft) return null;

  return (
    <div className={cn(
      "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md",
      isExpired ? "bg-red-500/20" : "bg-yellow-500/10"
    )}>
      <Clock className={cn("h-4 w-4", isExpired ? "text-red-400" : "text-yellow-400")} />
      {label && <span className={cn("text-xs font-bold", isExpired ? "text-red-300" : "text-yellow-300")}>{label}</span>}
      <span className={cn("text-xs font-extrabold whitespace-nowrap", isExpired ? "text-red-400" : "text-yellow-400")}>{timeLeft}</span>
    </div>
  );
};

// Helper component to show elapsed time (how long visitor has been inside)
const TimeElapsed: React.FC<{ startTime?: string; label?: string }> = ({ startTime, label = '' }) => {
  const [timeElapsed, setTimeElapsed] = useState<string>('');

  useEffect(() => {
    if (!startTime) return;

    const calculateTimeElapsed = () => {
      const now = new Date().getTime();
      const start = new Date(startTime).getTime();
      const diff = now - start;

      if (diff < 0) {
        setTimeElapsed('0min');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (hours > 0) {
        setTimeElapsed(`${hours}h ${minutes}min`);
      } else {
        setTimeElapsed(`${minutes}min ${seconds}s`);
      }
    };

    calculateTimeElapsed();
    const interval = setInterval(calculateTimeElapsed, 1000); // Update every second

    return () => clearInterval(interval);
  }, [startTime]);

  if (!startTime || !timeElapsed) return null;

  return (
    <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-500/10 rounded-md">
      <Clock className="h-4 w-4 text-blue-400" />
      {label && <span className="text-xs font-bold text-blue-300">{label}</span>}
      <span className="text-xs font-extrabold text-blue-400 whitespace-nowrap">{timeElapsed}</span>
    </div>
  );
};

// Tiny live-text helpers for the status badge
const LiveElapsed: React.FC<{ startTime?: string }> = ({ startTime }) => {
  const [text, setText] = useState('');
  useEffect(() => {
    if (!startTime) return;
    const calc = () => {
      const diff = Date.now() - new Date(startTime).getTime();
      if (diff < 0) { setText('0min'); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setText(h > 0 ? `${h}h ${m}min` : `${m}min ${s}s`);
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [startTime]);
  return text ? <span className="opacity-70">· {text}</span> : null;
};

const LiveRemaining: React.FC<{ expiresAt?: string }> = ({ expiresAt }) => {
  const [text, setText] = useState('');
  useEffect(() => {
    if (!expiresAt) return;
    const calc = () => {
      const diff = new Date(expiresAt).getTime() - Date.now();
      if (diff <= 0) { setText('expirado'); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setText(h > 0 ? `${h}h ${m}min` : m > 0 ? `${m}min ${s}s` : `${s}s`);
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  return text ? <span className="opacity-70">· {text}</span> : null;
};

/**
 * "Liberado até 29/10/2026" — for a visitor authorized for a PERIOD.
 *
 * These roles have no entry window: the backend copies valid_until into entry_window_expires_at as
 * an access ceiling, so feeding it to LiveRemaining produced "Aguardando · 512h 34min", a countdown
 * to the end of the authorization presented as if the person were about to walk in.
 */
const PeriodAccessUntil: React.FC<{ until?: string }> = ({ until }) => {
  if (!until) return <>Liberado</>;
  const d = new Date(until);
  if (Number.isNaN(d.getTime())) return <>Liberado</>;
  return <>Liberado até {d.toLocaleDateString('pt-BR')}</>;
};

// True once today's clock time has passed the given "HH:MM" daily deadline
const isPastDailyTime = (hhmm: string): boolean => {
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return false;
  const deadline = new Date();
  deadline.setHours(h, m, 0, 0);
  return Date.now() > deadline.getTime();
};

// Format a visitor's access-validity end date — "DD/MM/AAAA", set at creation time in the
// Novo Visitante modal (valid_until) but previously never surfaced on this card, only in the
// person detail modal.
const formatValidUntilDate = (isoStr: string): string => {
  return new Date(isoStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Format a visit timestamp: shows time if today, otherwise "DD/MM HH:MM"
const formatVisitTimestamp = (isoStr: string): string => {
  const d = new Date(isoStr);
  const now = new Date();
  const todayStr = now.toDateString();
  if (d.toDateString() === todayStr) {
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) +
    ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

// Memoized VisitorCard component to prevent unnecessary re-renders and image reloads
const VisitorCard = React.memo<{
  visitor: Visitor;
  onPersonClick?: (personId: string) => void;
  onUnitClick?: (unitId: string, unitName: string) => void;
  onManualCheckIn?: (visitor: Visitor) => void;
  onManualCheckOut?: (visitor: Visitor) => void;
  onRevokeAccess?: (visitor: Visitor) => void;
  onApproveAccess?: (visitor: Visitor) => void;
  onPhotoZoom: (url: string, name: string) => void;
  lightTheme?: boolean;
  /** Customer's "Saída Obrigatória" daily deadline ("HH:MM") for prestadores */
  exitByTime?: string;
}>(({ visitor, onPersonClick, onUnitClick, onManualCheckIn, onManualCheckOut, onRevokeAccess, onApproveAccess, onPhotoZoom, lightTheme = false, exitByTime }) => {
  const isServiceProvider = visitor.roleType === 'service_provider';
  const isInside = visitor.status === 'inside';

  // The clearance is over — revoked, expired or used up. Prefers the server's computed verdict
  // (models/liberacao.go) and falls back to the card's own status against an older backend.
  const clearanceEnded =
    visitor.liberacao
      ? ['revogada', 'vencida', 'encerrada'].includes(visitor.liberacao.status)
      : visitor.status === 'revoked';

  // Prestador still 'inside' past the customer's mandatory exit time — ticks so the
  // highlight appears live as the clock crosses the deadline, no refresh needed.
  const [isOverdue, setIsOverdue] = useState(false);
  useEffect(() => {
    if (!isServiceProvider || !isInside || !exitByTime) { setIsOverdue(false); return; }
    const check = () => setIsOverdue(isPastDailyTime(exitByTime));
    check();
    const id = setInterval(check, 15000);
    return () => clearInterval(id);
  }, [isServiceProvider, isInside, exitByTime]);

  return (
    <div
      onClick={() => onPersonClick?.(visitor.personId)}
      className={cn(
        "rounded-lg border transition-all flex flex-col gap-2 p-2.5 cursor-pointer",
        lightTheme && "shadow-sm hover:shadow-md",
        // Background color based on role type and theme
        isOverdue
          ? lightTheme ? "bg-red-50" : "bg-gradient-to-br from-red-950/60 to-gray-800"
          : isServiceProvider
          ? lightTheme ? "bg-orange-50" : "bg-gradient-to-br from-gray-800 to-gray-700"
          : lightTheme ? "bg-white" : "bg-gray-950",
        // Border color based on status and role
        visitor.status === 'revoked'
          ? lightTheme ? "border-red-300 hover:border-red-400 hover:ring-4 hover:ring-red-200 opacity-70" : "border-red-800/50 hover:border-red-600/80 hover:ring-4 hover:ring-red-700/40 opacity-70"
          : isOverdue
          ? lightTheme ? "border-red-500 ring-2 ring-red-300 hover:ring-4 animate-pulse" : "border-red-500 ring-2 ring-red-500/50 hover:ring-4 animate-pulse"
          : isServiceProvider
          ? visitor.status === 'inside'
            ? "border-orange-500/70 hover:border-orange-400 ring-2 ring-orange-500/20 hover:ring-8 hover:ring-orange-500/40"
            : visitor.status === 'awaiting'
            ? "border-orange-400/70 hover:border-orange-300 ring-2 ring-orange-400/20 hover:ring-8 hover:ring-orange-400/40"
            : lightTheme ? "border-orange-200 hover:border-orange-300 hover:ring-4 hover:ring-orange-100 opacity-60" : "border-orange-700/40 hover:border-orange-500/70 hover:ring-4 hover:ring-orange-500/30 opacity-60"
          : visitor.status === 'inside'
          ? lightTheme ? "border-green-400 hover:border-green-500 ring-2 ring-green-100 hover:ring-4 hover:ring-green-200" : "border-green-800/50 hover:border-green-500/80 hover:ring-4 hover:ring-green-600/40"
          : visitor.status === 'awaiting'
          ? lightTheme ? "border-yellow-400 hover:border-yellow-500 ring-2 ring-yellow-100 hover:ring-4 hover:ring-yellow-200" : "border-yellow-800/50 hover:border-yellow-500/80 hover:ring-4 hover:ring-yellow-600/40"
          : visitor.status === 'pending_approval'
          ? lightTheme ? "border-orange-400 hover:border-orange-500 ring-2 ring-orange-100 hover:ring-4 hover:ring-orange-200" : "border-orange-800/50 hover:border-orange-500/80 hover:ring-4 hover:ring-orange-600/40"
          : lightTheme ? "border-gray-300 hover:border-gray-400 hover:ring-4 hover:ring-gray-200 opacity-60" : "border-gray-800 hover:border-gray-500 hover:ring-4 hover:ring-gray-500/30 opacity-60"
      )}
    >
      {/* Top: Full name + role badge */}
      <div className="flex items-center justify-between gap-2 min-w-0">
        <h3
          className={cn("text-sm font-bold truncate leading-snug min-w-0", lightTheme ? "text-gray-900" : "text-gray-100")}
        >
          {visitor.name}
        </h3>
        <span className={cn(
          "text-xs font-bold px-2 py-0.5 rounded flex-shrink-0 border",
          isServiceProvider
            ? lightTheme ? "bg-orange-100 text-orange-700 border-orange-300" : "bg-orange-500/20 text-orange-300 border-orange-500/30"
            : lightTheme ? "bg-blue-100 text-blue-700 border-blue-300" : "bg-blue-500/20 text-blue-300 border-blue-500/30"
        )}>
          {isServiceProvider ? 'PRESTADOR' : 'VISITANTE'}
        </span>
      </div>

      {/* Overdue prestador alert — full-width so the phrase reads comfortably */}
      {isOverdue && (
        <div
          className={cn(
            "flex items-center gap-1.5 px-2 py-1 rounded animate-pulse",
            lightTheme ? "bg-red-100 text-red-700" : "bg-red-500/20 text-red-300"
          )}
          title={`Deveria ter saído até ${exitByTime}`}
        >
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="text-xs font-bold leading-tight">Prestador ativo após o horário permitido</span>
        </div>
      )}

      {/* Middle: Photo (left) + Info (right) */}
      <div className="flex gap-2.5">
        {/* Left: Photo + Action Buttons */}
        <div className="flex flex-col gap-2 flex-shrink-0">
          {/* Photo - Clickable for zoom */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              const photoUrl = visitor.photo ? toOriginalUrl(makeAbsolutePhotoUrl(visitor.photo)) : null;
              if (photoUrl) {
                onPhotoZoom(photoUrl, visitor.name);
              }
            }}
            tabIndex={-1}
            className="relative w-[77px] h-24 group cursor-zoom-in overflow-hidden rounded-lg"
            title="Clique para ampliar"
          >
            {visitor.photo ? (
              <img
                src={makeAbsolutePhotoUrl(visitor.photo)}
                alt={visitor.name}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center transition-all duration-300 group-hover:from-blue-600 group-hover:to-blue-700">
                <span className="text-2xl font-bold text-white">
                  {visitor.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
              <Search className="h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
          </button>

        </div>

        {/* Right: Unit, Street, Authorized by, Vehicle, Timestamp */}
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">

          {/* Unit */}
          {visitor.unit && (
            <div
              className={cn(
                "flex items-center gap-1.5 min-w-0",
                visitor.unit_id && onUnitClick ? "cursor-pointer group" : ""
              )}
              onClick={visitor.unit_id && onUnitClick ? (e) => { e.stopPropagation(); onUnitClick(visitor.unit_id!, visitor.unit); } : undefined}
              title={
                visitDestinationsTitle(visitor.destinations) ||
                (visitor.unit_id && onUnitClick ? `Ver unidade ${visitor.unit}` : undefined)
              }
            >
              <Home className={cn(
                "h-3.5 w-3.5 flex-shrink-0 transition-colors",
                visitor.unit_id && onUnitClick
                  ? lightTheme ? "text-blue-500 group-hover:text-blue-700" : "text-blue-500 group-hover:text-blue-300"
                  : lightTheme ? "text-gray-400" : "text-gray-500"
              )} />
              <span className={cn(
                "text-xs font-semibold truncate transition-colors",
                visitor.unit_id && onUnitClick
                  ? lightTheme ? "text-blue-600 group-hover:text-blue-800" : "text-blue-400 group-hover:text-blue-200"
                  : lightTheme ? "text-gray-700" : "text-gray-300"
              )}>
                {/* A visit to several unidades/departamentos names the first and counts the
                    rest — "101" alone is a lie when the person is also going to 202 and 303,
                    and the full list does not fit a card sized for one. */}
                {visitDestinationsLabel(visitor.destinations) || visitor.unit}
              </span>
            </div>
          )}

          {/* Street */}
          {visitor.street_name && (
            <div className="flex items-center gap-1.5 min-w-0">
              <MapPin className={cn("h-3.5 w-3.5 flex-shrink-0", lightTheme ? "text-gray-400" : "text-gray-500")} />
              <span className={cn("text-xs truncate", lightTheme ? "text-gray-500" : "text-gray-400")}>
                {visitor.street_name}
              </span>
            </div>
          )}

          {/* Authorized by */}
          {visitor.authorized_by_name && (
            <div
              className={cn(
                "flex items-center gap-1.5 min-w-0",
                visitor.responsible_person_id && onPersonClick ? "cursor-pointer group/auth" : ""
              )}
              onClick={visitor.responsible_person_id && onPersonClick ? (e) => { e.stopPropagation(); onPersonClick(visitor.responsible_person_id!); } : undefined}
              title={visitor.responsible_person_id && onPersonClick ? `Ver perfil de ${visitor.authorized_by_name}` : undefined}
            >
              <User className={cn(
                "h-3.5 w-3.5 flex-shrink-0 transition-colors",
                visitor.responsible_person_id && onPersonClick
                  ? lightTheme ? "text-gray-500 group-hover/auth:text-gray-700" : "text-gray-500 group-hover/auth:text-gray-300"
                  : lightTheme ? "text-gray-400" : "text-gray-500"
              )} />
              <span className={cn(
                "text-xs truncate transition-colors",
                visitor.responsible_person_id && onPersonClick
                  ? lightTheme ? "text-gray-600 group-hover/auth:text-gray-800" : "text-gray-400 group-hover/auth:text-gray-200"
                  : lightTheme ? "text-gray-500" : "text-gray-400"
              )}>
                {visitor.authorized_by_name}
              </span>
            </div>
          )}

          {/* Validity — set at creation time in the Novo Visitante modal; previously only
              visible by opening the person detail modal.
              Hidden once the clearance has ENDED: a revoked prestador was showing "Revogado" and
              "Válido até 18/11/2026" side by side, which reads as still being let in. Prefers the
              server's verdict and falls back to the card status against an older backend. */}
          {visitor.valid_until && !clearanceEnded && (
            <div className="flex items-center gap-1.5 min-w-0">
              <Calendar className={cn("h-3.5 w-3.5 flex-shrink-0", lightTheme ? "text-gray-400" : "text-gray-500")} />
              <span className={cn("text-xs truncate", lightTheme ? "text-gray-500" : "text-gray-400")}>
                Válido até {formatValidUntilDate(visitor.valid_until)}
              </span>
            </div>
          )}

          {/* Vehicle plate — always rendered so all cards in a row have equal height.
              Hidden (but space reserved) when no plate, so buttons naturally align. */}
          <div
            className="flex items-center gap-1.5"
            style={{ visibility: (visitor.vehicle && visitor.vehicle !== '—') ? 'visible' : 'hidden' }}
          >
            <Car className={cn("h-3.5 w-3.5 flex-shrink-0", lightTheme ? "text-gray-400" : "text-gray-500")} />
            <LicensePlate plate={visitor.vehicle && visitor.vehicle !== '—' ? visitor.vehicle : 'XXX-0000'} size="sm" />
          </div>
        </div>
      </div>

      {/* Action buttons */}
      {(visitor.status === 'inside' || visitor.status === 'awaiting' || visitor.status === 'pending_approval') && (onManualCheckIn || onManualCheckOut || onRevokeAccess || onApproveAccess) && visitor.personId && visitor.roleId && (
        <div className="flex gap-1.5">
          {visitor.status === 'pending_approval' && onApproveAccess && (
            <button
              onClick={(e) => { e.stopPropagation(); onApproveAccess(visitor); }}
              tabIndex={-1}
              className={cn("flex-1 px-2 py-1.5 rounded text-xs font-bold transition-colors border",
                lightTheme ? "bg-orange-100 hover:bg-orange-200 text-orange-700 border-orange-300" : "bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 border-orange-500/30")}
              title="Aprovar manualmente em nome do responsável"
            >
              Aprovar
            </button>
          )}
          {visitor.status === 'awaiting' && onManualCheckIn && (
            <button
              onClick={(e) => { e.stopPropagation(); onManualCheckIn(visitor); }}
              tabIndex={-1}
              className={cn("flex-1 px-2 py-1.5 rounded text-xs font-bold transition-colors border",
                lightTheme ? "bg-green-100 hover:bg-green-200 text-green-700 border-green-300" : "bg-green-500/20 hover:bg-green-500/30 text-green-400 border-green-500/30")}
              title="Entrada manual"
            >
              Entrada
            </button>
          )}
          {visitor.status === 'inside' && onManualCheckOut && (
            <button
              onClick={(e) => { e.stopPropagation(); onManualCheckOut(visitor); }}
              tabIndex={-1}
              className={cn("flex-1 px-2 py-1.5 rounded text-xs font-bold transition-colors border",
                lightTheme ? "bg-blue-100 hover:bg-blue-200 text-blue-700 border-blue-300" : "bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border-blue-500/30")}
              title="Registrar saída"
            >
              Saída
            </button>
          )}
          {onRevokeAccess && (
            <button
              onClick={(e) => { e.stopPropagation(); onRevokeAccess(visitor); }}
              tabIndex={-1}
              className={cn("p-1.5 rounded transition-colors border",
                lightTheme ? "bg-red-100 hover:bg-red-200 text-red-700 border-red-300" : "bg-red-500/20 hover:bg-red-500/30 text-red-400 border-red-500/30")}
              title="Revogar acesso"
            >
              <Ban className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {/* Bottom: Entry/exit times + status badge — single row */}
      <div className={cn("flex items-center gap-1.5 pt-1.5 border-t overflow-hidden", lightTheme ? "border-gray-300" : "border-gray-800/50")}>
        {visitor.entryTime && visitor.entryTime !== '—' && (
          <div className={cn("inline-flex items-center gap-1 px-2 py-1 rounded flex-shrink-0", lightTheme ? "bg-green-50 border border-green-200" : "bg-green-500/10")}>
            <span className={cn("text-xs font-bold", lightTheme ? "text-green-700" : "text-green-400")}>E:</span>
            <span className={cn("text-xs font-semibold", lightTheme ? "text-gray-800" : "text-gray-300")}>{visitor.entryTime}</span>
          </div>
        )}
        {visitor.exitTime && (
          <div className={cn("inline-flex items-center gap-1 px-2 py-1 rounded flex-shrink-0", lightTheme ? "bg-red-50 border border-red-200" : "bg-red-500/10")}>
            <span className={cn("text-xs font-bold", lightTheme ? "text-red-700" : "text-red-400")}>S:</span>
            <span className={cn("text-xs font-semibold", lightTheme ? "text-gray-800" : "text-gray-300")}>{visitor.exitTime}</span>
          </div>
        )}
        <span className={cn(
          "inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full flex-shrink-0 ml-auto",
          visitor.status === 'inside'
            ? lightTheme ? "bg-green-100 text-green-700" : "bg-green-500/20 text-green-400"
            : visitor.status === 'awaiting'
            ? lightTheme ? "bg-yellow-100 text-yellow-700" : "bg-yellow-500/20 text-yellow-400"
            : visitor.status === 'pending_approval'
            ? lightTheme ? "bg-orange-100 text-orange-700" : "bg-orange-500/20 text-orange-400"
            : visitor.status === 'revoked'
            ? lightTheme ? "bg-red-100 text-red-700" : "bg-red-500/20 text-red-400"
            : lightTheme ? "bg-gray-100 text-gray-600" : "bg-gray-500/20 text-gray-400"
        )}>
          {visitor.status === 'inside' ? (
            <>Dentro <LiveElapsed startTime={visitor.first_entry_at || visitor.last_entry_at} /></>
          ) : visitor.status === 'awaiting' && visitor.periodAccess ? (
            <PeriodAccessUntil until={visitor.valid_until || visitor.access_expires_at} />
          ) : visitor.status === 'awaiting' ? (
            <>Aguardando <LiveRemaining expiresAt={visitor.entry_window_expires_at} /></>
          ) : visitor.status === 'pending_approval' ? 'Aguardando aprovação'
            : visitor.status === 'revoked' ? 'Revogado' : 'Saiu'}
        </span>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: only re-render if visitor data or theme actually changed
  return (
    prevProps.visitor.id === nextProps.visitor.id &&
    prevProps.visitor.status === nextProps.visitor.status &&
    prevProps.visitor.name === nextProps.visitor.name &&
    prevProps.visitor.photo === nextProps.visitor.photo &&
    prevProps.visitor.entryTime === nextProps.visitor.entryTime &&
    prevProps.lightTheme === nextProps.lightTheme &&
    prevProps.exitByTime === nextProps.exitByTime
  );
});

VisitorCard.displayName = 'VisitorCard';

// ── WsDot — hover dropdown showing WebSocket stats ───────────────────────────

type WsConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

function formatWsRelative(date: Date): string {
  const secs = Math.floor((Date.now() - date.getTime()) / 1000);
  if (secs < 5) return 'agora';
  if (secs < 60) return `há ${secs}s`;
  return `há ${Math.floor(secs / 60)}min`;
}

function formatWsUptime(since: Date): string {
  const secs = Math.floor((Date.now() - since.getTime()) / 1000);
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

const WsDot: React.FC<{
  status: WsConnectionStatus;
  getWsStats?: () => import('../../../services/visitorWebSocketClient').WsStats | null;
}> = ({ status, getWsStats }) => {
  const [hovered, setHovered] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [stats, setStats] = useState<import('../../../services/visitorWebSocketClient').WsStats | null>(null);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!hovered || !getWsStats) return;
    setStats(getWsStats());
    const id = setInterval(() => setStats(getWsStats()), 500);
    return () => clearInterval(id);
  }, [hovered, getWsStats]);

  const handleMouseEnter = () => {
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, left: r.left });
    }
    setHovered(true);
  };

  const dotColor =
    status === 'connected' ? 'bg-green-500' :
    status === 'connecting' ? 'bg-yellow-400 animate-pulse' :
    status === 'reconnecting' ? 'bg-orange-400 animate-pulse' :
    'bg-gray-500';

  const statusLabel =
    status === 'connected' ? 'Conectado' :
    status === 'connecting' ? 'Conectando...' :
    status === 'reconnecting' ? 'Reconectando...' :
    'Desconectado';

  const labelColor =
    status === 'connected' ? 'text-green-400' :
    status === 'connecting' ? 'text-yellow-400' :
    status === 'reconnecting' ? 'text-orange-400' :
    'text-red-400';

  return (
    <span
      ref={ref}
      className="relative inline-flex items-center cursor-default"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setHovered(false)}
    >
      <span className={cn('inline-block w-2 h-2 rounded-full flex-shrink-0', dotColor)} />

      {hovered && pos && (
        <div
          className="fixed z-[9999] w-64 bg-gray-950 border border-gray-800 rounded-lg shadow-2xl overflow-hidden"
          style={{ top: pos.top, left: pos.left }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {/* Header */}
          <div className="px-3 py-2 bg-black border-b border-gray-800 flex items-center gap-2 whitespace-nowrap">
            <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', dotColor)} />
            <span className={cn('text-xs font-semibold', labelColor)}>Tempo Real</span>
            <span className={cn('text-[10px] ml-auto pl-2', labelColor)}>{statusLabel}</span>
          </div>

          <div className="px-3 py-2 space-y-1.5">
            {stats ? (
              <>
                {stats.connectedAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-gray-400">Uptime:</span>
                    <span className="text-[11px] text-gray-200 font-medium">{formatWsUptime(stats.connectedAt)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-gray-400">Msgs recebidas:</span>
                  <span className="text-[11px] font-semibold text-green-400">{stats.messagesReceived}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-gray-400">Msgs enviadas:</span>
                  <span className="text-[11px] font-semibold text-blue-400">{stats.messagesSent}</span>
                </div>
                {stats.lastReceivedAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-gray-400">Última recebida:</span>
                    <span className="text-[11px] text-gray-300">{formatWsRelative(stats.lastReceivedAt)}</span>
                  </div>
                )}
                {stats.lastSentAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-gray-400">Última enviada:</span>
                    <span className="text-[11px] text-gray-300">{formatWsRelative(stats.lastSentAt)}</span>
                  </div>
                )}
                {stats.reconnectCount > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-gray-400">Reconexões:</span>
                    <span className="text-[11px] text-orange-400 font-semibold">{stats.reconnectCount}</span>
                  </div>
                )}
              </>
            ) : (
              <p className="text-[11px] text-gray-500">Sem dados</p>
            )}
          </div>
        </div>
      )}
    </span>
  );
};

// Vehicle photo zone with hover zoom + lightbox
const VehiclePhotoZone: React.FC<{ photoUrl?: string; plate: string; lightTheme?: boolean }> = ({ photoUrl, plate, lightTheme }) => {
  const [imgError, setImgError] = React.useState(false);
  const [photoOpen, setPhotoOpen] = React.useState(false);
  React.useEffect(() => { setImgError(false); }, [photoUrl]);

  const absUrl = photoUrl && !imgError ? makeAbsolutePhotoUrl(photoUrl) : null;

  return (
    <>
      <div
        className={cn(
          "group w-full overflow-hidden flex-shrink-0 flex items-center justify-center relative",
          VEHICLE_PHOTO_ASPECT_CLASS,
          absUrl
            ? (lightTheme ? "bg-gray-100 cursor-zoom-in" : "bg-gray-900/80 cursor-zoom-in")
            : (lightTheme ? "bg-gradient-to-br from-gray-50 to-gray-100" : "bg-gradient-to-br from-gray-900 to-gray-950")
        )}
        onClick={e => { if (absUrl) { e.stopPropagation(); setPhotoOpen(true); } }}
      >
        {absUrl ? (
          <>
            {/* Whole photo over a blurred fill of itself — see components/vehicles/VehiclePhoto.
                Cropping cut the roof and the plate off; plain contain left portrait photos
                stranded between slabs of grey. */}
            <VehiclePhoto src={absUrl} alt={plate} onError={() => setImgError(true)} />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" />
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 w-full px-3">
            <LicensePlate plate={plate} size="fluid" className="drop-shadow-md" />
            <span className={cn(
              "flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium tracking-wide",
              lightTheme ? "bg-gray-200 text-gray-400" : "bg-gray-800 text-gray-500"
            )}>
              <Camera className="h-2.5 w-2.5" />
              sem foto
            </span>
          </div>
        )}
      </div>

      {photoOpen && absUrl && (
        <div
          className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPhotoOpen(false)}
        >
          <button className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={() => setPhotoOpen(false)}>
            <X className="h-7 w-7" />
          </button>
          <img
            src={absUrl}
            alt={plate}
            className="max-w-full max-h-[90vh] rounded-xl shadow-2xl object-contain"
            onClick={e => e.stopPropagation()}
          />
          <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white font-bold tracking-widest text-lg">
            {plate}
          </p>
        </div>
      )}
    </>
  );
};

// ── Device detail panel: info + actions, shown at the top of the device page ──────
type DeviceActionKind = 'trigger' | 'test' | 'reboot';

// Shorter than DeviceActionMenu's 5s reboot hold — opening a door is far less disruptive
// than rebooting the device, so a quick 2s hold is enough to avoid accidental taps.
const HOLD_MS = 2000;

const DeviceDetailPanel: React.FC<{
  device: Device;
  onDeviceAction?: (deviceId: string, action: string) => void | Promise<void>;
  confirm: (options: { title?: string; message: string; confirmLabel?: string; cancelLabel?: string; variant?: 'danger' | 'warning' | 'default' }) => Promise<boolean>;
}> = ({ device, onDeviceAction, confirm }) => {
  const [loadingAction, setLoadingAction] = useState<DeviceActionKind | null>(null);
  const [holdProgress, setHoldProgress] = useState(0);
  const holdIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const holdStartRef = useRef<number>(0);

  const runAction = async (action: DeviceActionKind) => {
    if (!onDeviceAction || loadingAction) return;
    if (action === 'reboot') {
      const ok = await confirm({
        title: 'Reiniciar dispositivo',
        message: `Tem certeza que deseja reiniciar "${device.name}"? O dispositivo ficará indisponível por alguns instantes.`,
        confirmLabel: 'Reiniciar',
        variant: 'warning',
      });
      if (!ok) return;
    }
    setLoadingAction(action);
    try {
      await onDeviceAction(String(device.id), action);
    } finally {
      setLoadingAction(null);
    }
  };

  // "Abrir" requires a hold-to-confirm press, like a physical botoeira release button —
  // prevents accidentally opening the door with a stray click.
  const startTriggerHold = (e: React.MouseEvent | React.TouchEvent) => {
    if (loadingAction) return;
    e.preventDefault();
    holdStartRef.current = Date.now();
    holdIntervalRef.current = setInterval(() => {
      const p = Math.min(((Date.now() - holdStartRef.current) / HOLD_MS) * 100, 100);
      setHoldProgress(p);
      if (p >= 100) {
        clearInterval(holdIntervalRef.current!);
        holdIntervalRef.current = null;
        setHoldProgress(0);
        runAction('trigger');
      }
    }, 50);
  };

  const cancelTriggerHold = () => {
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }
    setHoldProgress(0);
  };

  const infoItems: { icon: React.ElementType; label: string; value?: string }[] = [
    { icon: Cpu, label: 'Modelo', value: [device.manufacturer, device.model].filter(Boolean).join(' · ') || undefined },
    { icon: Shield, label: 'Nº de Série', value: device.serial_number },
    { icon: Info, label: 'Firmware', value: device.firmware_version },
    { icon: Monitor, label: 'MAC', value: device.mac_address },
    {
      icon: Clock,
      label: 'Última comunicação',
      value: device.last_heartbeat ? new Date(device.last_heartbeat).toLocaleString('pt-BR') : undefined,
    },
  ].filter(item => !!item.value);

  return (
    <div className="mb-3 flex-shrink-0 rounded-lg border border-gray-800 bg-gray-950 p-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {/* Info grid */}
        {infoItems.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 flex-1 min-w-0">
            {infoItems.map(({ icon: ItemIcon, label, value }) => (
              <div key={label} className="flex items-center gap-1.5 min-w-0">
                <ItemIcon className="h-3 w-3 text-gray-500 flex-shrink-0" />
                <span className="text-[11px] text-gray-500 flex-shrink-0">{label}:</span>
                <span className="text-[11px] text-gray-300 truncate" title={value}>{value}</span>
              </div>
            ))}
          </div>
        ) : (
          <span className="text-xs text-gray-500">Sem informações adicionais do dispositivo</span>
        )}

        {/* Actions */}
        {onDeviceAction && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onMouseDown={startTriggerHold}
              onMouseUp={cancelTriggerHold}
              onMouseLeave={cancelTriggerHold}
              onTouchStart={startTriggerHold}
              onTouchEnd={cancelTriggerHold}
              disabled={loadingAction !== null}
              className="relative inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden select-none"
              title="Segure 2s para abrir"
            >
              {/* Hold progress fill */}
              <div
                className="absolute inset-y-0 left-0 bg-blue-400/30 transition-none pointer-events-none"
                style={{ width: `${holdProgress}%` }}
              />
              <span className="relative z-10 inline-flex items-center gap-1.5">
                {loadingAction === 'trigger' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <DoorOpen className="h-3.5 w-3.5" />}
                {holdProgress > 0 ? `Segure... ${Math.round(holdProgress / 10) * 10}%` : 'Abrir (segure 2s)'}
              </span>
            </button>
            <button
              onClick={() => runAction('test')}
              disabled={loadingAction !== null}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-gray-800 text-gray-300 border border-gray-700 hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Testar conexão"
            >
              {loadingAction === 'test' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wifi className="h-3.5 w-3.5" />}
              Testar
            </button>
            <button
              onClick={() => runAction('reboot')}
              disabled={loadingAction !== null}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Reiniciar dispositivo"
            >
              {loadingAction === 'reboot' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Reiniciar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const MainContentAreaComponent: React.FC<MainContentAreaProps> = ({
  currentView,
  selectedCadastro,
  selectedCondo,
  activeVisitors,
  allVisitors,  // ALL visitors (for counts)
  selectedDeviceHistory,
  isLoadingVisitors = false,
  customerId,
  customerType = 'condominio',
  mobileAppEnabled,
  cadastroRefreshTrigger,
  lastRefreshTime,
  pagination,
  onPageChange,
  onNextPage,
  onPrevPage,
  onPersonClick,
  onDeviceClick,
  onLocationClick,
  onRevokeAccess,
  onManualCheckIn,
  onManualCheckOut,
  onApproveAccess,
  onBackFromDeviceHistory,
  onDeviceAction,
  onResetCadastro,
  onRefreshVisitors,
  onRegisterResident,
  onRegisterEmployee,
  visitorWsStatus,
  getWsStats,
  onStatusFilterChange,
  onSearchModeChange,
  condoType,
  petsEnabled = false,
  canApproveSelfRegistrations = true,
  lightTheme = false,
}) => {
  const confirm = useConfirm();
  const forceUpper = useForceUppercase();
  const [filterTerm, setFilterTerm] = useState('');
  const debouncedFilterTerm = useDebounce(filterTerm, 300);
  const [cadastroData, setCadastroData] = useState<any[]>([]);
  const [isLoadingCadastro, setIsLoadingCadastro] = useState(false);
  const [cadastroSearchTerm, setCadastroSearchTerm] = useState('');

  // Moradores-specific filters
  const [moradorRelationType, setMoradorRelationType] = useState('');
  const [moradorBlockId, setMoradorBlockId] = useState('');
  const [moradorUnitId, setMoradorUnitId] = useState('');
  const [moradorPendentes, setMoradorPendentes] = useState(false);
  const [moradorBlocks, setMoradorBlocks] = useState<{ id: string; name: string }[]>([]);
  const [moradorUnits, setMoradorUnits] = useState<{ id: string; number: string }[]>([]);

  // Funcionarios-specific filters
  const [funcionarioDeptId, setFuncionarioDeptId] = useState('');
  const [funcionarioPendentes, setFuncionarioPendentes] = useState(false);
  const [funcionarioDepts, setFuncionarioDepts] = useState<{ id: string; name: string }[]>([]);

  const [showNewPrestadorModal, setShowNewPrestadorModal] = useState(false);

  // Prestador mandatory exit time ("Saída Obrigatória") — used to highlight
  // overdue prestador cards in "Visitantes Ativos" (still 'inside' past this daily HH:MM)
  const [spExitByTime, setSpExitByTime] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!customerId) { setSpExitByTime(undefined); return; }
    condoApi.getServiceProviderConfig(customerId)
      .then(r => setSpExitByTime(r.data?.sp_default_exit_by || undefined))
      .catch(() => setSpExitByTime(undefined));
  }, [customerId]);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [showVehicleDetailModal, setShowVehicleDetailModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [vehicleRefreshTrigger, setVehicleRefreshTrigger] = useState(0);
  const [vehicleOwnerFilter, setVehicleOwnerFilter] = useState('');
  const [vehicleRfidFilter, setVehicleRfidFilter] = useState<'' | 'with' | 'without'>('');
  const [showPetModal, setShowPetModal] = useState(false);
  const [editingPet, setEditingPet] = useState<Pet | null>(null);
  const [viewingPet, setViewingPet] = useState<Pet | null>(null);
  const [petRefreshTrigger, setPetRefreshTrigger] = useState(0);
  const [showTagModal, setShowTagModal] = useState(false);
  const [editingTag, setEditingTag] = useState<PersonTag | null>(null);
  const [tagRefreshTrigger, setTagRefreshTrigger] = useState(0);
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [blockRefreshTrigger, setBlockRefreshTrigger] = useState(0);

  // Cadastros → Unidades can be browsed by quadra/lote or, where the condo registers street
  // addresses, by rua/número. Same units either way — only the path through them differs.
  const [unidadesViewMode, setUnidadesViewMode] = useState<'block' | 'street'>('block');
  const [unidadesStreet, setUnidadesStreet] = useState<{ id: string; name: string } | null>(null);
  const [unidadesFlatUnits, setUnidadesFlatUnits] = useState<FlatUnitRow[]>([]);
  const [syncingVehicleId, setSyncingVehicleId] = useState<string | null>(null);
  const [revokingVehicleId, setRevokingVehicleId] = useState<string | null>(null);
  const [syncingTagId, setSyncingTagId] = useState<string | null>(null);
  const [syncingAllVehiclesRfid, setSyncingAllVehiclesRfid] = useState(false);
  const { setData: setSyncProgressData, pauseRef: syncPauseRef, stopRef: syncStopRef } = useSyncProgress();

  // Unit name → unit_id lookup for clickable unit navigation
  // Initialise from cache so remounts (e.g. after location-detail navigation) are instant
  const [unitLookup, setUnitLookup] = useState<Map<string, string>>(
    () => (customerId && _unitLookupCache.get(customerId)) || new Map()
  );
  useEffect(() => {
    if (!customerId) return;
    // Serve from cache immediately so unit is clickable right away on remount
    if (_unitLookupCache.has(customerId)) {
      setUnitLookup(_unitLookupCache.get(customerId)!);
      return;
    }
    fetchWithAuth(`/api/v1/customers/${customerId}/units`)
      .then(r => r.ok ? r.json() : [])
      .then((arr: any[]) => {
        const map = new Map<string, string>();
        arr.forEach((u: any) => {
          if (!u.id) return;
          map.set(u.number, u.id);
          // Unit numbers can be "Q4LT2 - Rua Foo, 123" but metadata stores just "Q4LT2"
          // Also index by the prefix before " - "
          if (u.number.includes(' - ')) {
            const prefix = u.number.split(' - ')[0].trim();
            if (prefix && !map.has(prefix)) map.set(prefix, u.id);
          }
          if (u.block?.name) {
            map.set(`${u.number} - ${u.block.name}`, u.id);
          }
        });
        _unitLookupCache.set(customerId, map);
        setUnitLookup(map);
      })
      .catch(() => {});
  }, [customerId]);

  // Auto check-in awaiting visitors whose entry window has expired
  const autoCheckedInRef = React.useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!customerId) return;
    const run = async () => {
      const expired = activeVisitors.filter(
        (v) =>
          v.status === 'awaiting' &&
          v.entry_window_expires_at &&
          new Date(v.entry_window_expires_at).getTime() <= Date.now() &&
          v.personId &&
          v.roleId &&
          !autoCheckedInRef.current.has(v.roleId)
      );
      for (const v of expired) {
        autoCheckedInRef.current.add(v.roleId!);
        try {
          await condoApi.manualCheckIn(customerId, v.personId!, v.roleId!);
          onRefreshVisitors?.();
        } catch {
          autoCheckedInRef.current.delete(v.roleId!);
        }
      }
    };
    run();
    const id = setInterval(run, 30_000);
    return () => clearInterval(id);
  }, [customerId, activeVisitors, onRefreshVisitors]);

  // Visitor status filter with localStorage persistence
  const [statusFilter, setStatusFilter] = useState<VisitorStatusFilter>(() => {
    try {
      const saved = localStorage.getItem('panther_visitor_status_filter');
      if (saved && ['all', 'inside', 'awaiting', 'exited', 'expired'].includes(saved)) {
        return saved as VisitorStatusFilter;
      }
    } catch (e) {
      // localStorage not available
    }
    return 'all';
  });

  // Role type filter: 'all' | 'visitor' | 'service_provider'
  const [roleTypeFilter, setRoleTypeFilter] = useState<'all' | 'visitor' | 'service_provider'>('all');


  // Persist filter to localStorage and notify WS hook so server filters+paginates
  useEffect(() => {
    try {
      localStorage.setItem('panther_visitor_status_filter', statusFilter);
    } catch (e) {
      // localStorage not available
    }
    // 'all' → '' (server interprets empty string as no filter)
    onStatusFilterChange?.(statusFilter === 'all' ? '' : statusFilter);
  }, [statusFilter, onStatusFilterChange]);

  // Smart refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [dataChanged, setDataChanged] = useState(false);
  const previousVisitorsRef = useRef<Visitor[]>([]);

  // Detect data changes - only show visual feedback if data actually changed
  useEffect(() => {
    if (!isLoadingVisitors && activeVisitors.length > 0) {
      const prev = previousVisitorsRef.current;
      const current = activeVisitors;

      // Compare data: different length or different IDs/statuses
      const hasChanged = prev.length !== current.length ||
        !current.every((v, i) =>
          prev[i] && prev[i].id === v.id && prev[i].status === v.status
        );

      if (hasChanged) {
        setDataChanged(true);
        setTimeout(() => setDataChanged(false), 1000); // Flash for 1 second
      }

      previousVisitorsRef.current = current;
    }
  }, [activeVisitors, isLoadingVisitors]);

  // Handle manual refresh
  const handleRefresh = useCallback(async () => {
    if (onRefreshVisitors && !isRefreshing) {
      setIsRefreshing(true);
      try {
        await onRefreshVisitors();
      } finally {
        setTimeout(() => setIsRefreshing(false), 500);
      }
    }
  }, [onRefreshVisitors, isRefreshing]);

  // F5 key binding to refresh visitors when in visitors view
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F5' && currentView === 'visitors') {
        e.preventDefault(); // Prevent browser refresh
        handleRefresh();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentView, handleRefresh]);

  // Fetch today's visitor count from server (accurate, independent of infinite scroll state)
  useEffect(() => {
    if (!customerId || currentView !== 'visitors') return;
    let cancelled = false;
    fetchWithAuth(
      `/api/v1/customers/${customerId}/people?role_type=visitor,service_provider&include_inactive_roles=true&created_today=true&page=1&page_size=1`
    ).then(res => res.ok ? res.json() : null)
     .then(data => {
       if (!cancelled && data) {
         setTodayVisitorServerCount(data.pagination?.total ?? data.total ?? null);
       }
     })
     .catch(() => {});
    return () => { cancelled = true; };
  }, [customerId, currentView]);

  // History collapsible state - collapsed by default on small screens
  const [hasEnoughHeight, setHasEnoughHeight] = useState(window.innerHeight >= 900);
  const [historyCollapsed, setHistoryCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('panther_history_collapsed');
      if (saved !== null) {
        return JSON.parse(saved);
      }
    } catch (e) {
      // Fallback to default
    }
    return window.innerHeight < 900;
  });
  const [visitDateFilter, setVisitDateFilter] = useState<'all' | 'today' | 'yesterday' | '7d' | '30d' | 'custom'>('all');
  const [visitCustomStart, setVisitCustomStart] = useState('');
  const [visitCustomEnd, setVisitCustomEnd] = useState('');
  const [visitRoleFilter, setVisitRoleFilter] = useState<'all' | 'visitor' | 'service_provider'>('all');
  // "Quem está liberado?" — people a resident cleared in advance whose clearance is still live.
  // Server-side (?liberado=true): the list is paginated, so filtering here would hide matches on
  // other pages and show a count that isn't the real one.
  const [visitLiberadoFilter, setVisitLiberadoFilter] = useState<'all' | 'liberado'>('all');
  // Unidade filter for Liberados. Server-side too (ListPeople reads role_metadata.unit_id), for the
  // same reason: "quem está liberado para a QALT01" has to be answered over the whole list, not the
  // page in front of the operator.
  const [visitUnitId, setVisitUnitId] = useState('');
  const [visitUnits, setVisitUnits] = useState<{ id: string; label: string }[]>([]);
  const [prestadorStatusFilter, setPrestadorStatusFilter] = useState<'all' | 'inside' | 'awaiting_entry' | 'expired' | 'exited'>('all');
  const [prestadorDataVersion, setPrestadorDataVersion] = useState(0);
  const [prestadorView, setPrestadorView] = useState<'list' | 'schedule' | 'contractors'>('list');
  const [prestadorEditState, setPrestadorEditState] = useState<WindowEditState | null>(null);

  const handlePrestadorWindowSave = useCallback(async (
    personId: string,
    windowStart: string,
    windowEnd: string,
    allowedDays: number[],
    contractorId: string | null,
    contractorName: string | null,
  ) => {
    if (!customerId) return;
    await condoApi.updateServiceProviderWindow(customerId, personId, {
      entry_window_start: windowStart,
      entry_window_end: windowEnd,
      allowed_days: JSON.stringify(allowedDays),
      contractor_id: contractorId,
      contractor_name: contractorName,
    });
    setPrestadorEditState(null);
    setPrestadorDataVersion(v => v + 1); // refresh list
  }, [customerId]);

  // Photo zoom modal state
  const [zoomedPhoto, setZoomedPhoto] = useState<{ url: string; name: string; personId: string } | null>(null);
  const [showPhotoActionModal, setShowPhotoActionModal] = useState(false);
  const [showPhotoUploadModal, setShowPhotoUploadModal] = useState(false);
  const [showPhotoWebcamModal, setShowPhotoWebcamModal] = useState(false);
  const [showPhotoIPCameraModal, setShowPhotoIPCameraModal] = useState(false);
  // Last-write-wins by request order for the Cadastros grid.
  const cadastroSequence = useRef(createRequestSequence()).current;

  // Close photo zoom with ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && zoomedPhoto) {
        setZoomedPhoto(null);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [zoomedPhoto]);

  // Pagination state for cadastro view (server-side)
  const [cadastroPage, setCadastroPage] = useState(1);
  const [cadastroTotalItems, setCadastroTotalItems] = useState(0);
  const [cadastroTotalPages, setCadastroTotalPages] = useState(0);
  const [cadastroItemsPerPage, setCadastroItemsPerPage] = useState(50); // Server-side pagination: 50 items per page
  // Vehicle infinite scroll state
  const [vehicleHasMore, setVehicleHasMore] = useState(false);
  const [vehicleIsLoadingMore, setVehicleIsLoadingMore] = useState(false);
  const vehicleIsLoadingMoreRef = useRef(false);
  const visitorGridRef = useRef<HTMLDivElement>(null);
  const cadastroGridRef = useRef<HTMLDivElement>(null);

  // Server-side count of visitors registered today (accurate, independent of loaded pages)
  const [todayVisitorServerCount, setTodayVisitorServerCount] = useState<number | null>(null);

  // Infinite scroll: accumulated visitors across WS pages
  const [allLoadedVisitors, setAllLoadedVisitors] = useState<Visitor[]>([]);
  const loadedMaxPageRef = useRef(0);

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isLoadingMoreRef = useRef(false);
  const paginationRef = useRef(pagination);
  const onNextPageRef = useRef(onNextPage);

  // Visitor history state
  const [visitorHistory, setVisitorHistory] = useState<HistoricalVisitor[]>([]);
  const [isLoadingVisitorHistory, setIsLoadingVisitorHistory] = useState(false);
  const [isInitialHistoryLoad, setIsInitialHistoryLoad] = useState(true);
  const [historySearchTerm, setHistorySearchTerm] = useState(() => {
    try {
      return localStorage.getItem('panther_history_search') || '';
    } catch (e) {
      return '';
    }
  });
  const [historyPage, setHistoryPage] = useState(1);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [isLoadingMoreHistory, setIsLoadingMoreHistory] = useState(false);
  const historyIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const historyScrollRef = useRef<HTMLDivElement>(null);

  // Use real device history hook with customer context
  const deviceId = selectedDeviceHistory?.id ? String(selectedDeviceHistory.id) : null;
  const { history: deviceHistory, isLoading: isLoadingDeviceHistory } = useDeviceHistory(deviceId, customerId || null);

  const fetchPersonRfidDeviceIds = async (personId: string): Promise<string[]> => {
    if (!customerId) return [];
    return (await fetchPersonVehicleDeviceIds(customerId, personId)).rfidIds;
  };

  const handleSyncVehicle = async (vehicle: Vehicle) => {
    if (!customerId) return;
    setSyncingVehicleId(vehicle.id);
    try {
      await syncVehicleRfid({ customerId, vehicle, isAllowed: true });
    } finally {
      setSyncingVehicleId(null);
    }
  };

  const handleRevokeVehicle = async (vehicle: Vehicle) => {
    if (!customerId) return;
    setRevokingVehicleId(vehicle.id);
    try {
      await syncVehicleRfid({ customerId, vehicle, isAllowed: false });
    } finally {
      setRevokingVehicleId(null);
    }
  };

  // Sync all vehicles with RFID tags to their authorized RFID readers
  const handleSyncAllVehiclesRfid = async () => {
    if (!customerId) return;
    setSyncingAllVehiclesRfid(true);
    const toastId = toast.loading('Buscando leitores RFID...');

    // Query RFID devices directly — the device holds its own authorized-vehicle list.
    // No need to enumerate vehicles and derive devices from zone lookups.
    let allRfidDevices: any[] = [];
    let hasLprCameras = false;
    try {
      const res = await api.get(`/iot-devices/customer/${customerId}`);
      const devices: any[] = res.data?.devices || res.data?.data || [];
      allRfidDevices = devices.filter((d: any) =>
        d.device_type === 'rfid_reader' || d.device_type === 'controlid_rfid'
      );
      // LPR cameras hold their own plate allow-list and are keyed by plate rather than tag, so
      // they are not RFID readers and were being skipped by this whole action.
      hasLprCameras = devices.some((d: any) => d.device_type === 'hikvision_lpr');
    } catch {
      toast.error('Erro ao buscar leitores RFID', { id: toastId });
      setSyncingAllVehiclesRfid(false);
      return;
    }

    // Plate sync for every LPR camera in the customer. Runs before the RFID paths because
    // those can return early, and a site whose gates are plate-only has no RFID reader at all.
    const runLprPlateSync = async () => {
      if (!hasLprCameras) return;
      const lprTitle = 'Sincronização de Placas (LPR)';
      const lprBuf: SyncProgressItem[] = [];
      setSyncProgressData({ zoneName: lprTitle, total: 0, done: 0, ok: 0, fail: 0, recentItems: [], isComplete: false });

      const result = await syncAllVehiclePlatesToLpr(customerId, {
        shouldStop: () => syncStopRef.current,
        onProgress: (p) => {
          lprBuf.push({ label: p.label, owner: 'LPR', status: p.status });
          if (lprBuf.length > 50) lprBuf.shift();
          setSyncProgressData({
            zoneName: lprTitle,
            total: p.total, done: p.done, ok: p.ok, fail: p.fail,
            recentItems: [...lprBuf],
            isComplete: false,
          });
        },
      });

      if (result.skippedNoDesktopApp) {
        // Said once, rather than one failure per vehicle for something the browser could
        // never do — plates are written to the camera over the LAN by the desktop app.
        setSyncProgressData({ zoneName: lprTitle, total: 0, done: 0, ok: 0, fail: 0, recentItems: [], isComplete: true });
        toast('Sincronização de placas LPR exige o aplicativo desktop — as câmeras são acessadas pela rede local');
        return;
      }

      setSyncProgressData({
        zoneName: lprTitle,
        total: result.total, done: result.total, ok: result.ok, fail: result.fail,
        recentItems: [...lprBuf],
        isComplete: true,
      });
      if (result.fail > 0) {
        toast.error(`${result.fail} placa(s) não sincronizada(s) nas câmeras LPR`);
      }
    };

    if (allRfidDevices.length === 0) {
      // Previously this bailed out even when the site had LPR cameras, so a plate-only
      // condomínio could never bulk-sync anything.
      if (hasLprCameras) {
        toast.dismiss(toastId);
        await runLprPlateSync();
      } else {
        toast('Nenhum leitor RFID encontrado', { id: toastId });
      }
      setSyncingAllVehiclesRfid(false);
      return;
    }

    await runLprPlateSync();

    const progressTitle = 'Sincronização RFID de Veículos';
    const isElectronApp = typeof window !== 'undefined' && !!window.electronAPI;

    if (isElectronApp) {
      // Fetch ALL vehicles with RFID tags for this customer (no zone filtering)
      setSyncProgressData({
        zoneName: 'Buscando veículos com TAG RFID...',
        total: 0, done: 0, ok: 0, fail: 0,
        recentItems: [],
        isComplete: false,
      });
      toast.dismiss(toastId);

      let allTaggedVehicles: any[] = [];
      try {
        const resp = await fetchWithAuth(`/api/v1/customers/${customerId}/vehicles?page=1&page_size=5000&has_rfid=true`);
        if (resp.ok) {
          const body = await resp.json();
          allTaggedVehicles = (body.vehicles || body.data || []).map((v: any) => ({
            vehicle_id: v.id,
            rfid_tag: v.rfid_tag,
            plate: v.plate,
            person_name: v.owner_name || v.person?.first_name,
          })).filter((v: any) => v.rfid_tag);
        }
      } catch {
        // ignore — will show 0 vehicles
      }

      const totalVehicles = allTaggedVehicles.length;

      if (totalVehicles === 0) {
        setSyncProgressData({ zoneName: progressTitle, total: 0, done: 0, ok: 0, fail: 0, recentItems: [], isComplete: true });
        toast('Nenhum veículo com TAG RFID encontrado');
        setSyncingAllVehiclesRfid(false);
        return;
      }

      const totalSlots = totalVehicles * allRfidDevices.length;
      setSyncProgressData({
        zoneName: progressTitle,
        total: totalSlots, done: 0, ok: 0, fail: 0,
        recentItems: [],
        isComplete: false,
      });

      let offset = 0;
      let prevOk = 0;
      let prevFail = 0;
      const recentBuf: SyncProgressItem[] = [];

      for (let pi = 0; pi < allRfidDevices.length; pi++) {
        if (syncStopRef.current) break;
        const device = allRfidDevices[pi];
        const deviceInfo = apiDeviceToDeviceInfo(device);
        const deviceLabel = device.name || device.id;
        let devOk = 0;
        let devFail = 0;

        const vehicleItems: SyncProgressItem[] = [];
        let batchOkPrev = 0;

        try {
          const result = await deviceCommunicationService.forceReSyncControlIDRfidCards(
            { ...deviceInfo, vendor: 'controlid' as any },
            allTaggedVehicles,
            15000,
            (done, _total, itemOk, itemFail) => {
              const v = allTaggedVehicles[done - 1];
              const wasOk = itemOk > batchOkPrev;
              batchOkPrev = itemOk;
              if (v) {
                vehicleItems.push({
                  label: v.plate || v.vehicle_id,
                  owner: v.person_name || deviceLabel,
                  status: wasOk ? 'ok' : 'failed',
                });
                if (vehicleItems.length > 100) vehicleItems.shift();
              }
              setSyncProgressData({
                zoneName: `${progressTitle} — ${deviceLabel}`,
                total: totalSlots,
                done: offset + done,
                ok: prevOk + itemOk,
                fail: prevFail + itemFail,
                recentItems: [...vehicleItems],
                isComplete: false,
              });
            },
          );
          devOk = result.success;
          devFail = result.failure;
        } catch {
          devFail = totalVehicles;
        }

        offset += totalVehicles;
        prevOk += devOk;
        prevFail += devFail;

        recentBuf.push(...vehicleItems);

        const isLast = pi === allRfidDevices.length - 1 || syncStopRef.current;
        setSyncProgressData({
          zoneName: progressTitle,
          total: totalSlots,
          done: offset,
          ok: prevOk,
          fail: prevFail,
          recentItems: [...recentBuf],
          isComplete: isLast,
        });

        if (!isLast) await new Promise(r => setTimeout(r, 300));
      }

      setSyncingAllVehiclesRfid(false);
      return;
    }

    // Browser/cloud path — trigger server-side sync per device
    setSyncProgressData({
      zoneName: progressTitle,
      total: allRfidDevices.length,
      done: 0, ok: 0, fail: 0,
      recentItems: [],
      isComplete: false,
    });
    toast.dismiss(toastId);

    const recentBuf: SyncProgressItem[] = [];
    let ok = 0;
    let fail = 0;

    for (let i = 0; i < allRfidDevices.length; i++) {
      if (syncStopRef.current) break;
      while (syncPauseRef.current) await new Promise(r => setTimeout(r, 200));

      const device = allRfidDevices[i];
      let success = false;
      try {
        const syncRes = await fetchWithAuth(`/api/v1/iot-devices/${device.id}/sync`, { method: 'POST' });
        // fetchWithAuth resolves on 4xx/5xx, so without this check every device was tallied
        // as ok and a run where every sync failed still reported 100% success.
        await ensureOk(syncRes, 'Falha ao sincronizar o dispositivo');
        success = true;
        ok++;
      } catch {
        fail++;
      }

      recentBuf.push({
        label: device.name || device.id,
        owner: device.model || device.device_type || '',
        status: success ? 'ok' : 'failed',
      });
      if (recentBuf.length > 50) recentBuf.shift();

      setSyncProgressData({
        zoneName: progressTitle,
        total: allRfidDevices.length,
        done: i + 1, ok, fail,
        recentItems: [...recentBuf],
        isComplete: i + 1 === allRfidDevices.length,
      });
    }

    setSyncingAllVehiclesRfid(false);
  };

  // Sync tag to devices
  const handleSyncTag = async (tag: PersonTag) => {
    if (!customerId || !tag.person_id) {
      toast.error('Tag sem proprietário — não é possível sincronizar');
      return;
    }
    setSyncingTagId(tag.id);
    try {
      const rfidDeviceIds = await fetchPersonRfidDeviceIds(tag.person_id);
      if (rfidDeviceIds.length === 0) {
        toast('Pessoa sem leitores RFID autorizados');
        return;
      }

      const isElectronApp = typeof window !== 'undefined' && !!window.electronAPI;

      if (isElectronApp) {
        const deviceDetails = await Promise.all(
          rfidDeviceIds.map(async (deviceId: string) => {
            try {
              const res = await api.get(`/iot-devices/${deviceId}`);
              return DeviceIntegration.instance.toHandle(res.data);
            } catch { return null; }
          })
        );
        const validDevices = deviceDetails.filter((d): d is DeviceInfo => d !== null);

        if (validDevices.length > 0) {
          const hasDirectDevices = validDevices.some(d => !!(d.local_ip_address || d.ip_address));
          if (hasDirectDevices) {
            toast.loading(`Sincronizando tag ${tag.tag_value} diretamente...`, { id: `sync-tag-${tag.id}` });
          }
          const personSyncData = {
            person_id: tag.person_id,
            first_name: tag.person?.first_name || '',
            last_name: tag.person?.last_name || '',
            isAllowed: true,
            rfid_tags: [tag.tag_value],
            vehicle_plates: [],
          };
          const results = await Promise.all(
            validDevices.map(deviceInfo =>
              DeviceIntegration.instance.execute(
                deviceInfo,
                DeviceOperations.SYNC_PERSON,
                personSyncData,
                CommunicationMode.HYBRID
              )
            )
          );
          toast.dismiss(`sync-tag-${tag.id}`);
          const successes = results.filter(r => r.success);
          if (successes.length > 0) {
            const localCount = successes.filter(r => r.source === 'local').length;
            const publicCount = successes.filter(r => r.source === 'public').length;
            const parts: string[] = [];
            if (localCount > 0) parts.push(`${localCount} local`);
            if (publicCount > 0) parts.push(`${publicCount} público`);
            toast.success(`Tag ${tag.tag_value} sincronizada com ${successes.length} leitor(es)${parts.length > 0 ? ` (${parts.join(', ')})` : ''}`);
            return;
          }
          if (hasDirectDevices) {
            toast(`Conexão direta falhou, sincronizando via nuvem...`, { duration: 2500 });
          }
        }
      }

      // Cloud fallback
      const response = await api.post(
        `/customers/${customerId}/people/${tag.person_id}/sync`,
        { device_ids: rfidDeviceIds }
      );
      const synced = response.data?.devices_synced || 0;
      if (synced > 0) {
        toast.success(`Tag ${tag.tag_value} sincronizada com ${synced} leitor(es) (nuvem)`);
      } else {
        toast(`Sincronização enviada para tag ${tag.tag_value} (nuvem)`);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || `Erro ao sincronizar tag ${tag.tag_value}`);
    } finally {
      setSyncingTagId(null);
    }
  };

  // Delete tag handler
  const handleDeleteTag = async (tagId: string, tagValue: string) => {
    if (!customerId) return;
    if (!await confirm({ message: `Deseja realmente excluir a tag "${tagValue}"?`, variant: 'danger', confirmLabel: 'Excluir' })) return;
    try {
      const response = await fetchWithAuth(`/api/v1/customers/${customerId}/person-tags/${tagId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Tag excluída!');
        setTagRefreshTrigger(prev => prev + 1);
      } else {
        const err = await response.json();
        toast.error(err.error || 'Erro ao excluir tag');
      }
    } catch (err) {
      toast.error('Erro ao excluir tag');
    }
  };

  // Delete vehicle handler
  const handleDeleteVehicle = async (vehicleId: string, vehiclePlate: string) => {
    if (!customerId) return;

    if (!await confirm({ message: `Deseja realmente excluir o veículo ${vehiclePlate}?`, variant: 'danger', confirmLabel: 'Excluir' })) {
      return;
    }

    try {
      const response = await fetchWithAuth(
        `/api/v1/customers/${customerId}/vehicles/${vehicleId}`,
        {
          method: 'DELETE',
        }
      );

      if (response.ok) {
        toast.success('Veículo excluído!');
        setVehicleRefreshTrigger(prev => prev + 1);
      } else {
        toast.error('Erro ao excluir veículo');
      }
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      toast.error('Erro ao excluir veículo');
    }
  };

  // Delete pet handler
  const handleDeletePet = async (petId: string, petName: string) => {
    if (!customerId) return;
    if (!await confirm({ message: `Deseja realmente excluir o pet ${petName}?`, variant: 'danger', confirmLabel: 'Excluir' })) return;
    try {
      const response = await fetchWithAuth(
        `/api/v1/customers/${customerId}/pets/${petId}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        toast.success('Pet excluído!');
        setPetRefreshTrigger(prev => prev + 1);
      } else {
        toast.error('Erro ao excluir pet');
      }
    } catch {
      toast.error('Erro ao excluir pet');
    }
  };

  // Fetch blocks for moradores filter (runs once when moradores view is opened)
  useEffect(() => {
    if (selectedCadastro !== 'moradores' || !customerId) return;
    fetchWithAuth(`/api/v1/customers/${customerId}/blocks`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const list = (Array.isArray(data) ? data : data?.data ?? []) as { id: string; name: string }[];
        const sorted = list.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { numeric: true, sensitivity: 'base' }));
        setMoradorBlocks(sorted);
        if (sorted.length === 1) setMoradorBlockId(sorted[0].id);
      })
      .catch(() => {});
  }, [selectedCadastro, customerId]);

  // Units for the Liberados unidade filter — one flat searchable list (block + number), since the
  // operator knows the unit, not which block it sits in.
  useEffect(() => {
    if (selectedCadastro !== 'visitantes' || !customerId) return;
    fetchWithAuth(`/api/v1/customers/${customerId}/units?page_size=500`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const list = (Array.isArray(data) ? data : data?.data ?? data?.units ?? []) as any[];
        // "Quadra A QALT01" — the same shape the backend stamps into role_metadata.unit_display,
        // so the option the operator picks reads exactly like the unit line on the cards.
        setVisitUnits(list.map(u => ({
          id: u.id,
          label: [u.block?.name, u.number].filter(Boolean).join(' ') || u.number || '—',
        })));
      })
      .catch(() => {});
  }, [selectedCadastro, customerId]);

  // Fetch units when moradorBlockId changes
  useEffect(() => {
    setMoradorUnitId('');
    setMoradorUnits([]);
    if (!moradorBlockId) return;
    fetchWithAuth(`/api/v1/blocks/${moradorBlockId}/units`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const list = (Array.isArray(data) ? data : data?.data ?? []) as { id: string; number: string }[];
        setMoradorUnits(list.sort((a, b) => a.number.localeCompare(b.number, 'pt-BR', { numeric: true, sensitivity: 'base' })));
      })
      .catch(() => {});
  }, [moradorBlockId]);

  // Fetch departments for funcionarios filter
  useEffect(() => {
    if (selectedCadastro !== 'funcionarios' || !customerId) return;
    fetchWithAuth(`/api/v1/customers/${customerId}/departments`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const list = (Array.isArray(data) ? data : data?.data ?? []) as { id: string; name: string }[];
        setFuncionarioDepts(list.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { numeric: true, sensitivity: 'base' })));
      })
      .catch(() => {});
  }, [selectedCadastro, customerId]);

  // Reset moradores filters when leaving moradores view
  useEffect(() => {
    if (selectedCadastro !== 'moradores') {
      setMoradorRelationType('');
      setMoradorBlockId('');
      setMoradorUnitId('');
      setMoradorPendentes(false);
    }
    if (selectedCadastro !== 'funcionarios') {
      setFuncionarioDeptId('');
      setFuncionarioPendentes(false);
    }
  }, [selectedCadastro]);

  /**
   * The flat unit list behind Cadastros → Unidades.
   *
   * Loaded whenever the Unidades view is open, not only in "Ruas" mode: whether the toggle
   * is offered at all depends on some unit actually having a street, and that answer has to
   * exist before the operator can ask for it.
   */
  useEffect(() => {
    if (selectedCadastro !== 'unidades' || !customerId) {
      setUnidadesFlatUnits([]);
      return;
    }
    let cancelled = false;
    condoApi.searchUnits(customerId, '', { limit: 5000 })
      .then((res: any) => { if (!cancelled) setUnidadesFlatUnits(res.data?.data || []); })
      .catch(() => { if (!cancelled) setUnidadesFlatUnits([]); });
    return () => { cancelled = true; };
  }, [selectedCadastro, customerId, blockRefreshTrigger]);

  // Leaving Unidades drops the drill-down, so coming back starts at the top of the list.
  useEffect(() => {
    if (selectedCadastro !== 'unidades') {
      setUnidadesViewMode('block');
      setUnidadesStreet(null);
    }
  }, [selectedCadastro]);

  const unidadesHasStreets = useMemo(
    () => unidadesFlatUnits.some(u => !!u.street_id && !!u.street_name),
    [unidadesFlatUnits]
  );

  // Fetch cadastro data when selectedCadastro, customerId, cadastroPage, or cadastroSearchTerm changes
  // Uses SERVER-SIDE pagination - only loads one page at a time for performance
  useEffect(() => {
    // This effect has ~30 dependencies, so overlapping runs are routine: switching a
    // Cadastros tab or typing in the search starts a new fetch while the previous one is
    // still in flight. With eight setCadastroData sites spread across as many awaits and no
    // ordering, whichever response landed LAST won — so a superseded branch could paint an
    // empty grid over the one the operator asked for. See utils/requestSequence.
    const isCurrent = cadastroSequence.begin();
    const setCadastroDataIfCurrent = (value: any) => { if (!isCurrent()) setCadastroData(value); };
    const setCadastroTotalItemsIfCurrent = (value: any) => { if (!isCurrent()) setCadastroTotalItems(value); };
    const setCadastroTotalPagesIfCurrent = (value: any) => { if (!isCurrent()) setCadastroTotalPages(value); };

    const fetchCadastroData = async () => {
      if (!customerId || !selectedCadastro || currentView !== 'cadastro') {
        setCadastroDataIfCurrent([]);
        setCadastroTotalItemsIfCurrent(0);
        setCadastroTotalPagesIfCurrent(0);
        return;
      }

      const isVehicleLoadMore = selectedCadastro === 'veiculos' && cadastroPage > 1;
      if (isVehicleLoadMore) {
        setVehicleIsLoadingMore(true);
      } else {
        setIsLoadingCadastro(true);
      }
      try {
        let endpoint = '';
        // Normalize CPF in search term if it's a CPF format (remove dots and dashes)
        const normalizedSearchTerm = cadastroSearchTerm && isCPFFormat(cadastroSearchTerm)
          ? normalizeCPF(cadastroSearchTerm)
          : cadastroSearchTerm;
        let searchParam = normalizedSearchTerm ? `&search=${encodeURIComponent(normalizedSearchTerm)}` : '';

        // Handle 'unidades' - show blocks (blocos) first, not individual units
        if (selectedCadastro === 'unidades') {
          const term = (normalizedSearchTerm || '').trim().toLowerCase();

          // "Ruas" mode: the streets, then the números on the chosen street. Both levels come
          // out of the one flat unit list already loaded for the toggle.
          if (unidadesViewMode === 'street') {
            if (unidadesStreet) {
              const rows: StreetUnitRow[] = unidadesFlatUnits
                .filter(u => u.street_id === unidadesStreet.id)
                .sort((a, b) => (a.street_number || '').localeCompare(b.street_number || '', 'pt-BR', { numeric: true }))
                .map((u): StreetUnitRow => ({
                  kind: 'street-unit',
                  id: u.id,
                  streetNumber: u.street_number || '—',
                  internalLabel: [
                    u.block_name ? `${getBlockLabel(condoType)} ${u.block_name}` : null,
                    u.number ? `${getUnitLabel(condoType)} ${u.number}` : null,
                  ].filter(Boolean).join(' – '),
                }))
                .filter(r => !term
                  || r.streetNumber.toLowerCase().includes(term)
                  || r.internalLabel.toLowerCase().includes(term));
              setCadastroDataIfCurrent(rows as any[]);
              setCadastroTotalItemsIfCurrent(rows.length);
              setCadastroTotalPagesIfCurrent(1);
            } else {
              const byStreet = new Map<string, StreetRow>();
              unidadesFlatUnits.forEach(u => {
                if (!u.street_id || !u.street_name) return;
                const row = byStreet.get(u.street_id);
                if (row) row.unitCount += 1;
                else byStreet.set(u.street_id, { kind: 'street', id: u.street_id, name: u.street_name, unitCount: 1 });
              });
              const rows = Array.from(byStreet.values())
                .filter(r => !term || r.name.toLowerCase().includes(term))
                .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { numeric: true, sensitivity: 'base' }));
              setCadastroDataIfCurrent(rows as any[]);
              setCadastroTotalItemsIfCurrent(rows.length);
              setCadastroTotalPagesIfCurrent(1);
            }
            setIsLoadingCadastro(false);
            return;
          }

          const blocksResponse = await fetchWithAuth(`/api/v1/customers/${customerId}/blocks`);
          if (blocksResponse.ok) {
            const blocksData = await blocksResponse.json();
            const blocks = Array.isArray(blocksData) ? blocksData : (blocksData?.data || []);

            const blocksList = blocks.map((block: any) => ({
              id: block.id,
              name: block.name,
              created_at: block.created_at,
            }));

            blocksList.sort((a: any, b: any) => a.name.localeCompare(b.name, 'pt-BR', { numeric: true, sensitivity: 'base' }));

            // The blocks endpoint takes no search term, so the box above the grid filtered
            // nothing at all until this ran client side.
            const visible = term
              ? blocksList.filter((b: any) => (b.name || '').toLowerCase().includes(term))
              : blocksList;

            setCadastroDataIfCurrent(visible);
            setCadastroTotalItemsIfCurrent(visible.length);
            setCadastroTotalPagesIfCurrent(1);

            // Auto-select when there's only one block — no point forcing the user to click it
            if (!term && blocksList.length === 1 && onLocationClick) {
              onLocationClick(blocksList[0].id, 'block');
            }
          } else {
            setCadastroDataIfCurrent([]);
            setCadastroTotalItemsIfCurrent(0);
            setCadastroTotalPagesIfCurrent(0);
          }
          setIsLoadingCadastro(false);
          return;
        }

        // Server-side pagination: fetch ONLY the current page
        let result: any[] = [];
        let pagination: any = null;

        switch (selectedCadastro) {
          case 'moradores': {
            const mp = new URLSearchParams({ role_type: 'resident', page: String(cadastroPage), page_size: String(cadastroItemsPerPage) });
            if (normalizedSearchTerm) mp.set('search', normalizedSearchTerm);
            if (moradorRelationType) mp.set('relation_type', moradorRelationType);
            if (moradorBlockId) mp.set('block_id', moradorBlockId);
            if (moradorUnitId) mp.set('unit_id', moradorUnitId);
            if (moradorPendentes) mp.set('no_unit', 'true');
            endpoint = `/api/v1/customers/${customerId}/people?${mp.toString()}`;
            break;
          }
          case 'funcionarios': {
            const fp = new URLSearchParams({ page: String(cadastroPage), page_size: String(cadastroItemsPerPage) });
            if (normalizedSearchTerm) fp.set('search', normalizedSearchTerm);
            if (funcionarioDeptId) fp.set('department_id', funcionarioDeptId);
            if (funcionarioPendentes) fp.set('no_department', 'true');
            endpoint = `/api/v1/customers/${customerId}/employees?${fp.toString()}`;
            break;
          }
          case 'visitantes': {
            let dateParam = '';
            if (visitDateFilter === 'today') {
              dateParam = '&created_today=true';
            } else if (visitDateFilter === 'yesterday') {
              const y = new Date(); y.setDate(y.getDate() - 1);
              dateParam = `&created_date=${y.toISOString().split('T')[0]}`;
            } else if (visitDateFilter === '7d') {
              const s = new Date(Date.now() - 6 * 86400000);
              dateParam = `&start_date=${s.toISOString().split('T')[0]}`;
            } else if (visitDateFilter === '30d') {
              const s = new Date(Date.now() - 29 * 86400000);
              dateParam = `&start_date=${s.toISOString().split('T')[0]}`;
            } else if (visitDateFilter === 'custom' && visitCustomStart) {
              dateParam = `&start_date=${visitCustomStart}${visitCustomEnd ? `&end_date=${visitCustomEnd}` : ''}`;
            }
            // 'all' = no date param — returns all visitors
            // Liberações are stored as role_type 'visitor' even for a prestador — that is what the
            // morador app writes (liberacaoPayload), with role_metadata.category as the only thing
            // telling the two apart. So in Liberados the query must ask for both types and the
            // visitante/prestador split happens on the rows (liberacoesPage below); narrowing the
            // QUERY by role_type=service_provider there returned an empty list every time.
            const roleTypeParam = visitLiberadoFilter === 'liberado'
              ? 'visitor,service_provider'
              : visitRoleFilter === 'visitor' ? 'visitor' : visitRoleFilter === 'service_provider' ? 'service_provider' : 'visitor,service_provider';
            const liberadoParam = visitLiberadoFilter === 'liberado' ? '&liberado=true' : '';
            // Narrows to people holding a liberação for THIS unit; their liberações for other
            // units still ride along in the payload, so the rows are narrowed again below.
            const unitParam = visitLiberadoFilter === 'liberado' && visitUnitId ? `&unit_id=${visitUnitId}` : '';
            endpoint = `/api/v1/customers/${customerId}/people?role_type=${roleTypeParam}&include_inactive_roles=true&page=${cadastroPage}&page_size=${cadastroItemsPerPage}${searchParam}${dateParam}${liberadoParam}${unitParam}`;
            break;
          }
          case 'prestadores': {
            const spParams = new URLSearchParams();
            if (normalizedSearchTerm) spParams.set('search', normalizedSearchTerm);
            if (prestadorStatusFilter !== 'all') spParams.set('status', prestadorStatusFilter);
            const spQs = spParams.toString();
            endpoint = `/api/v1/customers/${customerId}/service-providers${spQs ? `?${spQs}` : ''}`;
            break;
          }
          case 'departamentos':
            endpoint = `/api/v1/customers/${customerId}/departments?page=${cadastroPage}&page_size=${cadastroItemsPerPage}${searchParam}`;
            break;
          case 'veiculos': {
            const ownerParam = vehicleOwnerFilter ? `&owner_type=${vehicleOwnerFilter}` : '';
            const rfidParam = vehicleRfidFilter === 'with' ? '&has_rfid=true' : vehicleRfidFilter === 'without' ? '&has_rfid=false' : '';
            endpoint = `/api/v1/customers/${customerId}/vehicles?page=${cadastroPage}&page_size=20${searchParam}${ownerParam}${rfidParam}`;
            break;
          }
          case 'animais':
            endpoint = `/api/v1/customers/${customerId}/pets${searchParam ? `?search=${encodeURIComponent(cadastroSearchTerm)}` : ''}`;
            break;
          case 'tags':
            endpoint = `/api/v1/customers/${customerId}/person-tags?page=${cadastroPage}&page_size=${cadastroItemsPerPage}${searchParam}`;
            break;
          case 'dispositivos':
            endpoint = `/api/v1/iot-devices/customer/${customerId}?page=${cadastroPage}&page_size=${cadastroItemsPerPage}${searchParam}`;
            break;
          default:
            setIsLoadingCadastro(false);
            return;
        }

        console.log(`[${selectedCadastro}] Fetching page ${cadastroPage} (server-side pagination)`);
        const response = await fetchWithAuth(endpoint);

        if (response.ok) {
          const data = await response.json();
          result = data.data || data.people || data.employees || data.devices || data.departments || data.vehicles || data.pets || data.tags || data.items || data || [];
          pagination = data.pagination;

          console.log(`[${selectedCadastro}] Page ${cadastroPage}/${pagination?.total_pages || 1}: loaded ${result.length} items (${pagination?.total || result.length} total)`);
        }

        // Update state with current page data and pagination info
        const resultArray = Array.isArray(result) ? result : [];
        if (isVehicleLoadMore) {
          setCadastroDataIfCurrent(prev => [...prev, ...resultArray]);
        } else {
          setCadastroDataIfCurrent(resultArray);
        }
        setCadastroTotalItemsIfCurrent(pagination?.total ?? result.length ?? 0);
        setCadastroTotalPagesIfCurrent(pagination?.total_pages || 1);
        if (selectedCadastro === 'veiculos') {
          const hasMore = pagination ? pagination.page < pagination.total_pages : false;
          setVehicleHasMore(hasMore);
          setVehicleIsLoadingMore(false);
          vehicleIsLoadingMoreRef.current = false;
        }
      } catch (error) {
        console.error('Error fetching cadastro data:', error);
        if (!isVehicleLoadMore) setCadastroDataIfCurrent([]);
        setCadastroTotalItemsIfCurrent(0);
        setCadastroTotalPagesIfCurrent(0);
        setVehicleIsLoadingMore(false);
        vehicleIsLoadingMoreRef.current = false;
      } finally {
        if (!isVehicleLoadMore) setIsLoadingCadastro(false);
      }
    };

    fetchCadastroData();
  }, [customerId, selectedCadastro, currentView, cadastroPage, cadastroSearchTerm, cadastroItemsPerPage, cadastroRefreshTrigger, vehicleRefreshTrigger, petRefreshTrigger, tagRefreshTrigger, blockRefreshTrigger, unidadesViewMode, unidadesStreet, unidadesFlatUnits, condoType, visitDateFilter, visitCustomStart, visitCustomEnd, visitRoleFilter, visitLiberadoFilter, visitUnitId, prestadorStatusFilter, prestadorDataVersion, vehicleOwnerFilter, vehicleRfidFilter, moradorRelationType, moradorBlockId, moradorUnitId, moradorPendentes, funcionarioDeptId, funcionarioPendentes]);

  // Fetch visitor history function - reusable for initial load and auto-refresh
  // Now fetches RECENT visitors from People API with pagination support
  const fetchVisitorHistory = useCallback(async (page: number = 1, isLoadMore: boolean = false) => {
    if (!customerId || currentView !== 'visitors') {
      return;
    }

    // Set appropriate loading state
    if (isLoadMore) {
      setIsLoadingMoreHistory(true);
    } else {
      setIsLoadingVisitorHistory(true);
    }

    try {
      // Load 30 visitors per page for horizontal scroll
      const pageSize = 30;

      console.log(`[VisitorHistory] Fetching visitors page ${page} (${pageSize} per page)`);

      // Fetch registered visitors and service providers from People API in a single call —
      // the backend already supports a comma-separated role_type list (single SQL query,
      // one consistent pagination window), used elsewhere (LocationDetailView, Blocks, Visitors).
      // Previously this fired two separate paginated queries and concatenated the results,
      // which also made the `hasMoreHistory` check below unreliable (see history.length below).
      // IMPORTANT: include_inactive_roles=true is required because visitor roles may have
      // visitor_state=exited/expired/consumed which the backend treats as "inactive"
      // but we still want to show them in the HISTORY view
      const peopleResponse = await fetchWithAuth(`/api/v1/customers/${customerId}/people?role_type=visitor,service_provider&include_inactive_roles=true&page=${page}&page_size=${pageSize}`);

      let allPeople: any[] = [];

      if (peopleResponse.ok) {
        const peopleData = await peopleResponse.json();
        allPeople = peopleData.data || peopleData.people || [];
        console.log(`[VisitorHistory] Loaded ${allPeople.length} recent visitors/service providers (${peopleData.pagination?.total || '?'} total)`);
      }

      // Debug logging to understand data structure
      console.log('[VisitorHistory] Raw API response:', { peopleCount: allPeople.length });
      if (allPeople.length > 0) {
        console.log('[VisitorHistory] First person sample:', {
          person: allPeople[0],
          roles: allPeople[0]?.roles,
          identity_photo: allPeople[0]?.identity_photo,
        });
      } else {
        console.log('[VisitorHistory] No people returned from API');
      }

      // Transform people with visitor/service_provider roles to HistoricalVisitor format
      const history: HistoricalVisitor[] = allPeople
        .map((person: any) => {
          // Get MOST RECENT visitor or service_provider role
          // CRITICAL: A person can have multiple visitor roles (old expired + new active)
          // We need the NEWEST role by created_at, regardless of is_active status
          const visitorRoles = person.roles?.filter((r: any) =>
            r.role_type === 'visitor' || r.role_type === 'service_provider'
          );
          // Sort by created_at only (newest first) - include recently exited visitors
          const visitorRole = visitorRoles?.sort((a: any, b: any) => {
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
          })[0];
          const metadata = visitorRole?.role_metadata || {};

          // Get primary photo URL using centralized utility
          const photoUrl = getPrimaryPhotoUrl(person as any);

          // Determine timestamp for display - first_entry_at if entered, or created_at
          const entryTimestamp = metadata.first_entry_at || visitorRole?.created_at;

          return {
            id: person.id,
            name: `${person.first_name || ''} ${person.last_name || ''}`.trim() || 'Sem nome',
            photo: photoUrl ? makeAbsolutePhotoUrl(photoUrl) : null,
            unit: metadata.host_unit_name || metadata.host_department_name || metadata.department_name || '—',
            lastVisit: entryTimestamp
              ? new Date(entryTimestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
              : '—',
            lastVisitDate: entryTimestamp
              ? (() => {
                  const date = new Date(entryTimestamp);
                  const today = new Date();
                  const yesterday = new Date(today);
                  yesterday.setDate(yesterday.getDate() - 1);

                  // Check if today
                  if (date.toDateString() === today.toDateString()) {
                    return 'Hoje';
                  }
                  // Check if yesterday
                  if (date.toDateString() === yesterday.toDateString()) {
                    return 'Ontem';
                  }
                  // Format as "6 Jan" for other dates
                  return date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' });
                })()
              : '—',
            personId: person.id,
            visitorState: metadata.visitor_state,
            roleType: visitorRole?.role_type,
          };
        })
        // Sort by most recent first (based on role created_at)
        .sort((a: any, b: any) => {
          const aPerson = allPeople.find((p: any) => p.id === a.personId);
          const bPerson = allPeople.find((p: any) => p.id === b.personId);

          // Get most recent role for each person (by created_at only)
          const aRoles = aPerson?.roles?.filter((r: any) => r.role_type === 'visitor' || r.role_type === 'service_provider');
          const bRoles = bPerson?.roles?.filter((r: any) => r.role_type === 'visitor' || r.role_type === 'service_provider');

          const aRole = aRoles?.sort((x: any, y: any) => {
            return new Date(y.created_at).getTime() - new Date(x.created_at).getTime();
          })[0];
          const bRole = bRoles?.sort((x: any, y: any) => {
            return new Date(y.created_at).getTime() - new Date(x.created_at).getTime();
          })[0];

          const aDate = aRole?.created_at ? new Date(aRole.created_at).getTime() : 0;
          const bDate = bRole?.created_at ? new Date(bRole.created_at).getTime() : 0;
          return bDate - aDate;
        });

      console.log('[VisitorHistory] Transformed history:', history.length, 'visitors');

      // Append or replace history based on load mode
      if (isLoadMore) {
        setVisitorHistory(prev => [...prev, ...history]);
      } else {
        setVisitorHistory(history);
        setIsInitialHistoryLoad(false);
      }

      // Check if there are more pages
      // If we got less than pageSize items, we've reached the end
      setHasMoreHistory(history.length === 30);

    } catch (error) {
      console.error('[VisitorHistory] Error fetching visitor history:', error);
    } finally {
      setIsLoadingVisitorHistory(false);
      setIsLoadingMoreHistory(false);
    }
  }, [customerId, currentView]);

  // Initial fetch and auto-refresh setup for visitor history (15s interval)
  useEffect(() => {
    // Clear existing interval
    if (historyIntervalRef.current) {
      clearInterval(historyIntervalRef.current);
      historyIntervalRef.current = null;
    }

    if (!customerId || currentView !== 'visitors') {
      return;
    }

    // Reset pagination and fetch first page
    setHistoryPage(1);
    setHasMoreHistory(true);
    setIsInitialHistoryLoad(true);
    fetchVisitorHistory(1, false);

    // Disable auto-refresh for now to avoid conflicts with pagination
    // Users will scroll to load more data

    return () => {
      if (historyIntervalRef.current) {
        clearInterval(historyIntervalRef.current);
        historyIntervalRef.current = null;
      }
    };
  }, [customerId, currentView, fetchVisitorHistory]);

  // Handle scroll event to load more history
  const handleHistoryScroll = useCallback(() => {
    const scrollContainer = historyScrollRef.current;
    if (!scrollContainer || !hasMoreHistory || isLoadingMoreHistory) {
      return;
    }

    // Check if scrolled near the end (within 100px of the right edge)
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
    const isNearEnd = scrollLeft + clientWidth >= scrollWidth - 100;

    if (isNearEnd) {
      const nextPage = historyPage + 1;
      setHistoryPage(nextPage);
      fetchVisitorHistory(nextPage, true);
    }
  }, [historyPage, hasMoreHistory, isLoadingMoreHistory, fetchVisitorHistory]);

  // When searching: ask WS for all visitors at once (pageSize=1000) so we can filter client-side.
  // When cleared: restore normal pageSize (50) and pagination.
  useEffect(() => {
    onSearchModeChange?.(!!debouncedFilterTerm.trim());
  }, [debouncedFilterTerm, onSearchModeChange]);

  // Keep refs fresh so the scroll listener (attached once) always reads current values
  useEffect(() => { paginationRef.current = pagination; }, [pagination]);
  useEffect(() => { onNextPageRef.current = onNextPage; }, [onNextPage]);

  // Accumulate visitors across WS pages.
  // Page 1 resets (handles filter/customer change + real-time updates).
  // New pages are appended without duplicates.
  useEffect(() => {
    const page = pagination?.page ?? 1;
    if (page <= 1) {
      setAllLoadedVisitors(activeVisitors);
      loadedMaxPageRef.current = 1;
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
    } else if (page > loadedMaxPageRef.current) {
      loadedMaxPageRef.current = page;
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
      setAllLoadedVisitors(prev => {
        const existingIds = new Set(prev.map(v => v.roleId));
        return [...prev, ...activeVisitors.filter(v => !existingIds.has(v.roleId))];
      });
    }
  }, [activeVisitors, pagination?.page]);


  // Scroll listener attached ONCE — uses refs for fresh values, no churn
  useEffect(() => {
    const container = visitorGridRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (isLoadingMoreRef.current) return;
      const pg = paginationRef.current;
      if (!pg || pg.page >= pg.totalPages) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        isLoadingMoreRef.current = true;
        setIsLoadingMore(true);
        onNextPageRef.current?.();
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []); // empty — attached once on mount


  const filteredVisitors = useMemo(() => {
    return allLoadedVisitors.filter(visitor => {
      if (filterTerm.trim()) {
        // Concatenate all searchable fields into one haystack
        const haystack = normalizeForSearch([
          visitor.name,
          visitor.unit,
          visitor.vehicle ?? '',
          visitor.street_name ?? '',
        ].join(' '));
        // Every token must appear somewhere in the haystack
        const tokens = normalizeForSearch(filterTerm).split(/\s+/).filter(Boolean);
        if (!tokens.every(token => haystack.includes(token))) return false;
      }
      if (roleTypeFilter !== 'all' && visitor.roleType !== roleTypeFilter) return false;
      return statusFilter === 'all' || visitor.status === statusFilter;
    });
  }, [allLoadedVisitors, filterTerm, statusFilter, roleTypeFilter]);

  // Calculate counts for each status
  // CRITICAL: Use allVisitors (all pages) for accurate counts, not just current page!
  const statusCounts = useMemo(() => {
    const visitorsToCount = allVisitors || activeVisitors;  // Use all if available
    return {
      all: visitorsToCount.length,
      inside: visitorsToCount.filter(v => v.status === 'inside').length,
      awaiting: visitorsToCount.filter(v => v.status === 'awaiting').length,
      exited: visitorsToCount.filter(v => v.status === 'exited').length,
      expired: visitorsToCount.filter(v => v.status === 'expired').length,
    };
  }, [allVisitors, activeVisitors]);

  // Sort: pending approval first (needs action now), then awaiting entry, then inside, then
  // exited/expired, then revoked last
  const STATUS_ORDER: Record<string, number> = { pending_approval: 0, awaiting: 1, inside: 2, exited: 3, expired: 3, revoked: 4 };
  const paginatedVisitors = useMemo(() =>
    [...filteredVisitors].sort((a, b) =>
      (STATUS_ORDER[a.status] ?? 2) - (STATUS_ORDER[b.status] ?? 2)
    ),
  [filteredVisitors]);


  // Vehicle infinite scroll — attach to cadastroGridRef
  useEffect(() => {
    const container = cadastroGridRef.current;
    if (!container || selectedCadastro !== 'veiculos') return;

    const handleScroll = () => {
      if (vehicleIsLoadingMoreRef.current || !vehicleHasMore) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollTop + clientHeight >= scrollHeight - 300) {
        vehicleIsLoadingMoreRef.current = true;
        setCadastroPage(p => p + 1);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [selectedCadastro, vehicleHasMore]);

  // Filter visitor history based on search term
  const filteredVisitorHistory = useMemo(() => {
    if (!historySearchTerm.trim()) return visitorHistory;
    const tokens = normalizeForSearch(historySearchTerm).split(/\s+/).filter(Boolean);
    return visitorHistory.filter(visitor => {
      const haystack = normalizeForSearch(`${visitor.name} ${visitor.unit}`);
      return tokens.every(token => haystack.includes(token));
    });
  }, [visitorHistory, historySearchTerm]);

  // Server-side pagination: no client-side filtering needed
  // Data is already filtered and paginated by the API
  const paginatedCadastroData = cadastroData;

  // "Liberados" is a list of LIBERAÇÕES, not a filtered list of people: the same visitor can be
  // liberado for several units at once, each with its own period, grade and intercom rule, so one
  // person becomes one row per unit. The server still decides WHO comes back (?liberado=true) and
  // pages by person; this only expands the page it returned. See utils/liberacoes.
  const showLiberacoes = selectedCadastro === 'visitantes' && visitLiberadoFilter === 'liberado';
  const liberacoesPage = useMemo(() => {
    if (!showLiberacoes) return { rows: [], unmatched: [], ended: [] };
    const page = liberacoesForPeople(paginatedCadastroData);
    let rows = page.rows;
    // The unit query returns the PERSON, and the payload carries every liberação they hold — show
    // only the one for the unit that was asked about.
    if (visitUnitId) rows = rows.filter((r) => r.unitId === visitUnitId);
    // Visitante/prestador is decided by role_metadata.category, not by role_type: a prestador
    // liberação is written as a 'visitor' role too, so this split belongs here and not in the query.
    if (visitRoleFilter !== 'all') {
      const wantServiceProvider = visitRoleFilter === 'service_provider';
      rows = rows.filter((r) => r.isServiceProvider === wantServiceProvider);
    }
    return rows === page.rows ? page : { ...page, rows };
  }, [showLiberacoes, paginatedCadastroData, visitRoleFilter, visitUnitId]);

  /**
   * The clearances on this page that have ENDED, behind a toggle.
   *
   * Off by default: the portaria's question is who may come in NOW, and a list that answers it
   * with last month's visits answers something else. The toggle exists because the count above
   * is the server's — it pages by person and cannot see a visita única whose day has passed —
   * so without a way to look, the gap between "19 liberados" and two cards is unexplainable.
   */
  const [showEndedLiberacoes, setShowEndedLiberacoes] = useState(false);
  const endedLiberacoesRows = useMemo(() => {
    if (!showLiberacoes) return [];
    let rows = historicoForPeople(paginatedCadastroData).rows;
    // The same narrowing the live rows get: a unit filter and the visitante/prestador split.
    if (visitUnitId) rows = rows.filter((r) => r.unitId === visitUnitId);
    if (visitRoleFilter !== 'all') {
      const wantServiceProvider = visitRoleFilter === 'service_provider';
      rows = rows.filter((r) => r.isServiceProvider === wantServiceProvider);
    }
    return rows;
  }, [showLiberacoes, paginatedCadastroData, visitRoleFilter, visitUnitId]);

  // Reset cadastro page to 1 when search term, selection, or date filter changes
  useEffect(() => {
    setCadastroPage(1);
    setVehicleHasMore(false);
    setVehicleIsLoadingMore(false);
    vehicleIsLoadingMoreRef.current = false;
  }, [cadastroSearchTerm, selectedCadastro, visitDateFilter, visitCustomStart, visitCustomEnd, visitRoleFilter, visitLiberadoFilter, visitUnitId, vehicleOwnerFilter, vehicleRfidFilter, moradorRelationType, moradorBlockId, moradorUnitId, moradorPendentes, funcionarioDeptId, funcionarioPendentes]);

  // Detect window height changes for history collapsible state
  useEffect(() => {
    const handleHeightResize = () => {
      const newHasEnoughHeight = window.innerHeight >= 900;
      setHasEnoughHeight(newHasEnoughHeight);
      // Auto-collapse/expand based on screen height
      if (!newHasEnoughHeight && !historyCollapsed) {
        setHistoryCollapsed(true);
      } else if (newHasEnoughHeight && historyCollapsed) {
        setHistoryCollapsed(false);
      }
    };

    window.addEventListener('resize', handleHeightResize);
    return () => window.removeEventListener('resize', handleHeightResize);
  }, [historyCollapsed]);

  // Save history collapsed state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('panther_history_collapsed', JSON.stringify(historyCollapsed));
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [historyCollapsed]);

  // Save history search term to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('panther_history_search', historySearchTerm);
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [historySearchTerm]);

  // Get label for cadastro type
  const getCadastroLabel = () => {
    switch (selectedCadastro) {
      case 'moradores': return 'Moradores';
      case 'funcionarios': return 'Funcionarios';
      case 'visitantes': return 'Visitantes';
      case 'prestadores': return 'Prestadores';
      case 'unidades':
        if (unidadesViewMode === 'street') return unidadesStreet ? 'Números' : 'Ruas';
        return getBlockLabel(condoType, true); // Blocos or Quadras depending on condo type
      case 'departamentos': return 'Departamentos';
      case 'veiculos': return 'Veículos';
      case 'animais': return 'Pets';
      case 'tags': return 'Tags de Acesso';
      case 'dispositivos': return 'Dispositivos';
      case 'relatorios': return 'Relatórios';
      default: return selectedCadastro || '';
    }
  };

  // Get icon for cadastro type
  const getCadastroIcon = () => {
    switch (selectedCadastro) {
      case 'moradores':
      case 'funcionarios':
        return <Users className="h-4 w-4 text-blue-500" />;
      case 'visitantes':
        return <UserCheck className="h-4 w-4 text-green-500" />;
      case 'prestadores':
        return <Briefcase className="h-4 w-4 text-orange-400" />;
      case 'unidades':
        return <Building2 className="h-4 w-4 text-blue-500" />;
      case 'departamentos':
        return <Briefcase className="h-4 w-4 text-orange-500" />;
      case 'veiculos':
        return <Car className="h-4 w-4 text-blue-500" />;
      case 'animais':
        return <PawPrint className="h-4 w-4 text-amber-400" />;
      case 'tags':
        return <Tag className="h-4 w-4 text-orange-400" />;
      case 'dispositivos':
        return <Cpu className="h-4 w-4 text-cyan-500" />;
      case 'relatorios':
        return <FileText className="h-4 w-4 text-blue-500" />;
      default:
        return <FileText className="h-4 w-4 text-blue-500" />;
    }
  };

  /**
   * One liberação — a card in Cadastros → Visitantes → "Liberados".
   *
   * Deliberately NOT the people card: what the operator needs here is the clearance, not the
   * person. Which unit, what it allows, until when, and whether they have to interfonar — a person
   * liberado for três unidades gets três cards, each with its own answer.
   */
  const renderLiberacaoCard = (row: LiberacaoRow, endedLabel?: string) => {
    const person = row.person;
    const photoUrl = getPrimaryPhotoUrl(person as any);
    const vehiclePlate =
      person?.roles?.map((r: any) => r.role_metadata?.vehicle_plate as string | undefined).find((p: any) => !!p) ||
      person?.vehicles?.[0]?.plate;

    // Clearance and "can they come in this minute" are different questions (see
    // models/liberacao.go §3): a grade liberação is perfectly valid at 3am and still outside its
    // window. Saying only "Liberado" is what sent porteiros to open the gate out of hours.
    // Short on the chip, full sentence on hover: at card width a long label pushes the name out.
    // An ended clearance keeps every fact on the card — unit, period, who authorised it — and
    // swaps the live state for how it ended ("Venceu", "Revogada"). Reading it as "Liberado"
    // would defeat the point of hiding these in the first place.
    const nowChip = endedLabel
      ? { label: endedLabel, tone: 'gray' as const, title: 'Esta liberação não vale mais' }
      : row.notStartedYet
      ? { label: 'Não começou', tone: 'amber' as const, title: 'A liberação começa em uma data futura' }
      : row.withinWindowNow === false
        ? { label: 'Fora do horário', tone: 'amber' as const, title: 'Liberação válida, mas fora da grade de horário neste momento' }
        : row.withinWindowNow === true
          ? { label: 'No horário', tone: 'emerald' as const, title: 'Dentro da grade de horário neste momento' }
          : { label: 'Liberado', tone: 'emerald' as const, title: 'Liberação válida agora' };

    const chip = (label: string, tone: 'emerald' | 'amber' | 'blue' | 'gray', title?: string) => {
      const tones: Record<typeof tone, string> = {
        emerald: lightTheme ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
        amber: lightTheme ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-amber-500/20 text-amber-300 border-amber-500/30',
        blue: lightTheme ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-blue-500/20 text-blue-300 border-blue-500/30',
        gray: lightTheme ? 'bg-gray-100 text-gray-600 border-gray-200' : 'bg-gray-800 text-gray-400 border-gray-700',
      };
      return (
        <span
          key={label}
          title={title}
          className={cn('inline-flex items-center whitespace-nowrap px-1.5 py-0.5 rounded text-[10px] font-bold border', tones[tone])}
        >
          {label}
        </span>
      );
    };

    // The accent runs the full height of the card: the operator scans a grid of these for the one
    // that is NOT plain green ("fora do horário", "ainda não começou") before reading any of them.
    const accent = nowChip.tone === 'gray' ? 'bg-gray-300'
      : nowChip.tone === 'amber' ? 'bg-amber-400'
        : 'bg-emerald-500';

    return (
      <div
        key={row.rowKey}
        onClick={() => onPersonClick && row.personId && onPersonClick(row.personId)}
        tabIndex={-1}
        className={cn(
          'relative h-full rounded-lg border overflow-hidden transition-all flex flex-col',
          lightTheme
            ? 'bg-white border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md'
            : 'bg-gray-950 border-gray-800 hover:border-gray-700',
          onPersonClick && (lightTheme ? 'cursor-pointer hover:bg-gray-50' : 'cursor-pointer hover:bg-gray-900'),
        )}
      >
        <span className={cn('absolute left-0 top-0 bottom-0 w-1', accent)} aria-hidden="true" />

        {/* Who */}
        <div className="flex items-start gap-2 pl-3.5 pr-2.5 pt-2.5 pb-2">
          {photoUrl ? (
            <img
              src={toThumbnailUrl(photoUrl) ?? photoUrl}
              alt={row.personName}
              className="w-9 h-11 rounded-md object-cover flex-shrink-0"
            />
          ) : (
            <div className={cn(
              'w-9 h-11 rounded-md flex items-center justify-center flex-shrink-0',
              row.isServiceProvider ? 'bg-gradient-to-br from-orange-400 to-orange-500' : 'bg-gradient-to-br from-blue-500 to-blue-600',
            )}>
              <span className="text-base font-semibold text-[white]">{row.personName.charAt(0).toUpperCase()}</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className={cn('text-sm font-semibold leading-tight truncate', lightTheme ? 'text-gray-900' : 'text-gray-100')} title={row.personName}>
              {row.personName}
            </h3>
            <span className={cn(
              'inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide',
              row.isServiceProvider
                ? lightTheme ? 'bg-orange-100 text-orange-700' : 'bg-orange-500/20 text-orange-400'
                : lightTheme ? 'bg-blue-100 text-blue-700' : 'bg-blue-500/20 text-blue-400',
            )}>
              {row.isServiceProvider ? 'Prestador' : 'Visitante'}
            </span>
          </div>
          <div className="flex-shrink-0">{chip(nowChip.label, nowChip.tone, nowChip.title)}</div>
        </div>

        {/* Which unit — the answer the portaria is actually looking for, so it gets its own band
            and wraps instead of truncating (unit labels carry block + número + rua). */}
        <div className={cn('pl-3.5 pr-2.5 py-1.5 border-y flex items-start gap-1.5', lightTheme ? 'bg-gray-50 border-gray-100' : 'bg-gray-900/40 border-gray-800/60')}>
          <Building2 className={cn('h-3.5 w-3.5 mt-px flex-shrink-0', lightTheme ? 'text-gray-500' : 'text-gray-500')} />
          <span className={cn('text-xs font-bold leading-snug line-clamp-2', lightTheme ? 'text-gray-900' : 'text-gray-100')} title={row.unitLabel}>
            {row.unitLabel}
          </span>
        </div>

        {/* What it allows, and for how long */}
        <div className="pl-3.5 pr-2.5 py-2 space-y-1 flex-1">
          <div className="flex items-start gap-1.5">
            <Clock className={cn('h-3.5 w-3.5 mt-px flex-shrink-0', lightTheme ? 'text-gray-400' : 'text-gray-600')} />
            <span className={cn('text-xs font-medium leading-snug line-clamp-2', lightTheme ? 'text-gray-700' : 'text-gray-300')} title={row.periodLabel}>
              {row.periodLabel}
            </span>
          </div>
          {row.rangeLabel && (
            <div className="flex items-center gap-1.5">
              <Calendar className={cn('h-3.5 w-3.5 flex-shrink-0', lightTheme ? 'text-gray-400' : 'text-gray-600')} />
              <span className={cn('text-xs tabular-nums', lightTheme ? 'text-gray-600' : 'text-gray-400')}>
                {row.rangeLabel}
              </span>
            </div>
          )}
          {vehiclePlate && (
            <div className="pt-0.5">
              <LicensePlate plate={vehiclePlate} size="sm" />
            </div>
          )}
        </div>

        {/* How to receive them, and who vouched */}
        <div className={cn('pl-3.5 pr-2.5 py-2 border-t flex items-center justify-between gap-2', lightTheme ? 'border-gray-100' : 'border-gray-800/60')}>
          {row.requiresIntercom ? chip('Interfonar', 'blue') : chip('Entrada direta', 'gray')}
          {row.authorizedByName && (
            <span
              className={cn('text-[11px] truncate text-right', lightTheme ? 'text-gray-500' : 'text-gray-500')}
              title={`Liberado por ${row.authorizedByName}`}
            >
              Liberado por {row.authorizedByName}
            </span>
          )}
        </div>
      </div>
    );
  };

  // Render cadastro item based on type - grid card layout
  const renderCadastroItem = (item: any, index: number) => {
    if (selectedCadastro === 'prestadores') {
      const statusColors: Record<string, string> = {
        inside: 'text-green-400',
        awaiting_entry: 'text-yellow-400',
        expired: 'text-red-400',
        exited: 'text-gray-400',
      };
      const statusDots: Record<string, string> = {
        inside: 'bg-green-400',
        awaiting_entry: 'bg-yellow-400',
        expired: 'bg-red-400',
        exited: 'bg-gray-500',
      };
      const statusLabels: Record<string, string> = {
        inside: 'Dentro',
        awaiting_entry: 'Aguardando',
        expired: 'Não entrou',
        exited: 'Saiu',
      };
      const windowStart = item.role_metadata?.entry_window_start;
      const windowEnd = item.role_metadata?.entry_window_end;
      const contractorName = item.role_metadata?.contractor_name;
      const noWindowRestriction = item.role_metadata?.no_window_restriction === true;
      const windowLabel = noWindowRestriction ? 'Sempre' : (windowStart ? `${windowStart}–${windowEnd}` : '—');
      return (
        <div
          key={item.role_id || index}
          onClick={() => onPersonClick?.(item.person_id)}
          className={cn("border hover:border-orange-500/40 rounded-lg p-2 cursor-pointer transition-all", lightTheme ? "bg-white border-gray-200" : "bg-gray-900 border-gray-800")}
        >
          <div className="flex items-center gap-2">
            {item.photo ? (
              <img src={makeAbsolutePhotoUrl(item.photo)} alt={item.name} className={cn("w-[26px] h-8 rounded-md object-cover flex-shrink-0 border", lightTheme ? "border-gray-300" : "border-gray-700")} />
            ) : (
              <div className="w-[26px] h-8 rounded-md bg-orange-500/20 border border-orange-500/30 flex items-center justify-center flex-shrink-0">
                <span className="text-orange-400 text-xs font-bold">{item.name?.charAt(0)?.toUpperCase()}</span>
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p style={forceUpper ? { textTransform: 'uppercase' } : undefined} className={cn("text-xs font-medium truncate leading-tight", lightTheme ? "text-gray-900" : "text-gray-100")}>{item.name}</p>
              <p className="text-[10px] text-gray-500 truncate leading-tight">
                {contractorName ? contractorName : windowLabel}
              </p>
              {item.role_metadata?.vehicle_plate && (
                <div className="mt-0.5">
                  <LicensePlate plate={item.role_metadata.vehicle_plate} size="sm" />
                </div>
              )}
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <span className={cn('text-[10px] font-medium', statusColors[item.status_today] || 'text-gray-500')}>
                {statusLabels[item.status_today] || '—'}
              </span>
              {item.status_today && (
                <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', statusDots[item.status_today] || 'bg-gray-600')} />
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setPrestadorEditState({
                    personId: item.person_id,
                    name: item.name,
                    windowStart: item.role_metadata?.entry_window_start ?? '07:00',
                    windowEnd: item.role_metadata?.entry_window_end ?? '09:00',
                    allowedDays: (() => {
                      const raw = item.role_metadata?.allowed_days;
                      if (!raw) return [1,2,3,4,5];
                      if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return [1,2,3,4,5]; } }
                      if (Array.isArray(raw)) return raw.map(Number);
                      return [1,2,3,4,5];
                    })(),
                    contractorId: item.role_metadata?.contractor_id,
                    contractorName: item.role_metadata?.contractor_name,
                  });
                }}
                className="p-0.5 rounded text-gray-500 hover:text-orange-300 hover:bg-orange-500/10 transition-colors"
                title="Editar janela / empresa"
              >
                <Edit2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (selectedCadastro === 'moradores' || selectedCadastro === 'funcionarios' || selectedCadastro === 'visitantes') {
      const person = item as Person;
      const fullName = `${person.first_name || ''} ${person.last_name || ''}`.trim() || 'Sem nome';
      // Use centralized photo selection utility
      const photoUrl = getPrimaryPhotoUrl(person as any);
      const isClickable = !!onPersonClick; // Allow clicks for moradores, funcionarios, AND visitantes

      // Get role info — use the MOST RECENT visitor/service_provider role (sorted by created_at desc)
      // A person can have many visitor roles (one per visit), roles[0] could be oldest
      const visitorRoles = person.roles?.filter((r: any) =>
        r.role_type === 'visitor' || r.role_type === 'service_provider'
      ) || [];
      const primaryRole = visitorRoles.length > 0
        ? [...visitorRoles].sort((a: any, b: any) =>
            new Date((b as any).created_at || 0).getTime() - new Date((a as any).created_at || 0).getTime()
          )[0]
        : person.roles?.[0];
      const roleType = primaryRole?.role_type || selectedCadastro.slice(0, -1); // remove 's' from end
      const roleLabel = roleType === 'morador' ? 'Morador' :
                        roleType === 'resident' ? 'Morador' :
                        roleType === 'funcionario' ? 'Funcionário' :
                        roleType === 'employee' ? 'Funcionário' :
                        roleType === 'staff' ? 'Funcionário' :
                        roleType === 'visitor' ? 'Visitante' :
                        roleType === 'service_provider' ? 'Prestador' : roleType;
      const vehiclePlate =
        person.roles?.map(r => r.role_metadata?.vehicle_plate as string | undefined).find(p => !!p) ||
        person.vehicles?.[0]?.plate;

      // Get department/unit info. role_metadata (not the top-level department_name/unit_name
      // fields, which the backend never populates) carries unit_number/block_name/street_name —
      // resolve the resident role explicitly since primaryRole prefers visitor/service_provider roles.
      const residentRole = person.roles?.find((r: any) => r.role_type === 'resident' && r.is_active !== false);
      const staffRole = person.roles?.find((r: any) => (r.role_type === 'staff' || r.role_type === 'funcionario') && r.is_active !== false);
      // For funcionarios, the fallback location must come from the staff role only — never from
      // primaryRole (which prefers visitor/service_provider roles, or just person.roles[0]) or a
      // resident role the same person happens to also have. Otherwise unrelated metadata (a lot
      // number from when they were also a resident, a phone/email from an old visitor record)
      // leaks into the "Funcionário" card's location line instead of correctly showing nothing.
      const displayRole = selectedCadastro === 'funcionarios' ? staffRole : (residentRole || primaryRole);
      const unitMeta = displayRole?.role_metadata as Record<string, any> | undefined;
      const departmentName = selectedCadastro === 'funcionarios'
        ? ((item as any).department_name || staffRole?.role_metadata?.department_name)
        : ((item as any).department_name || primaryRole?.department_name || staffRole?.role_metadata?.department_name);
      // Street and block/unit are shown as two stacked lines (not one-or-the-other) — a unit can
      // have both a street address AND a quadra/lote or bloco/unidade assignment.
      const unitStreetLine = unitMeta?.street_name
        ? (unitMeta.street_number ? `${unitMeta.street_name}, ${unitMeta.street_number}` : unitMeta.street_name)
        : null;
      const unitBlockLine = unitMeta && (unitMeta.unit_number || unitMeta.block_name)
        ? formatUnitLabel({ number: unitMeta.unit_number, blockName: unitMeta.block_name }, condoType)
        : (!unitStreetLine ? ((item as any).unit_name || displayRole?.unit_name) : null);
      const locationInfo = departmentName || unitBlockLine;

      // Last visit: prefer last_entry_at (when they physically entered), fallback to first_entry_at
      // Uses the most recent role so this reflects their most recent actual visit
      const lastVisitAt = (primaryRole as any)?.role_metadata?.last_entry_at || (primaryRole as any)?.role_metadata?.first_entry_at;

      const isServiceProviderRole = roleType === 'service_provider';
      // Shown on resident cards only: a visitante has no relation to a unit, and a funcionário's
      // old resident role must not relabel their Funcionário card.
      //
      // Position AND grau de parentesco — "Proprietário · Pai" — because they are two facts and
      // the card used to show only the first, while the person modal one click away showed both.
      // Moradores read position + grau de parentesco ("Proprietário · Pai"); funcionários read
      // the position they hold in the DEPARTMENT the card is showing (Responsável, Inquilino —
      // the company rents its space, and so on). Scoped by `kind`, because a funcionário who also
      // lives in the condomínio must not have their Funcionário card relabelled by the other hat.
      const relationLabel = selectedCadastro === 'moradores'
        ? residentRelationChipLabel(person)
        : selectedCadastro === 'funcionarios'
          ? personRelationLabel(person, { kind: 'staff', departmentId: funcionarioDeptId || undefined })
          : null;

      // Detect pending assignment for moradores (no unit) or funcionarios (no dept)
      const hasPendingAssignment = (() => {
        if (selectedCadastro === 'moradores') {
          const residentRole = person.roles?.find((r: any) => r.role_type === 'resident' && r.is_active !== false);
          if (!residentRole) return true;
          const meta = residentRole.role_metadata;
          if (!meta) return true;
          return !meta.unit_id &&
            !(meta.unit_ids && meta.unit_ids.length > 0) &&
            !(meta.unit_assignments && meta.unit_assignments.length > 0);
        }
        if (selectedCadastro === 'funcionarios') {
          const staffRole = person.roles?.find((r: any) => r.role_type === 'staff' && r.is_active !== false);
          if (!staffRole) return true;
          const meta = staffRole.role_metadata;
          if (!meta) return true;
          return !meta.department_id &&
            !(meta.department_ids && meta.department_ids.length > 0) &&
            !(meta.departments && meta.departments.length > 0);
        }
        return false;
      })();

      return (
        <div
          key={person.id || index}
          onClick={() => isClickable && onPersonClick(person.id)}
          tabIndex={-1}
          className={cn(
            "rounded-lg p-3 border transition-all flex flex-col",
            lightTheme
              ? "bg-white border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md"
              : "bg-gray-950 border-gray-800 hover:border-gray-700",
            isClickable && (lightTheme ? "cursor-pointer hover:bg-gray-50" : "cursor-pointer hover:bg-gray-900")
          )}
        >
          {/* Card Header with Photo */}
          <div className="flex items-start gap-2 mb-2">
            {photoUrl ? (
              <img
                src={toThumbnailUrl(photoUrl) ?? photoUrl}
                alt={fullName}
                className="w-10 h-12 rounded-md object-cover flex-shrink-0"
              />
            ) : (
              <div className={cn("w-10 h-12 rounded-md flex items-center justify-center flex-shrink-0", isServiceProviderRole ? "bg-gradient-to-br from-orange-400 to-orange-500" : "bg-gradient-to-br from-blue-500 to-blue-600")}>
                <span className="text-base font-semibold text-white">
                  {fullName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className={cn("text-sm font-semibold truncate", lightTheme ? "text-gray-900" : "text-gray-200")} title={fullName}>
                {fullName}
              </h3>
              <div className="flex flex-wrap items-center gap-1 mt-0.5">
                <span className={cn(
                  "inline-block px-1.5 py-0.5 rounded text-xs font-bold border",
                  isServiceProviderRole
                    ? lightTheme ? "bg-orange-100 text-orange-700 border-orange-200" : "bg-orange-500/20 text-orange-400 border-orange-500/20"
                    : lightTheme ? "bg-blue-100 text-blue-700 border-blue-200" : "bg-blue-500/20 text-blue-400 border-blue-500/20"
                )}>
                  {roleLabel}
                </span>
                {/* Position in the unit + grau de parentesco. One NEUTRAL chip, never a colour
                    per category: the role chip beside it already carries the only colour this row
                    needs, and the relation pills above the list filter on a value the cards never
                    showed. */}
                {relationLabel && (
                  <span className={cn(
                    "inline-block px-1.5 py-0.5 rounded text-xs font-medium border",
                    lightTheme ? "bg-gray-100 text-gray-600 border-gray-200" : "bg-gray-800 text-gray-400 border-gray-700"
                  )}>
                    {relationLabel}
                  </span>
                )}
                {hasPendingAssignment && (
                  <span className={cn(
                    "inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-bold border",
                    lightTheme ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-amber-500/20 text-amber-400 border-amber-500/20"
                  )}>
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                    Pendente
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Card Info */}
          <div className="flex-1 space-y-1">
            {departmentName ? (
              <p className={cn("text-xs truncate font-medium", lightTheme ? "text-gray-700" : "text-gray-400")} title={departmentName}>
                <Building2 className="h-3 w-3 inline mr-1" />
                {departmentName}
              </p>
            ) : (unitStreetLine || unitBlockLine) && (
              <div>
                {unitStreetLine && (
                  <p className={cn("text-xs font-semibold truncate", lightTheme ? "text-gray-800" : "text-gray-200")} title={unitStreetLine}>
                    <Building2 className="h-3 w-3 inline mr-1" />
                    {unitStreetLine}
                  </p>
                )}
                {unitBlockLine && (
                  <p className={cn(
                    unitStreetLine ? "text-[11px] font-normal" : "text-xs font-medium truncate",
                    lightTheme ? (unitStreetLine ? "text-gray-500" : "text-gray-700") : (unitStreetLine ? "text-gray-500" : "text-gray-400")
                  )} title={unitBlockLine}>
                    {!unitStreetLine && <Building2 className="h-3 w-3 inline mr-1" />}
                    {unitBlockLine}
                  </p>
                )}
              </div>
            )}
            {lastVisitAt && (
              <div className={cn("mt-1 pt-1.5 border-t", lightTheme ? "border-gray-100" : "border-gray-800/60")}>
                <p className={cn("text-xs font-bold uppercase tracking-widest mb-0.5", lightTheme ? "text-gray-400" : "text-gray-600")}>
                  Último Acesso
                </p>
                <p className={cn("text-sm font-semibold tabular-nums", lightTheme ? "text-gray-800" : "text-gray-200")}>
                  {formatVisitTimestamp(lastVisitAt)}
                </p>
              </div>
            )}
            {person.email && (
              <p className={cn("text-xs truncate", lightTheme ? "text-gray-500" : "text-gray-500")} title={person.email}>
                <Mail className="h-2.5 w-2.5 inline mr-1" />
                {person.email}
              </p>
            )}
            {person.phone && (
              <p className={cn("text-xs truncate", lightTheme ? "text-gray-500" : "text-gray-500")}>
                <Phone className="h-2.5 w-2.5 inline mr-1" />
                {person.phone}
              </p>
            )}
            {vehiclePlate && (
              <div className="mt-0.5">
                <LicensePlate plate={vehiclePlate} size="sm" />
              </div>
            )}
          </div>
        </div>
      );
    }

    // 'unidades' cadastro now shows blocks - clicking a block will show its units
    if (selectedCadastro === 'unidades') {
      // "Ruas" mode — a street card, then a número card per lot on it.
      const row = item as Partial<StreetRow> | Partial<StreetUnitRow>;
      if (row.kind === 'street') {
        return (
          <div
            key={row.id}
            onClick={() => setUnidadesStreet({ id: row.id!, name: row.name! })}
            tabIndex={-1}
            className={cn(
              "rounded-lg p-3 border transition-all cursor-pointer",
              lightTheme
                ? "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                : "bg-gray-950 border-gray-800 hover:border-gray-700 hover:bg-gray-900"
            )}
          >
            <h3 className={cn("text-sm font-semibold", lightTheme ? "text-gray-900" : "text-gray-200")}>{row.name}</h3>
            <span className="text-xs text-gray-500">
              {row.unitCount} {row.unitCount === 1 ? getUnitLabel(condoType).toLowerCase() : getUnitLabel(condoType, true).toLowerCase()}
            </span>
          </div>
        );
      }
      if (row.kind === 'street-unit') {
        return (
          <div
            key={row.id}
            onClick={() => onLocationClick && onLocationClick(row.id!, 'unit')}
            tabIndex={-1}
            className={cn(
              "rounded-lg p-3 border transition-all",
              lightTheme
                ? "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                : "bg-gray-950 border-gray-800 hover:border-gray-700 hover:bg-gray-900",
              onLocationClick && "cursor-pointer"
            )}
          >
            <h3 className={cn("text-sm font-semibold", lightTheme ? "text-gray-900" : "text-gray-200")}>{row.streetNumber}</h3>
            <span className="text-xs text-gray-500">{row.internalLabel}</span>
          </div>
        );
      }

      const block = item as Block;
      const isClickable = onLocationClick;
      return (
        <div
          key={block.id || index}
          onClick={() => isClickable && onLocationClick(block.id, 'block')}
          tabIndex={-1}
          className={cn(
            "bg-gray-950 rounded-lg p-3 border border-gray-800 hover:border-gray-700 transition-all",
            isClickable && "cursor-pointer hover:bg-gray-900"
          )}
        >
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-200">{block.name}</h3>
              <span className="text-xs text-gray-500">Clique para ver {getUnitLabel(condoType, true).toLowerCase()}</span>
            </div>
          </div>
        </div>
      );
    }

    if (selectedCadastro === 'departamentos') {
      const dept = item as Department;
      const isClickable = onLocationClick;
      return (
        <div
          key={dept.id || index}
          onClick={() => isClickable && onLocationClick(dept.id, 'department')}
          tabIndex={-1}
          className={cn(
            "rounded-xl border transition-all flex flex-col gap-3 p-4",
            lightTheme
              ? "bg-white border-gray-200 shadow-sm hover:shadow-md hover:border-gray-300"
              : "bg-gray-950 border-gray-800 hover:border-gray-700",
            isClickable && "cursor-pointer"
          )}
        >
          {/* Icon + name + description */}
          <div className="flex items-start gap-3">
            <div className={cn("p-2 rounded-lg flex-shrink-0", lightTheme ? "bg-blue-50" : "bg-blue-500/10")}>
              <Briefcase className={cn("h-4 w-4", lightTheme ? "text-blue-600" : "text-blue-400")} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className={cn("text-sm font-bold truncate leading-snug", lightTheme ? "text-gray-900" : "text-gray-100")}>
                {dept.name}
              </h3>
              {dept.description && (
                <p className={cn("text-xs truncate mt-0.5", lightTheme ? "text-gray-500" : "text-gray-500")}>
                  {dept.description}
                </p>
              )}
            </div>
          </div>
          {/* Employee count */}
          <div className={cn("flex items-center gap-1.5 pt-2 border-t", lightTheme ? "border-gray-100" : "border-gray-800/60")}>
            <Users className={cn("h-3.5 w-3.5", lightTheme ? "text-gray-400" : "text-gray-600")} />
            <span className={cn("text-xs font-bold uppercase tracking-wide", lightTheme ? "text-gray-500" : "text-gray-500")}>
              {dept.employee_count ?? 0} {(dept.employee_count ?? 0) === 1 ? 'funcionário' : 'funcionários'}
            </span>
          </div>
        </div>
      );
    }

    if (selectedCadastro === 'dispositivos') {
      const device = item as IoTDevice;
      const isOnline = device.status === 'online';
      // Clean device name: replace newlines and multiple spaces with single space
      const cleanDeviceName = device.name?.replace(/[\n\r]+/g, ' ').replace(/\s+/g, ' ').trim() || 'Dispositivo sem nome';

      return (
        <div
          key={device.id || index}
          onClick={() => onDeviceClick && onDeviceClick(device.id)}
          tabIndex={-1}
          className={cn(
            "bg-gray-950 rounded-lg p-3 border border-gray-800 hover:border-gray-700 transition-all",
            onDeviceClick && "cursor-pointer hover:bg-gray-900"
          )}
        >
          <div className="flex items-center gap-3">
            {/* Device type icon — neutral, no gradient */}
            {(() => {
              const dt = device.device_type ?? '';
              const DevIcon = /facial|face/.test(dt) ? ScanFace
                : /rfid|card|controlid_rfid/.test(dt) ? Wifi
                : /guarita|gate/.test(dt) ? DoorOpen
                : /controller|access/.test(dt) ? Shield
                : Monitor;
              return (
                <div className="h-10 w-10 rounded-lg bg-gray-800 border border-gray-700 flex items-center justify-center flex-shrink-0">
                  <DevIcon className="h-5 w-5 text-gray-400" />
                </div>
              );
            })()}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="text-sm font-semibold text-gray-200 truncate flex-1 min-w-0 whitespace-nowrap overflow-hidden text-ellipsis" title={device.name}>{cleanDeviceName}</h3>
                <span className="inline-flex items-center gap-1 text-[10px] font-medium flex-shrink-0 whitespace-nowrap text-gray-500">
                  <span className={cn("h-1.5 w-1.5 rounded-full flex-shrink-0", isOnline ? "bg-green-500 animate-pulse" : "bg-gray-600")} />
                  {isOnline ? 'Online' : 'Offline'}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-xs text-gray-500">
                  {deviceTypeLabel(device.device_type)}
                </span>
                {device.zone_name && (
                  <span className="text-xs text-gray-500 truncate">
                    {device.zone_name}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // 'veiculos' cadastro - show vehicles
    if (selectedCadastro === 'veiculos') {
      const vehicle = item as Vehicle;
      const isClickable = !!onPersonClick && !!vehicle.person_id;
      const unitLabel = vehicle.unit
        ? [
            vehicle.unit.street?.name
              ? (vehicle.unit.street_number ? `${vehicle.unit.street.name}, ${vehicle.unit.street_number}` : vehicle.unit.street.name)
              : null,
            formatUnitLabel({ number: vehicle.unit.number, blockName: vehicle.unit.block?.name }, condoType),
          ].filter(Boolean).join(' – ')
        : null;
      const ownerName = vehicle.person
        ? `${vehicle.person.first_name} ${vehicle.person.last_name}`
        : (unitLabel ?? 'Sem proprietário');

      return (
        <div
          key={vehicle.id || index}
          tabIndex={0}
          role="button"
          onClick={() => { setEditingVehicle(vehicle); setShowVehicleDetailModal(true); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setEditingVehicle(vehicle); setShowVehicleDetailModal(true); } }}
          className={cn(
            "group rounded-lg border transition-all flex flex-col cursor-pointer overflow-hidden",
            lightTheme
              ? "bg-white border-gray-200 hover:border-gray-400"
              : "bg-gray-950 border-gray-800 hover:border-gray-700"
          )}
        >
          {/* Photo zone — always present */}
          <VehiclePhotoZone photoUrl={vehicle.photo_url} plate={vehicle.plate} lightTheme={lightTheme} />

          <div className="p-3 flex flex-col gap-2">
            {/* Header: plate + actions */}
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <LicensePlate plate={vehicle.plate} size="sm" />
                <div className="flex items-center gap-1 flex-wrap mt-0.5">
                  {vehicle.is_active ? (
                    <span className={cn("inline-block px-1.5 py-0.5 rounded text-xs font-medium", lightTheme ? "bg-green-100 text-green-700" : "bg-green-500/20 text-green-400")}>
                      Ativo
                    </span>
                  ) : (
                    <span className={cn("inline-block px-1.5 py-0.5 rounded text-xs font-medium", lightTheme ? "bg-gray-100 text-gray-500" : "bg-gray-500/20 text-gray-400")}>
                      Inativo
                    </span>
                  )}
                  {vehicle.owner_type && (() => {
                    const ownerTypeMap: Record<string, { label: string; darkCls: string; lightCls: string }> = {
                      resident:         { label: customerType === 'condominio' ? 'Morador' : 'Residente', darkCls: 'bg-blue-500/20 text-blue-400', lightCls: 'bg-blue-100 text-blue-700' },
                      employee:         { label: 'Funcionário', darkCls: 'bg-indigo-500/20 text-indigo-400', lightCls: 'bg-indigo-100 text-indigo-700' },
                      visitor:          { label: 'Visitante',   darkCls: 'bg-green-500/20 text-green-400',  lightCls: 'bg-green-100 text-green-700' },
                      service_provider: { label: 'Prestador',   darkCls: 'bg-orange-500/20 text-orange-400', lightCls: 'bg-orange-100 text-orange-700' },
                      company:          { label: 'Empresa',     darkCls: 'bg-gray-500/20 text-gray-400',    lightCls: 'bg-gray-100 text-gray-600' },
                    };
                    const info = ownerTypeMap[vehicle.owner_type];
                    return info ? (
                      <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${lightTheme ? info.lightCls : info.darkCls}`}>
                        {info.label}
                      </span>
                    ) : null;
                  })()}
                </div>
              </div>
              {/* Action buttons */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {(vehicle.person_id || vehicle.unit_id) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSyncVehicle(vehicle);
                    }}
                    disabled={syncingVehicleId === vehicle.id}
                    className={cn(
                      'p-1.5 rounded transition-all',
                      syncingVehicleId === vehicle.id
                        ? 'text-gray-400 cursor-not-allowed'
                        : lightTheme
                          ? 'hover:bg-blue-50 text-gray-400 hover:text-blue-600'
                          : 'hover:bg-blue-500/10 text-gray-500 hover:text-blue-400'
                    )}
                    title="Sincronizar com leitores"
                  >
                    <RefreshCw className={cn('h-3.5 w-3.5', syncingVehicleId === vehicle.id && 'animate-spin')} />
                  </button>
                )}
                {(vehicle.person_id || vehicle.unit_id) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRevokeVehicle(vehicle);
                    }}
                    disabled={revokingVehicleId === vehicle.id}
                    className={cn(
                      'p-1.5 rounded transition-all',
                      revokingVehicleId === vehicle.id
                        ? 'text-gray-400 cursor-not-allowed'
                        : lightTheme
                          ? 'hover:bg-red-50 text-gray-400 hover:text-red-600'
                          : 'hover:bg-red-500/10 text-gray-500 hover:text-red-400'
                    )}
                    title="Revogar acesso"
                  >
                    <ShieldOff className={cn('h-3.5 w-3.5', revokingVehicleId === vehicle.id && 'animate-pulse')} />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteVehicle(vehicle.id, vehicle.plate);
                  }}
                  className={cn(
                    "p-1.5 rounded text-red-500 transition-all",
                    lightTheme ? "hover:bg-red-50 hover:text-red-700" : "hover:bg-gray-800 hover:text-red-300"
                  )}
                  title="Excluir veículo"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {/* Vehicle details */}
            <div className="flex-1 space-y-1">
              {(vehicle.brand || vehicle.model) && (
                <p className={cn("text-xs truncate", lightTheme ? "text-gray-600" : "text-gray-400")}>
                  {[vehicle.brand, vehicle.model].filter(Boolean).join(' ')}
                </p>
              )}
              {vehicle.color && (
                <p className={cn("text-xs truncate", lightTheme ? "text-gray-500" : "text-gray-500")}>
                  Cor: {vehicle.color}
                </p>
              )}
              {vehicle.year && (
                <p className={cn("text-xs", lightTheme ? "text-gray-500" : "text-gray-500")}>
                  Ano: {vehicle.year}
                </p>
              )}
              {vehicle.rfid_tag && (
                <p className={cn("text-xs truncate", lightTheme ? "text-gray-500" : "text-gray-500")} title={vehicle.rfid_tag}>
                  RFID: {vehicle.rfid_tag}
                </p>
              )}
            </div>

            {/* Owner / unit info */}
            <div className={cn("pt-2 border-t", lightTheme ? "border-gray-200" : "border-gray-800")}>
              {isClickable ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (vehicle.person_id) onPersonClick(vehicle.person_id);
                  }}
                  className={cn(
                    "w-full flex items-start gap-1.5 -mx-1 px-1 py-1 rounded transition-colors",
                    lightTheme ? "hover:bg-gray-100" : "hover:bg-gray-800"
                  )}
                >
                  {(() => {
                    const pic = vehicle.person?.profile_picture_url || vehicle.person?.identity_photo?.photo_url;
                    return pic ? (
                      <img src={makeAbsolutePhotoUrl(pic)} alt="" className="h-4 w-4 rounded-full object-cover flex-shrink-0 mt-0.5" />
                    ) : (
                      <div className={cn("h-4 w-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-[8px] font-bold", lightTheme ? "bg-blue-100 text-blue-600" : "bg-blue-500/20 text-blue-400")}>
                        {(vehicle.person?.first_name?.[0] || '?').toUpperCase()}
                      </div>
                    );
                  })()}
                  <div className="min-w-0 text-left">
                    <p className={cn("text-xs truncate", lightTheme ? "text-gray-600 hover:text-gray-900" : "text-gray-400 hover:text-gray-300")} title={ownerName}>
                      {ownerName}
                    </p>
                    {/* The owning person's unit — shown alongside their name, not instead of
                        it (unlike vehicle.unit below, which only applies with no person). */}
                    {vehicle.owner_unit && (
                      <div>
                        {vehicle.owner_unit.street?.name && (
                          <p className={cn("text-[10px] font-medium truncate", lightTheme ? "text-gray-600" : "text-gray-400")}>
                            {vehicle.owner_unit.street_number ? `${vehicle.owner_unit.street.name}, ${vehicle.owner_unit.street_number}` : vehicle.owner_unit.street.name}
                          </p>
                        )}
                        <p className={cn("text-[10px] truncate", lightTheme ? "text-gray-400" : "text-gray-500")}>
                          {formatUnitLabel({ number: vehicle.owner_unit.number, blockName: vehicle.owner_unit.block?.name }, condoType)}
                        </p>
                      </div>
                    )}
                  </div>
                </button>
              ) : unitLabel && vehicle.unit_id && onLocationClick ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onLocationClick(vehicle.unit_id!, 'unit');
                  }}
                  className={cn(
                    "w-full flex items-start gap-1.5 -mx-1 px-1 py-1 rounded transition-colors",
                    lightTheme ? "hover:bg-gray-100" : "hover:bg-gray-800"
                  )}
                >
                  <Building2 className="h-3 w-3 text-blue-500/60 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 text-left">
                    {vehicle.unit?.street?.name ? (
                      <>
                        <p className={cn("text-xs font-semibold truncate", lightTheme ? "text-blue-600" : "text-blue-400/80")}>
                          {vehicle.unit.street_number ? `${vehicle.unit.street.name}, ${vehicle.unit.street_number}` : vehicle.unit.street.name}
                        </p>
                        <p className={cn("text-[10px] truncate", lightTheme ? "text-gray-400" : "text-gray-500")}>
                          {formatUnitLabel({ number: vehicle.unit?.number, blockName: vehicle.unit?.block?.name }, condoType)}
                        </p>
                      </>
                    ) : (
                      <p className={cn("text-xs font-semibold truncate", lightTheme ? "text-blue-600" : "text-blue-400/80")}>
                        {formatUnitLabel({ number: vehicle.unit?.number, blockName: vehicle.unit?.block?.name }, condoType)}
                      </p>
                    )}
                  </div>
                </button>
              ) : unitLabel ? (
                <div className="flex items-start gap-1.5">
                  <Building2 className="h-3 w-3 text-blue-500/60 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    {vehicle.unit?.street?.name ? (
                      <>
                        <p className={cn("text-xs font-semibold truncate", lightTheme ? "text-blue-600" : "text-blue-400/80")}>
                          {vehicle.unit.street_number ? `${vehicle.unit.street.name}, ${vehicle.unit.street_number}` : vehicle.unit.street.name}
                        </p>
                        <p className={cn("text-[10px] truncate", lightTheme ? "text-gray-400" : "text-gray-500")}>
                          {formatUnitLabel({ number: vehicle.unit?.number, blockName: vehicle.unit?.block?.name }, condoType)}
                        </p>
                      </>
                    ) : (
                      <p className={cn("text-xs font-semibold truncate", lightTheme ? "text-blue-600" : "text-blue-400/80")}>
                        {formatUnitLabel({ number: vehicle.unit?.number, blockName: vehicle.unit?.block?.name }, condoType)}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-1.5">
                  {(() => {
                    const pic = vehicle.person?.profile_picture_url || vehicle.person?.identity_photo?.photo_url;
                    return pic ? (
                      <img src={makeAbsolutePhotoUrl(pic)} alt="" className="h-4 w-4 rounded-full object-cover flex-shrink-0 mt-0.5" />
                    ) : vehicle.person ? (
                      <div className={cn("h-4 w-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-[8px] font-bold", lightTheme ? "bg-blue-100 text-blue-600" : "bg-blue-500/20 text-blue-400")}>
                        {(vehicle.person.first_name?.[0] || '?').toUpperCase()}
                      </div>
                    ) : (
                      <User className={cn("h-3 w-3 flex-shrink-0 mt-0.5", lightTheme ? "text-gray-400" : "text-gray-500")} />
                    );
                  })()}
                  <div className="min-w-0">
                    <p className={cn("text-xs truncate", lightTheme ? "text-gray-600" : "text-gray-400")} title={ownerName}>
                      {ownerName}
                    </p>
                    {vehicle.owner_unit && (
                      <div>
                        {vehicle.owner_unit.street?.name && (
                          <p className={cn("text-[10px] font-medium truncate", lightTheme ? "text-gray-600" : "text-gray-400")}>
                            {vehicle.owner_unit.street_number ? `${vehicle.owner_unit.street.name}, ${vehicle.owner_unit.street_number}` : vehicle.owner_unit.street.name}
                          </p>
                        )}
                        <p className={cn("text-[10px] truncate", lightTheme ? "text-gray-400" : "text-gray-500")}>
                          {formatUnitLabel({ number: vehicle.owner_unit.number, blockName: vehicle.owner_unit.block?.name }, condoType)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // 'animais' cadastro - show pets
    if (selectedCadastro === 'animais') {
      const pet = item as Pet;
      const petUnitStreetLine = pet.unit?.street?.name
        ? (pet.unit.street_number ? `${pet.unit.street.name}, ${pet.unit.street_number}` : pet.unit.street.name)
        : null;
      const petUnitBlockLine = pet.unit
        ? formatUnitLabel({ number: pet.unit.number, blockName: pet.unit.block?.name }, condoType)
        : null;

      return (
        <div
          key={pet.id || index}
          tabIndex={0}
          role="button"
          onClick={() => setViewingPet(pet)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setViewingPet(pet); }}
          className={`group rounded-lg border transition-all flex flex-col cursor-pointer overflow-hidden ${
            lightTheme
              ? 'bg-white border-gray-200 hover:border-amber-300 shadow-sm hover:shadow-md'
              : 'bg-gray-950 border-gray-800 hover:border-gray-700'
          }`}
        >
          <div className="p-3 flex flex-col gap-2">
            {/* Avatar + name + delete */}
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 ${
                lightTheme ? 'bg-amber-50' : 'bg-amber-900/30'
              }`}>
                {pet.photo_url ? (
                  <img src={getAbsoluteImageUrl(pet.photo_url)} alt={pet.name} className="w-full h-full object-cover" />
                ) : (
                  <PawPrint className={`h-6 w-6 ${lightTheme ? 'text-amber-400' : 'text-amber-700/50'}`} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`text-sm font-bold truncate ${lightTheme ? 'text-gray-900' : 'text-gray-200'}`} title={pet.name}>
                  {pet.name}
                </h3>
                {/* Espécie · [raça]. The card used to show the breed alone, so "Siamês" left the
                    reader to work out it was a cat — and a pet with only an espécie recorded said
                    nothing at all. The raça is the chip because it is the identifying half; it is
                    neutral-toned, like every other classification chip in the product. */}
                {(() => {
                  const species = petSpeciesLabel(pet.species);
                  const breed = petBreedLabel(pet.breed);
                  if (!species && !breed) return null;
                  // Espécie and raça are the same KIND of fact, so they read as one pair of chips
                  // rather than grey text beside a chip — the espécie was the faint half of a line
                  // whose other half had already been fixed. The raça is weighted more because it
                  // is the identifying one; both stay neutral, since a classification is not a
                  // status.
                  return (
                    <div className="flex items-center gap-1 min-w-0">
                      {species && (
                        <span className={`text-xs font-medium px-2 py-0.5 rounded truncate ${
                          lightTheme ? 'bg-gray-100 text-gray-700' : 'bg-gray-800 text-gray-300'
                        }`}>
                          {species}
                        </span>
                      )}
                      {breed && (
                        <span
                          title={breed}
                          className={`text-xs font-semibold px-2 py-0.5 rounded truncate ${
                            lightTheme ? 'bg-gray-200 text-gray-900' : 'bg-gray-700 text-gray-100'
                          }`}
                        >
                          {breed}
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); handleDeletePet(pet.id, pet.name); }}
                className={`p-1.5 rounded text-red-400 hover:text-red-500 transition-all opacity-0 group-hover:opacity-100 flex-shrink-0 ${
                  lightTheme ? 'hover:bg-red-50' : 'hover:bg-gray-800'
                }`}
                title="Excluir pet"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Description — in practice this is what identifies the animal to a porteiro (whose
                dog it is, "MORADORA RENATA"), so it is read at a glance under pressure. At
                text-xs/gray-600 it was the faintest thing on a card whose other lines are bold or
                blue. Neutral but solid: weight and contrast carry it, not colour. */}
            {pet.description && (
              <p className={`text-sm font-medium leading-snug line-clamp-2 ${lightTheme ? 'text-gray-800' : 'text-gray-300'}`}>{pet.description}</p>
            )}

            {/* Unit link */}
            <div className={`pt-2 border-t ${lightTheme ? 'border-gray-100' : 'border-gray-800'}`}>
              {(petUnitStreetLine || petUnitBlockLine) && pet.unit_id && onLocationClick ? (
                <button
                  onClick={(e) => { e.stopPropagation(); onLocationClick(pet.unit_id!, 'unit'); }}
                  className={`w-full flex items-center gap-1.5 -mx-1 px-1 py-1 rounded transition-colors ${
                    lightTheme ? 'hover:bg-gray-50' : 'hover:bg-gray-800'
                  }`}
                >
                  <Building2 className={`h-3 w-3 flex-shrink-0 ${lightTheme ? 'text-blue-500' : 'text-blue-500/60'}`} />
                  <div className="min-w-0 text-left">
                    {petUnitStreetLine ? (
                      <>
                        <p className={`text-xs truncate ${lightTheme ? 'text-blue-600 hover:text-blue-700' : 'text-blue-400/80 hover:text-blue-300'}`}>
                          {petUnitStreetLine}
                        </p>
                        <p className={`text-[10px] truncate ${lightTheme ? 'text-gray-400' : 'text-gray-500'}`}>{petUnitBlockLine}</p>
                      </>
                    ) : (
                      <p className={`text-xs truncate ${lightTheme ? 'text-blue-600 hover:text-blue-700' : 'text-blue-400/80 hover:text-blue-300'}`}>
                        {petUnitBlockLine}
                      </p>
                    )}
                  </div>
                </button>
              ) : (
                <div className="flex items-center gap-1.5">
                  <Building2 className={`h-3 w-3 flex-shrink-0 ${lightTheme ? 'text-gray-400' : 'text-gray-600'}`} />
                  <p className={`text-xs ${lightTheme ? 'text-gray-400' : 'text-gray-600'}`}>Sem unidade</p>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // 'tags' cadastro - show person tags
    if (selectedCadastro === 'tags') {
      const tag = item as PersonTag;
      const personName = tag.person
        ? `${tag.person.first_name} ${tag.person.last_name}`
        : 'Sem pessoa';
      const isClickable = !!onPersonClick && !!tag.person_id;

      return (
        <div
          key={tag.id || index}
          tabIndex={0}
          role="button"
          onClick={() => { setEditingTag(tag); setShowTagModal(true); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingTag(tag); setShowTagModal(true); } }}
          className={cn(
            'rounded-lg p-3 border transition-all flex flex-col gap-2 cursor-pointer',
            lightTheme ? 'bg-white border-gray-200 hover:border-orange-300 shadow-sm hover:shadow-md' : 'bg-gray-950 border-gray-800 hover:border-gray-700'
          )}
        >
          {/* Header with tag value and actions */}
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center flex-shrink-0">
              <Tag className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <h3 className={cn('text-sm font-bold font-mono truncate', lightTheme ? 'text-gray-900' : 'text-gray-200')} title={tag.tag_value}>
                  {tag.tag_value}
                </h3>
                {tag.tag_type && (
                  <span className={cn('inline-block px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0', lightTheme ? 'bg-gray-100 text-gray-600' : 'bg-gray-700 text-gray-300')}>
                    {tagTypeLabel(tag.tag_type)}
                  </span>
                )}
              </div>
              {tag.is_active ? (
                <span className={cn('inline-block px-1.5 py-0.5 rounded text-xs font-medium', lightTheme ? 'bg-green-100 text-green-700' : 'bg-green-500/20 text-green-400')}>
                  Ativo
                </span>
              ) : (
                <span className={cn('inline-block px-1.5 py-0.5 rounded text-xs font-medium', lightTheme ? 'bg-gray-100 text-gray-500' : 'bg-gray-500/20 text-gray-400')}>
                  Inativo
                </span>
              )}
            </div>
            {/* Action buttons */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSyncTag(tag);
                }}
                disabled={syncingTagId === tag.id || !tag.person_id}
                className={cn(
                  'p-1.5 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
                  lightTheme ? 'hover:bg-green-50 text-green-600 hover:text-green-700' : 'hover:bg-gray-800 text-green-400 hover:text-green-300'
                )}
                title="Sincronizar tag com leitores"
              >
                <RefreshCw className={cn('h-3.5 w-3.5', syncingTagId === tag.id && 'animate-spin')} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingTag(tag);
                  setShowTagModal(true);
                }}
                className={cn(
                  'p-1.5 rounded transition-colors',
                  lightTheme ? 'hover:bg-blue-50 text-blue-600 hover:text-blue-700' : 'hover:bg-gray-800 text-blue-400 hover:text-blue-300'
                )}
                title="Editar tag"
              >
                <Edit2 className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteTag(tag.id, tag.tag_value);
                }}
                className={cn(
                  'p-1.5 rounded transition-colors',
                  lightTheme ? 'hover:bg-red-50 text-red-600 hover:text-red-700' : 'hover:bg-gray-800 text-red-400 hover:text-red-300'
                )}
                title="Excluir tag"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Tag details */}
          <div className="flex flex-wrap gap-x-3 gap-y-0.5">
            {tag.label && (
              <p className={cn('text-xs truncate', lightTheme ? 'text-gray-500' : 'text-gray-400')} title={tag.label}>
                {tag.label}
              </p>
            )}
            {tag.target_devices && tag.target_devices.length > 0 && (
              <div className="flex items-center gap-1">
                <Radio className={cn('h-3 w-3 flex-shrink-0', lightTheme ? 'text-gray-400' : 'text-gray-500')} />
                <p className={cn('text-xs truncate', lightTheme ? 'text-gray-500' : 'text-gray-400')} title={tag.target_devices.map(d => d.name).join(', ')}>
                  {tag.target_devices.map(d => d.name).join(', ')}
                </p>
              </div>
            )}
          </div>

          {/* Person info - clickable if has person_id */}
          <div className={cn('pt-2 border-t', lightTheme ? 'border-gray-100' : 'border-gray-800')}>
            {isClickable ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (tag.person_id) onPersonClick(tag.person_id);
                }}
                className={cn('w-full flex items-center gap-1.5 -mx-1 px-1 py-1 rounded transition-colors', lightTheme ? 'hover:bg-gray-50' : 'hover:bg-gray-800')}
              >
                <User className={cn('h-3 w-3 flex-shrink-0', lightTheme ? 'text-gray-400' : 'text-gray-500')} />
                <p className={cn('text-xs truncate', lightTheme ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-300')} title={personName}>
                  {personName}
                </p>
              </button>
            ) : (
              <div className="flex items-center gap-1.5">
                <User className={cn('h-3 w-3 flex-shrink-0', lightTheme ? 'text-gray-400' : 'text-gray-500')} />
                <p className={cn('text-xs truncate', lightTheme ? 'text-gray-500' : 'text-gray-400')} title={personName}>
                  {personName}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <style>{scrollbarStyles}</style>
      <div className={cn("lg:col-span-3 2xl:col-span-4 border p-2 md:p-3 flex flex-col overflow-hidden min-h-0 min-w-0", currentView === 'visitors' ? "rounded-tr-xl rounded-br-xl rounded-bl-xl" : "rounded-xl", lightTheme ? "bg-gray-100 border-gray-200" : "bg-black border-gray-800")}>
      {currentView === 'history' ? (
        <>
          {/* Device Detail View */}
          <div className="flex items-center justify-between mb-3 flex-shrink-0">
            <div className="flex items-center gap-3">
              {/* Back Button */}
              {onBackFromDeviceHistory && (
                <button
                  onClick={onBackFromDeviceHistory}
                  className={cn(
                    'p-2 rounded-lg border transition-all',
                    lightTheme ? 'bg-white hover:bg-gray-50 border-gray-200 hover:border-gray-300 text-gray-500 hover:text-gray-800' : 'bg-gray-900 hover:bg-gray-800 border-gray-800 hover:border-gray-700 text-gray-400 hover:text-gray-200'
                  )}
                  title="Voltar"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              <h2 className={cn('text-lg font-semibold flex items-center gap-2', lightTheme ? 'text-gray-900' : 'text-gray-200')}>
                <Monitor className="h-4 w-4 text-blue-500" />
                {selectedDeviceHistory?.name}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <div className={cn(
                "w-2 h-2 rounded-full",
                selectedDeviceHistory?.status === 'online' ? "bg-green-500" : "bg-red-500"
              )} />
              <span className={cn('text-xs', lightTheme ? 'text-gray-500' : 'text-gray-400')}>
                {selectedDeviceHistory?.status === 'online' ? 'Online' : 'Offline'}
              </span>
            </div>
          </div>

          {/* Device Info + Actions */}
          {selectedDeviceHistory && (
            <DeviceDetailPanel
              device={selectedDeviceHistory}
              onDeviceAction={onDeviceAction}
              confirm={confirm}
            />
          )}

          <div className="flex items-center gap-2 mb-2 flex-shrink-0 mt-1">
            <History className={cn('h-3.5 w-3.5', lightTheme ? 'text-gray-400' : 'text-gray-500')} />
            <h3 className={cn('text-xs font-semibold uppercase tracking-wide', lightTheme ? 'text-gray-500' : 'text-gray-400')}>Histórico</h3>
          </div>

          {/* History List */}
          <div className="flex-1 overflow-y-scroll space-y-2 min-h-0 custom-scrollbar">
            {isLoadingDeviceHistory ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
              </div>
            ) : deviceHistory.length > 0 ? (
              deviceHistory.map((entry) => {
                const action = entry.action ?? '';
                const rawDirection = entry.direction || '';
                const evtType = entry.eventType || '';
                const isRemoteEvt = evtType === 'remote_door_trigger' || evtType === 'remote_user';
                const isDenied = entry.status === 'error' || /negad/i.test(action);
                const isEntrada = !isDenied && !isRemoteEvt && (/entrada/i.test(action) || rawDirection === 'entrada');
                const isSaida = !isDenied && !isRemoteEvt && (/sa[íi]da/i.test(action) || rawDirection === 'saida');
                const isRemote = !isDenied && (isRemoteEvt || /remot|abertura|aciona|manual/i.test(action));

                const dirBadge = isDenied
                  ? { label: 'Negado', icon: XCircle, cls: lightTheme ? 'bg-red-100 text-red-700 border border-red-300' : 'bg-red-500/15 text-red-400 border border-red-500/20' }
                  : isEntrada
                  ? { label: 'Entrada', icon: DoorOpen, cls: lightTheme ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-green-500/15 text-green-400 border border-green-500/20' }
                  : isSaida
                  ? { label: 'Saída', icon: DoorClosed, cls: lightTheme ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-blue-500/15 text-blue-400 border border-blue-500/20' }
                  : isRemote
                  ? { label: 'Remoto', icon: Fingerprint, cls: lightTheme ? 'bg-indigo-100 text-indigo-700 border border-indigo-300' : 'bg-indigo-500/15 text-indigo-400 border border-indigo-500/20' }
                  : { label: 'Acesso', icon: Check, cls: lightTheme ? 'bg-gray-100 text-gray-600 border border-gray-300' : 'bg-gray-500/15 text-gray-400 border border-gray-500/20' };

                const borderColor = isDenied
                  ? 'border-l-red-500'
                  : isEntrada
                  ? 'border-l-green-500'
                  : isSaida
                  ? 'border-l-blue-500'
                  : isRemote
                  ? 'border-l-indigo-500'
                  : 'border-l-gray-600';

                // No personId = not linked to a registered person in the system
                const isUnregistered = !entry.personId;
                const personName = entry.user || entry.personName || 'Desconhecido';

                // Show "Cadastro fora do sistema" whenever the person has no PantherAccess link
                const showExternalBadge = isUnregistered && !isRemote;
                const secondaryLabel = showExternalBadge ? null : (!isDenied && action && action !== 'Evento' ? action : null);
                // Unit (moradores) / department (funcionários) + role — e.g. "Bloco A - 101 · Proprietário"
                // or "Financeiro · Colaborador". Only registered people have this (role-derived).
                const contextLabel = [entry.unitName, entry.departmentName, entry.personRole].filter(Boolean).join(' · ') || null;
                const BadgeIcon = dirBadge.icon;

                return (
                  <div
                    key={entry.id}
                    onClick={() => entry.personId && onPersonClick?.(entry.personId)}
                    className={cn(
                      "rounded-lg p-3 border border-l-4 transition-all",
                      lightTheme ? "bg-white border-gray-200 shadow-sm" : "bg-gray-950 border-gray-800",
                      borderColor,
                      entry.personId
                        ? (lightTheme ? "cursor-pointer hover:border-gray-300 hover:bg-gray-50" : "cursor-pointer hover:border-gray-600 hover:bg-gray-900/50")
                        : (lightTheme ? "hover:border-gray-300" : "hover:border-gray-700")
                    )}
                  >
                    <div className="flex items-center gap-3">
                      {/* Avatar: fingerprint for remote events, photo/user otherwise */}
                      {isRemote ? (
                        <div className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 bg-indigo-500/10 border border-indigo-500/30">
                          <Fingerprint className="h-5 w-5 text-indigo-400" />
                        </div>
                      ) : entry.personPhoto ? (
                        <img
                          src={makeAbsolutePhotoUrl(toThumbnailUrl(entry.personPhoto) ?? entry.personPhoto) ?? undefined}
                          alt={entry.personName || ''}
                          className={cn('h-10 w-8 rounded-md object-cover flex-shrink-0 border-2', lightTheme ? 'border-gray-200' : 'border-gray-700')}
                        />
                      ) : (
                        <div className={cn('h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 border', lightTheme ? 'bg-gray-100 border-gray-200' : 'bg-gray-800 border-gray-700')}>
                          <User className={cn('h-5 w-5', lightTheme ? 'text-gray-400' : 'text-gray-500')} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className={cn('text-sm font-semibold truncate', lightTheme ? 'text-gray-900' : 'text-gray-100')}>{personName}</h3>
                          <span className={cn('text-xs flex-shrink-0', lightTheme ? 'text-gray-400' : 'text-gray-500')}>{entry.timestamp}</span>
                        </div>
                        {showExternalBadge ? (
                          <div className="flex items-center gap-1 mt-0.5 group/tip relative">
                            <span className={cn('text-xs font-medium', lightTheme ? 'text-gray-500' : 'text-gray-400')}>Cadastro fora do sistema</span>
                            <Info className={cn('h-3 w-3 flex-shrink-0 cursor-help', lightTheme ? 'text-gray-400' : 'text-gray-500')} />
                            {/* Tooltip — always dark, matches the app's universal tooltip convention */}
                            <div className="absolute bottom-full left-0 mb-1.5 hidden group-hover/tip:block z-50 pointer-events-none">
                              <div className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 shadow-xl w-64">
                                <p className="text-xs text-gray-300 leading-relaxed">
                                  Este usuário possui cadastro direto no dispositivo, mas <span className="text-white font-medium">não está vinculado</span> ao sistema PantherAccess.
                                </p>
                              </div>
                              <div className="w-2 h-2 bg-gray-900 border-l border-b border-gray-700 rotate-[-45deg] ml-3 -mt-1" />
                            </div>
                          </div>
                        ) : secondaryLabel ? (
                          <p className={cn('text-xs mt-0.5 truncate', lightTheme ? 'text-gray-400' : 'text-gray-500')}>{secondaryLabel}</p>
                        ) : null}
                        {contextLabel && (
                          <p className={cn('text-[11px] mt-0.5 truncate', lightTheme ? 'text-gray-400' : 'text-gray-600')}>{contextLabel}</p>
                        )}
                      </div>
                      {/* Direction badge */}
                      <div className={cn("inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium flex-shrink-0", dirBadge.cls)}>
                        <BadgeIcon className="h-3 w-3" />
                        {dirBadge.label}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className={cn('flex items-center justify-center h-full text-sm', lightTheme ? 'text-gray-400' : 'text-gray-500')}>
                Nenhum historico disponivel
              </div>
            )}
          </div>
        </>
      ) : currentView === 'visitors' ? (
        <>
          {/* Active Visitors View - Single header row */}
          <div className="mb-2 flex-shrink-0 relative z-10" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', alignItems: 'center', gap: '8px' }}>
            {/* Search input - grows to fill available space */}
            <div className="relative min-w-0" style={{ flex: '1 1 0%' }}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome, unidade ou veículo..."
                value={filterTerm}
                onChange={(e) => setFilterTerm(e.target.value)}
                className={cn(
                  "w-full pl-9 pr-4 py-1.5 text-sm rounded-lg border focus:outline-none focus:border-blue-500 placeholder-gray-500",
                  lightTheme ? "bg-white text-gray-800 border-gray-300" : "bg-gray-950 text-gray-200 border-gray-800"
                )}
              />
            </div>

            {/* Right-side controls — all in one no-wrap group so they can never drop to a second row */}
            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              {/* Count labels — enterprise style */}
              {customerId && (
                <div className="flex items-center gap-3">
                  <div className="flex flex-col items-center leading-none">
                    <span className={cn("text-sm font-bold tabular-nums", lightTheme ? "text-gray-800" : "text-gray-100")}>
                      {pagination
                        ? (statusFilter !== 'all' ? `${filteredVisitors.length}/${pagination.total}` : pagination.total)
                        : (statusFilter !== 'all' ? `${filteredVisitors.length}/${activeVisitors.length}` : activeVisitors.length)
                      }
                    </span>
                    <span className={cn("text-[9px] uppercase tracking-widest font-medium mt-0.5", lightTheme ? "text-gray-400" : "text-gray-500")}>ativos</span>
                  </div>
                  {todayVisitorServerCount !== null && todayVisitorServerCount > 0 && (
                    <div className="flex flex-col items-center leading-none">
                      <span className={cn("text-sm font-bold tabular-nums", lightTheme ? "text-emerald-600" : "text-emerald-400")}>
                        ↑{todayVisitorServerCount.toLocaleString('pt-BR')}
                      </span>
                      <span className={cn("text-[9px] uppercase tracking-widest font-medium mt-0.5", lightTheme ? "text-gray-400" : "text-gray-500")}>hoje</span>
                    </div>
                  )}
                </div>
              )}

              {/* Role type toggle group */}
              <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', alignItems: 'center' }}>
                {([
                  { key: 'all', label: 'Todos' },
                  { key: 'visitor', label: 'Visitantes' },
                  { key: 'service_provider', label: 'Prestadores' },
                ] as const).map(({ key, label }, i) => (
                  <button
                    key={key}
                    onClick={() => setRoleTypeFilter(key)}
                    className={cn(
                      'px-2.5 py-1.5 text-xs font-medium border transition-colors relative',
                      i === 0 ? 'rounded-l-lg' : i === 2 ? 'rounded-r-lg -ml-px' : '-ml-px',
                      roleTypeFilter === key
                        ? key === 'service_provider'
                          ? lightTheme ? 'bg-orange-100 border-orange-400 text-orange-700 z-10' : 'bg-orange-500/20 border-orange-500/60 text-orange-300 z-10'
                          : lightTheme ? 'bg-blue-100 border-blue-400 text-blue-700 z-10' : 'bg-blue-500/20 border-blue-500/60 text-blue-300 z-10'
                        : lightTheme ? 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50' : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-gray-200 hover:bg-gray-700',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Status filter dropdown */}
              <StatusFilterDropdown
                value={statusFilter}
                onChange={setStatusFilter}
                counts={statusCounts}
                lightTheme={lightTheme}
              />

              {/* Refresh button + timer — only when WS is down */}
              {(!visitorWsStatus || visitorWsStatus === 'disconnected') && (
                <>
                  {lastRefreshTime && <RefreshTimer lastRefreshTime={lastRefreshTime} />}
                  <button
                    onClick={handleRefresh}
                    disabled={isRefreshing || !onRefreshVisitors}
                    className={cn(
                      "p-1.5 rounded-lg transition-all",
                      isRefreshing
                        ? "text-blue-500"
                        : lightTheme ? "text-gray-500 hover:text-gray-700 hover:bg-gray-100" : "text-gray-400 hover:text-gray-200 hover:bg-gray-800"
                    )}
                    title="Atualizar visitantes"
                  >
                    <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Show message if no customer selected */}
          {!customerId ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 text-sm">
              <Building2 className="h-12 w-12 text-gray-700 mb-3" />
              <p>Selecione um cliente para ver os visitantes</p>
            </div>
          ) : (
            <>

              {/* Container for visitors grid and history - NO scroll here */}
              <div className="flex-1 flex flex-col min-h-0 gap-2">
              {/* Visitantes Ativos - scrollable independently */}
              <div ref={visitorGridRef} className="flex-1 min-h-[160px] overflow-auto custom-scrollbar">
                {isLoadingVisitors ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
                  </div>
                ) : filteredVisitors.length > 0 ? (
                  <>
                    {/* Grid Layout for Visitors - CSS Grid auto-fill for responsive layout */}
                    <div
                      className="grid gap-3 pt-1"
                      style={{
                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                      }}
                    >
                      {paginatedVisitors.map((visitor, index) => {
                        // Enrich with resolved unit_id for old visitors missing it in WS data
                        const enrichedVisitor = visitor.unit_id ? visitor : {
                          ...visitor,
                          unit_id: unitLookup.get(visitor.unit) || undefined,
                        };
                        return (
                        <VisitorCard
                          key={`${visitor.roleId}-${visitor.id}-${index}`}
                          visitor={enrichedVisitor}
                          onPersonClick={onPersonClick}
                          onUnitClick={(unitId) => {
                            if (unitId) onLocationClick?.(unitId, customerType === 'empresa' ? 'department' : 'unit');
                          }}
                          onManualCheckIn={onManualCheckIn}
                          onManualCheckOut={onManualCheckOut}
                          onRevokeAccess={onRevokeAccess}
                          onApproveAccess={onApproveAccess}
                          onPhotoZoom={(url, name) => {
                            // url is the full-res original URL; v.photo is the thumbnail URL — compare via toOriginalUrl
                            const visitor = activeVisitors.find(v => v.photo && toOriginalUrl(makeAbsolutePhotoUrl(v.photo)) === url);
                            if (visitor) {
                              setZoomedPhoto({ url, name, personId: visitor.personId });
                            }
                          }}
                          lightTheme={lightTheme}
                          exitByTime={spExitByTime}
                        />
                        );
                      })}
                    </div>

                    {/* Load-more indicator — only shown while fetching next page */}
                    {isLoadingMore && (
                      <div className="flex justify-center py-3">
                        <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-center w-full h-20 text-gray-600 text-xs">
                    Nenhum visitante ativo no momento
                  </div>
                )}
              </div>

              {/* Visitor History Section — disabled */}
              <div className="hidden">
                <div className="flex items-center justify-between mb-2 py-1">
                  <h3 className="text-base font-semibold flex items-center gap-2 text-gray-300">
                    <Clock className="h-4 w-4 text-blue-500" />
                    Historico de Visitantes
                  </h3>
                  <div className="flex items-center gap-2">
                    {isLoadingVisitorHistory && (
                      <Loader2 className="h-3 w-3 text-blue-500 animate-spin" />
                    )}
                    <span className="text-xs font-medium text-gray-500">
                      {filteredVisitorHistory.length}
                    </span>
                    {/* Refresh Button */}
                    <button
                      onClick={() => {
                        setHistoryPage(1);
                        setHasMoreHistory(true);
                        setIsInitialHistoryLoad(true);
                        fetchVisitorHistory(1, false);
                      }}
                      className="p-1.5 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 transition-colors text-blue-400 hover:text-blue-300"
                      title="Atualizar histórico"
                      disabled={isLoadingVisitorHistory}
                    >
                      <RefreshCw className={cn(
                        "h-4 w-4",
                        isLoadingVisitorHistory && "animate-spin"
                      )} />
                    </button>
                    {/* Collapse/Expand Toggle Button */}
                    <button
                      onClick={() => setHistoryCollapsed(!historyCollapsed)}
                      className="p-1.5 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-colors text-gray-400 hover:text-gray-200 border border-gray-700/50"
                      title={historyCollapsed ? 'Expandir histórico' : 'Recolher histórico'}
                    >
                      <ChevronDown className={cn(
                        "h-4 w-4 transition-transform duration-200",
                        historyCollapsed && "rotate-180"
                      )} />
                    </button>
                  </div>
                </div>

                {/* History Search Bar and List - Only show when not collapsed */}
                {!historyCollapsed && (
                  <>
                    {/* History Search Bar */}
                    <div className="mb-2 relative flex-shrink-0">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
                      <input
                        type="text"
                        placeholder="Buscar no historico..."
                        value={historySearchTerm}
                        onChange={(e) => setHistorySearchTerm(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 bg-gray-950 text-gray-200 text-xs rounded-lg border border-gray-800 focus:outline-none focus:border-blue-500 placeholder-gray-500"
                      />
                    </div>

                    {/* History List - Horizontal Scrolling */}
                    <div className="flex gap-2 overflow-x-auto pb-1 custom-scrollbar min-h-[90px]" data-history-scroll>
                  {isLoadingVisitorHistory && isInitialHistoryLoad ? (
                    <div className="flex items-center justify-center h-20 w-full">
                      <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                    </div>
                  ) : filteredVisitorHistory.length > 0 ? (
                    filteredVisitorHistory.map((visitor, index) => (
                      <div
                        key={`history-${visitor.id}-${index}`}
                        onClick={() => visitor.personId && onPersonClick?.(visitor.personId)}
                        className={cn(
                          "bg-gray-950/50 rounded-lg border border-gray-800/50 hover:border-blue-700/50 transition-all flex-shrink-0 w-52 overflow-hidden",
                          visitor.personId && onPersonClick && "cursor-pointer hover:bg-gray-900/50"
                        )}
                      >
                        <div className="flex gap-0 h-20">
                          {/* Left: Photo */}
                          <div className="w-16 h-20 flex-shrink-0 relative">
                            {visitor.photo ? (
                              <img
                                src={makeAbsolutePhotoUrl(visitor.photo)}
                                alt={visitor.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                                <User className="h-6 w-6 text-white" />
                              </div>
                            )}
                          </div>

                          {/* Right: Info */}
                          <div className="flex-1 min-w-0 p-2 flex flex-col justify-between">
                            <div>
                              <h4 style={forceUpper ? { textTransform: 'uppercase' } : undefined} className="text-xs font-bold text-gray-200 truncate leading-tight">{visitor.name}</h4>
                              <p className="text-xs text-gray-500 truncate mt-0.5">{visitor.unit}</p>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-blue-400 font-bold">{visitor.lastVisitDate}</span>
                              <span className="text-xs text-gray-300 font-semibold">{visitor.lastVisit}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center justify-center h-20 w-full text-gray-600 text-xs">
                      {historySearchTerm ? 'Nenhum visitante encontrado' : 'Nenhum historico disponivel'}
                    </div>
                  )}
                    </div>
                  </>
                )}
              </div>

                {/* Custom scrollbar styles */}
                <style dangerouslySetInnerHTML={{
                  __html: `
                    [data-history-scroll]::-webkit-scrollbar {
                      height: 8px;
                    }
                    [data-history-scroll]::-webkit-scrollbar-track {
                      background: #1a1a1a;
                      border-radius: 4px;
                    }
                    [data-history-scroll]::-webkit-scrollbar-thumb {
                      background: #6b21a8;
                      border-radius: 4px;
                    }
                    [data-history-scroll]::-webkit-scrollbar-thumb:hover {
                      background: #7c3aed;
                    }
                  `
                }} />
              </div>{/* End hidden history section */}
            </>
          )}
        </>
      ) : selectedCadastro === 'chaves' ? (
        <Keys lightTheme={lightTheme} customerId={customerId} />
      ) : selectedCadastro === 'relatorios' ? (
        <EventosGerais onBack={onResetCadastro} customerId={customerId ?? undefined} customerType={customerType} />
      ) : (
        <>
          {/* Cadastro View */}
          <div className="flex items-center gap-2 mb-2 flex-shrink-0">
            {/* Back out of a street before the title, so the crumb reads left to right */}
            {selectedCadastro === 'unidades' && unidadesViewMode === 'street' && unidadesStreet && (
              <button
                onClick={() => setUnidadesStreet(null)}
                title="Voltar para as ruas"
                aria-label="Voltar para as ruas"
                className={cn(
                  "flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors flex-shrink-0",
                  lightTheme ? "bg-gray-100 hover:bg-gray-200 text-gray-700" : "bg-gray-800 hover:bg-gray-700 text-gray-300"
                )}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                <span>{unidadesStreet.name}</span>
              </button>
            )}
            <h2 className="text-sm font-semibold text-gray-200 uppercase tracking-wide flex-shrink-0">
              {getCadastroLabel()}
            </h2>
            <span className={cn(
              "px-1.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0",
              selectedCadastro === 'visitantes' && visitDateFilter !== 'all'
                ? "bg-blue-500/30 text-blue-300 border border-blue-500/30"
                : "bg-blue-500/20 text-blue-400"
            )}>
              {isLoadingCadastro
                ? <Loader2 className="h-3 w-3 animate-spin inline" />
                : (cadastroTotalItems ?? 0).toLocaleString('pt-BR')}
              {selectedCadastro === 'visitantes' && visitDateFilter !== 'all' && (
                <span className="ml-1 opacity-75">
                  · {visitDateFilter === 'today' ? 'hoje' : visitDateFilter === 'yesterday' ? 'ontem' : visitDateFilter === '7d' ? '7d' : visitDateFilter === '30d' ? '30d' : 'período'}
                </span>
              )}
              {/* Says so on the count itself: a narrowed list that looks like the full one is how
                  someone concludes the condo has three visitors.
                  Two numbers on purpose — the server counts PEOPLE (it pages by person) while the
                  list below shows LIBERAÇÕES, and a multi-unit liberação makes the two differ. */}
              {showLiberacoes && !isLoadingCadastro && (
                <span className="ml-1 opacity-75">
                  {cadastroTotalPages > 1
                    ? ` liberados · ${liberacoesPage.rows.length} liberações nesta página`
                    : ` liberados · ${liberacoesPage.rows.length} ${liberacoesPage.rows.length === 1 ? 'liberação' : 'liberações'}`}
                </span>
              )}
            </span>
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500 pointer-events-none" />
              <input
                type="text"
                placeholder={`Buscar ${getCadastroLabel().toLowerCase()}...`}
                value={cadastroSearchTerm}
                onChange={(e) => setCadastroSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 bg-gray-950 text-gray-200 text-xs rounded-lg border border-gray-800 focus:outline-none focus:border-blue-500 placeholder-gray-600"
              />
            </div>
            {/* Quadra/lote ↔ rua/número — only where the condo registers street addresses */}
            {selectedCadastro === 'unidades' && unidadesHasStreets && (
              <div className={cn(
                "flex items-center gap-0.5 p-0.5 rounded-lg flex-shrink-0",
                lightTheme ? "bg-gray-100" : "bg-gray-900"
              )}>
                {([
                  { mode: 'block' as const, label: getBlockLabel(condoType, true) },
                  { mode: 'street' as const, label: 'Ruas' },
                ]).map(({ mode, label }) => (
                  <button
                    key={mode}
                    onClick={() => { setUnidadesViewMode(mode); setUnidadesStreet(null); }}
                    className={cn(
                      "px-2.5 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap",
                      unidadesViewMode === mode
                        ? lightTheme ? "bg-white text-gray-900 shadow-sm" : "bg-gray-700 text-gray-100"
                        : lightTheme ? "text-gray-500 hover:text-gray-800" : "text-gray-500 hover:text-gray-300"
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {/* Add Prestador button */}
              {selectedCadastro === 'prestadores' && (
                <button
                  onClick={() => setShowNewPrestadorModal(true)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-orange-600 hover:bg-orange-500 text-white text-xs font-medium rounded-lg transition-colors"
                  title="Cadastrar novo prestador"
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  <span>Novo</span>
                </button>
              )}
              {/* Add Resident button - only for moradores view */}
              {selectedCadastro === 'moradores' && onRegisterResident && (
                <button
                  onClick={onRegisterResident}
                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
                  title="Adicionar novo morador"
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  <span>Novo</span>
                </button>
              )}
              {/* Add Employee button - only for funcionarios view */}
              {selectedCadastro === 'funcionarios' && onRegisterEmployee && (
                <button
                  onClick={onRegisterEmployee}
                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
                  title="Adicionar novo funcionário"
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  <span>Novo</span>
                </button>
              )}
              {/* Add Tag button - only for tags view */}
              {selectedCadastro === 'tags' && (
                <button
                  onClick={() => {
                    setEditingTag(null);
                    setShowTagModal(true);
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium rounded-lg transition-colors"
                  title="Adicionar nova tag de acesso"
                >
                  <Tag className="h-3.5 w-3.5" />
                  <span>Novo</span>
                </button>
              )}
              {/* Add Vehicle + Sync RFID buttons - only for veiculos view */}
              {selectedCadastro === 'veiculos' && (
                <>
                  <button
                    onClick={handleSyncAllVehiclesRfid}
                    disabled={syncingAllVehiclesRfid || syncingVehicleId !== null}
                    className={cn(
                      'flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border',
                      syncingAllVehiclesRfid || syncingVehicleId !== null
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : lightTheme
                          ? 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300 hover:border-gray-400'
                          : 'bg-gray-900 hover:bg-gray-800 text-gray-300 border-gray-700 hover:border-gray-600'
                    )}
                    title="Sincronizar RFID de todos os veículos com leitores autorizados"
                  >
                    <RefreshCw className={cn('h-3.5 w-3.5', syncingAllVehiclesRfid && 'animate-spin')} />
                    <span>Sincronizar RFID</span>
                  </button>
                  <button
                    onClick={() => {
                      setEditingVehicle(null);
                      setShowVehicleModal(true);
                    }}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                      lightTheme
                        ? "bg-gray-900 hover:bg-gray-700 text-white"
                        : "bg-gray-100 hover:bg-white text-gray-900"
                    )}
                    title="Adicionar novo veículo"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Novo Veículo</span>
                  </button>
                </>
              )}
              {/* Add Block/Quadra button - only for unidades view */}
              {selectedCadastro === 'unidades' && (
                <button
                  onClick={() => setShowBlockForm(true)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
                  title={`Adicionar ${getBlockArticle(condoType).ordinal.toLowerCase()} ${getBlockLabel(condoType).toLowerCase()}`}
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span>Novo</span>
                </button>
              )}
              {/* Add Pet button - only for animais view */}
              {selectedCadastro === 'animais' && (
                <button
                  onClick={() => { setEditingPet(null); setShowPetModal(true); }}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                    lightTheme
                      ? "bg-gray-900 hover:bg-gray-700 text-white"
                      : "bg-gray-100 hover:bg-white text-gray-900"
                  )}
                  title="Cadastrar novo pet"
                >
                  <PawPrint className="h-4 w-4" />
                  <span>Novo Pet</span>
                </button>
              )}
            </div>
          </div>

          {/* Moradores filters — relation type pills + block dropdown + pendentes */}
          {selectedCadastro === 'moradores' && (
            <div className="flex flex-wrap items-center gap-1.5 mb-2 flex-shrink-0">
              {/* Relation type pills */}
              {([
                { key: '', label: 'Todos' },
                { key: 'proprietario', label: 'Proprietário' },
                { key: 'inquilino', label: 'Inquilino' },
                { key: 'dependente', label: 'Dependente' },
                { key: 'familiar', label: 'Familiar' },
              ] as { key: string; label: string }[]).map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => { setMoradorRelationType(key); setCadastroPage(1); }}
                  className={cn(
                    'px-2.5 py-1 rounded-lg text-xs font-medium transition-colors whitespace-nowrap',
                    moradorRelationType === key
                      ? lightTheme ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-900'
                      : lightTheme ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-gray-800 text-gray-400 hover:text-gray-200'
                  )}
                >
                  {label}
                </button>
              ))}

              {/* Block/Quadra filter */}
              {moradorBlocks.length > 0 && (
                <>
                  <div className={cn('w-px h-4 flex-shrink-0', lightTheme ? 'bg-gray-200' : 'bg-gray-700')} />
                  <FilterCombobox
                    value={moradorBlockId}
                    onChange={v => { setMoradorBlockId(v); setCadastroPage(1); }}
                    options={moradorBlocks.map(b => ({ value: b.id, label: b.name }))}
                    allLabel={`Todos ${getBlockLabel(condoType)}s`}
                    dark={!lightTheme}
                    className="min-w-[110px]"
                  />
                </>
              )}

              {/* Unit/Lote filter — shown only after a block is selected */}
              {moradorBlockId && moradorUnits.length > 0 && (
                <FilterCombobox
                  value={moradorUnitId}
                  onChange={v => { setMoradorUnitId(v); setCadastroPage(1); }}
                  options={moradorUnits.map(u => ({ value: u.id, label: u.number }))}
                  allLabel={`Todos ${getUnitLabel(condoType)}s`}
                  dark={!lightTheme}
                  className="min-w-[100px]"
                />
              )}

              {/* Pendentes pill */}
              <div className={cn('w-px h-4 flex-shrink-0', lightTheme ? 'bg-gray-200' : 'bg-gray-700')} />
              <button
                onClick={() => { setMoradorPendentes(p => !p); setCadastroPage(1); }}
                className={cn(
                  'flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors whitespace-nowrap',
                  moradorPendentes
                    ? lightTheme ? 'bg-amber-100 text-amber-700 border border-amber-400' : 'bg-amber-500/20 text-amber-400'
                    : lightTheme ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-gray-800 text-gray-400 hover:text-gray-200'
                )}
              >
                {moradorPendentes && <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', lightTheme ? 'bg-amber-500' : 'bg-amber-500')} />}
                Pendentes
              </button>
            </div>
          )}

          {/* Funcionarios filters — department + pendentes */}
          {selectedCadastro === 'funcionarios' && (
            <div className="flex flex-wrap items-center gap-1.5 mb-2 flex-shrink-0">
              {/* Department filter */}
              {funcionarioDepts.length > 0 && (
                <>
                  <FilterCombobox
                    value={funcionarioDeptId}
                    onChange={v => { setFuncionarioDeptId(v); setCadastroPage(1); }}
                    options={funcionarioDepts.map(d => ({ value: d.id, label: d.name }))}
                    allLabel="Todos Departamentos"
                    dark={!lightTheme}
                    className="min-w-[150px]"
                  />
                  <div className={cn('w-px h-4 flex-shrink-0', lightTheme ? 'bg-gray-200' : 'bg-gray-700')} />
                </>
              )}

              {/* Pendentes pill */}
              <button
                onClick={() => { setFuncionarioPendentes(p => !p); setCadastroPage(1); }}
                className={cn(
                  'flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors whitespace-nowrap',
                  funcionarioPendentes
                    ? lightTheme ? 'bg-amber-100 text-amber-700 border border-amber-400' : 'bg-amber-500/20 text-amber-400'
                    : lightTheme ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-gray-800 text-gray-400 hover:text-gray-200'
                )}
              >
                {funcionarioPendentes && <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', lightTheme ? 'bg-amber-500' : 'bg-amber-500')} />}
                Pendentes
              </button>
            </div>
          )}

          {/* Veículos filters — owner type + RFID tag in one row */}
          {selectedCadastro === 'veiculos' && (
            <div className="flex items-center gap-2 mb-2 flex-wrap flex-shrink-0">
              {/* Owner type pills */}
              <div className="flex items-center gap-1">
                {([
                  { key: '', label: 'Todos' },
                  customerType === 'condominio'
                    ? { key: 'resident', label: 'Morador' }
                    : { key: 'employee', label: 'Funcionário' },
                  { key: 'visitor', label: 'Visitante' },
                  { key: 'service_provider', label: 'Prestador' },
                ] as Array<{ key: string; label: string }>).map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setVehicleOwnerFilter(key)}
                    className={cn(
                      'flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors',
                      vehicleOwnerFilter === key
                        ? lightTheme
                          ? 'bg-gray-900 text-white'
                          : 'bg-gray-200 text-gray-900'
                        : lightTheme
                          ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          : 'bg-gray-800 text-gray-400 hover:text-gray-200'
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Divider */}
              <div className={cn("w-px h-4 flex-shrink-0", lightTheme ? "bg-gray-200" : "bg-gray-700")} />

              {/* RFID tag pills */}
              <div className="flex items-center gap-1">
                {([
                  { key: '' as const, label: 'Todas tags' },
                  { key: 'with' as const, label: 'Com tag RFID' },
                  { key: 'without' as const, label: 'Sem tag RFID' },
                ]).map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setVehicleRfidFilter(key)}
                    className={cn(
                      'flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors',
                      vehicleRfidFilter === key
                        ? key === 'with'
                          ? 'bg-green-700 text-white'
                          : key === 'without'
                            ? 'bg-amber-600 text-white'
                            : lightTheme
                              ? 'bg-gray-900 text-white'
                              : 'bg-gray-200 text-gray-900'
                        : lightTheme
                          ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          : 'bg-gray-800 text-gray-400 hover:text-gray-200'
                    )}
                  >
                    <Tag className="h-3 w-3" />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Prestadores view tabs */}
          {selectedCadastro === 'prestadores' && (
            <div className="flex items-center gap-1 mb-2 flex-shrink-0 border-b border-gray-800 -mx-1 px-1">
              {([
                { key: 'list', label: 'Lista', icon: Briefcase },
                { key: 'schedule', label: 'Agenda', icon: Calendar },
                { key: 'contractors', label: 'Empresas', icon: Building2 },
              ] as const).map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setPrestadorView(key)}
                  className={cn(
                    'flex items-center gap-1 px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors',
                    prestadorView === key
                      ? 'border-orange-500 text-orange-300'
                      : 'border-transparent text-gray-500 hover:text-gray-300'
                  )}
                >
                  <Icon className="h-3 w-3" />
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* Prestadores status filter pills */}
          {selectedCadastro === 'prestadores' && prestadorView === 'list' && (
            <div className="flex gap-1.5 mb-2 flex-wrap flex-shrink-0">
              {([
                { key: 'all', label: 'Todos', icon: null },
                { key: 'inside', label: 'Dentro', icon: CheckCircle },
                { key: 'awaiting_entry', label: 'Aguardando', icon: LogIn },
                { key: 'expired', label: 'Não entrou', icon: XCircle },
                { key: 'exited', label: 'Saiu', icon: LogOut },
              ] as const).map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setPrestadorStatusFilter(key)}
                  className={cn(
                    'flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors',
                    prestadorStatusFilter === key
                      ? lightTheme ? 'bg-orange-100 border-orange-300 text-orange-700' : 'bg-orange-500/20 border-orange-500/50 text-orange-300'
                      : lightTheme ? 'bg-white border-gray-300 text-gray-600 hover:text-gray-800 hover:bg-gray-50' : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-gray-200'
                  )}
                >
                  {Icon && <Icon className="h-3 w-3" />}
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* Prestadores schedule view */}
          {selectedCadastro === 'prestadores' && prestadorView === 'schedule' && (
            <div className="flex-1 overflow-auto min-h-0 custom-scrollbar">
              <ScheduleView items={cadastroData as any} onOpenPerson={(id) => onPersonClick?.(id)} />
            </div>
          )}

          {/* Prestadores contractors view */}
          {selectedCadastro === 'prestadores' && prestadorView === 'contractors' && customerId && (
            <div className="flex-1 overflow-auto min-h-0 custom-scrollbar">
              <ContractorsView customerId={customerId} />
            </div>
          )}

          {/* Horizontal flex wrapper — adds right panel only for visitantes */}
          {(selectedCadastro !== 'prestadores' || prestadorView === 'list') && (
          <div className="flex-1 flex min-h-0 gap-3">
            <div className="flex-1 flex flex-col min-h-0">
              <div ref={cadastroGridRef} className="flex-1 overflow-auto min-h-0 custom-scrollbar">
                {isLoadingCadastro ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
                  </div>
                ) : !customerId ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500 text-sm">
                    <Building2 className="h-12 w-12 text-gray-700 mb-3" />
                    <p>Selecione um cliente para ver os dados</p>
                  </div>
                ) : showLiberacoes ? (
                  liberacoesPage.rows.length === 0
                    && liberacoesPage.unmatched.length === 0
                    && endedLiberacoesRows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500 text-sm">
                      <UserCheck className="h-12 w-12 text-gray-700 mb-3" />
                      <p>
                        {cadastroSearchTerm
                          ? `Nenhuma liberação encontrada para "${cadastroSearchTerm}"`
                          : 'Nenhuma liberação ativa'}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Liberações são criadas pelo morador no app ou em "+ Liberar", na unidade.
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* One card per liberação — a person liberado para várias unidades repeats, once per unit */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-1.5 md:gap-2">
                        {liberacoesPage.rows.map((row) => renderLiberacaoCard(row))}
                      </div>
                      {/* Two different gaps between the count above and the cards below, and the
                          portaria can only act on one of them.

                          ENDED: read perfectly well, and over — a visita única for a day that has
                          passed. The server's filter cannot see those (such a role usually carries
                          no valid_until, so no SQL expires it) and keeps returning them as
                          liberados. Nothing to open, nothing to fix at the gate.

                          UNREADABLE: the server picked them and this screen found no unit or
                          period on any role. That one IS worth opening the cadastro for. Lumping
                          the two together sent the portaria to check twenty records with nothing
                          wrong with them. */}
                      {endedLiberacoesRows.length > 0 && (
                        <div className="mt-3 px-1">
                          {/* A way to look, not a wall of text. Off by default: the portaria's
                              question is who may come in now. */}
                          <button
                            type="button"
                            onClick={() => setShowEndedLiberacoes((open) => !open)}
                            className={cn(
                              'inline-flex items-center gap-1.5 text-xs font-medium rounded px-2 py-1 border transition-colors',
                              lightTheme
                                ? 'text-gray-600 border-gray-200 hover:bg-gray-100'
                                : 'text-gray-400 border-gray-700 hover:bg-gray-800',
                            )}
                          >
                            <History className="h-3 w-3" />
                            {showEndedLiberacoes
                              ? `Ocultar encerradas (${endedLiberacoesRows.length})`
                              : `Ver encerradas (${endedLiberacoesRows.length})`}
                          </button>

                          {showEndedLiberacoes && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-1.5 md:gap-2 mt-2">
                              {endedLiberacoesRows.map((row) => renderLiberacaoCard(row, row.endedLabel))}
                            </div>
                          )}
                        </div>
                      )}
                      {liberacoesPage.unmatched.length > 0 && (
                        <p className={cn('text-xs mt-1 px-1', lightTheme ? 'text-amber-700' : 'text-amber-400')}>
                          <AlertTriangle className="h-3 w-3 inline mr-1" />
                          {liberacoesPage.unmatched.length === 1
                            ? '1 pessoa liberada sem unidade ou período no cadastro — abra o cadastro para ver.'
                            : `${liberacoesPage.unmatched.length} pessoas liberadas sem unidade ou período no cadastro — abra o cadastro para ver.`}
                        </p>
                      )}
                    </>
                  )
                ) : paginatedCadastroData.length > 0 ? (
                  <>
                    {/* Grid Layout for Cadastro - Responsive columns */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-1.5 md:gap-2">
                      {paginatedCadastroData.map((item, index) => renderCadastroItem(item, index))}
                    </div>
                    {/* Vehicle infinite scroll loading indicator */}
                    {selectedCadastro === 'veiculos' && vehicleIsLoadingMore && (
                      <div className="flex justify-center py-4">
                        <Loader2 className={cn("h-5 w-5 animate-spin", lightTheme ? "text-gray-400" : "text-gray-500")} />
                      </div>
                    )}
                    {selectedCadastro === 'veiculos' && !vehicleHasMore && cadastroData.length > 0 && (
                      <p className="text-center text-xs text-gray-700 py-3">
                        {(cadastroTotalItems ?? 0).toLocaleString('pt-BR')} veículos carregados
                      </p>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500 text-sm">
                    <p>
                      {cadastroSearchTerm
                        ? `Nenhum resultado encontrado para "${cadastroSearchTerm}"`
                        : `Nenhum ${getCadastroLabel().toLowerCase()} cadastrado`}
                    </p>
                  </div>
                )}
              </div>

              {/* Pagination Controls for Cadastro - Outside scrollable area (not used for veiculos which has infinite scroll) */}
              {cadastroTotalPages > 1 && selectedCadastro !== 'veiculos' && selectedCadastro !== 'animais' && (
                <div className={cn("flex items-center justify-between mt-3 pt-3 border-t flex-shrink-0", lightTheme ? "border-gray-200" : "border-gray-800")}>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCadastroPage(1)}
                      disabled={cadastroPage === 1}
                      className={cn("px-2 py-1 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs", lightTheme ? "bg-gray-100 hover:bg-gray-200 text-gray-700" : "bg-gray-800 hover:bg-gray-700 text-gray-300")}
                    >
                      Primeira
                    </button>
                    <button
                      onClick={() => setCadastroPage(p => Math.max(1, p - 1))}
                      disabled={cadastroPage === 1}
                      className={cn("p-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors", lightTheme ? "bg-gray-100 hover:bg-gray-200" : "bg-gray-800 hover:bg-gray-700")}
                    >
                      <ChevronLeft className={cn("h-4 w-4", lightTheme ? "text-gray-600" : "text-gray-300")} />
                    </button>
                  </div>

                  <span className={cn("text-xs font-medium", lightTheme ? "text-gray-700" : "text-gray-400")}>
                    Página {cadastroPage} de {cadastroTotalPages}
                    <span className={cn("ml-2", lightTheme ? "text-gray-500" : "text-gray-600")}>
                      ({(cadastroTotalItems ?? 0).toLocaleString('pt-BR')} total)
                    </span>
                  </span>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCadastroPage(p => Math.min(cadastroTotalPages, p + 1))}
                      disabled={cadastroPage === cadastroTotalPages}
                      className={cn("p-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors", lightTheme ? "bg-gray-100 hover:bg-gray-200" : "bg-gray-800 hover:bg-gray-700")}
                    >
                      <ChevronRight className={cn("h-4 w-4", lightTheme ? "text-gray-600" : "text-gray-300")} />
                    </button>
                    <button
                      onClick={() => setCadastroPage(cadastroTotalPages)}
                      disabled={cadastroPage === cadastroTotalPages}
                      className={cn("px-2 py-1 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs", lightTheme ? "bg-gray-100 hover:bg-gray-200 text-gray-700" : "bg-gray-800 hover:bg-gray-700 text-gray-300")}
                    >
                      Última
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Right summary panel — only for visitantes */}
            {selectedCadastro === 'moradores' && customerId && canApproveSelfRegistrations && (
            <MoradoresSelfRegSidebar customerId={customerId} lightTheme={lightTheme} condoType={condoType} />
          )}

          {selectedCadastro === 'visitantes' && (
              <div className={cn("w-52 flex-shrink-0 flex flex-col gap-2.5 border-l pl-3 pt-0.5", lightTheme ? "border-gray-200" : "border-gray-800/50")}>
                {/* Situação — "quem está liberado agora" */}
                <div>
                  <p className={cn("text-xs font-bold uppercase tracking-widest mb-1.5", lightTheme ? "text-gray-500" : "text-gray-400")}>Situação</p>
                  <div className="flex gap-1">
                    {([
                      { value: 'all', label: 'Todos' },
                      { value: 'liberado', label: 'Liberados' },
                    ] as const).map(({ value, label }) => (
                      <button
                        key={value}
                        onClick={() => {
                          setVisitLiberadoFilter(value);
                          // The unidade filter only exists in Liberados; leaving it set while it is
                          // off-screen would silently narrow the list on the way back.
                          if (value === 'all') setVisitUnitId('');
                        }}
                        title={value === 'liberado'
                          ? 'Liberações ativas: um cartão por unidade, com período e regra de interfone. Quem está liberado para várias unidades aparece uma vez em cada.'
                          : 'Todos os visitantes cadastrados'}
                        className={cn(
                          "flex-1 py-1 rounded-lg text-xs font-semibold transition-all border",
                          visitLiberadoFilter === value
                            ? value === 'liberado'
                              ? lightTheme
                                ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                                : "bg-emerald-500/30 text-emerald-300 border-emerald-500/40"
                              : lightTheme
                                ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                                : "bg-blue-500/30 text-blue-300 border-blue-500/40"
                            : lightTheme
                              ? "bg-white text-gray-600 border-gray-300 hover:bg-gray-100 hover:text-gray-900"
                              : "bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200 border-gray-700/50"
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Role type filter */}
                <div>
                  <p className={cn("text-xs font-bold uppercase tracking-widest mb-1.5", lightTheme ? "text-gray-500" : "text-gray-400")}>Tipo</p>
                  <div className="flex gap-1">
                    {([
                      { value: 'all', label: 'Todos' },
                      { value: 'visitor', label: 'Visitante' },
                      { value: 'service_provider', label: 'Prestador' },
                    ] as const).map(({ value, label }) => (
                      <button
                        key={value}
                        onClick={() => setVisitRoleFilter(value)}
                        className={cn(
                          "flex-1 py-1 rounded-lg text-xs font-semibold transition-all border",
                          visitRoleFilter === value
                            ? lightTheme
                              ? value === 'service_provider' ? "bg-orange-500 text-white border-orange-500 shadow-sm" : "bg-blue-600 text-white border-blue-600 shadow-sm"
                              : value === 'service_provider' ? "bg-orange-500/30 text-orange-300 border-orange-500/40" : "bg-blue-500/30 text-blue-300 border-blue-500/40"
                            : lightTheme
                              ? "bg-white text-gray-600 border-gray-300 hover:bg-gray-100 hover:text-gray-900"
                              : "bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200 border-gray-700/50"
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Unidade — only in Liberados, where a row IS a unit. In the full list a person
                    card covers every unit they ever visited, so "filtrar por unidade" there would
                    have to pick one of them arbitrarily. */}
                {showLiberacoes && visitUnits.length > 0 && (
                  <div>
                    <p className={cn("text-xs font-bold uppercase tracking-widest mb-1.5", lightTheme ? "text-gray-500" : "text-gray-400")}>Unidade</p>
                    <FilterCombobox
                      value={visitUnitId}
                      onChange={v => { setVisitUnitId(v); setCadastroPage(1); }}
                      options={visitUnits.map(u => ({ value: u.id, label: u.label }))}
                      allLabel="Todas as unidades"
                      searchPlaceholder={`Buscar ${getUnitLabel(condoType).toLowerCase()}...`}
                      dark={!lightTheme}
                      className="w-full"
                    />
                  </div>
                )}

                <p className={cn("text-xs font-bold uppercase tracking-widest", lightTheme ? "text-gray-500" : "text-gray-400")}>Filtrar por período</p>

                {/* Period filter pills */}
                <div className="flex flex-wrap gap-1">
                  {(['all', 'today', 'yesterday', '7d', '30d', 'custom'] as const).map(f => (
                    <button
                      key={f}
                      onClick={() => setVisitDateFilter(f)}
                      className={cn(
                        "px-2.5 py-1 rounded-full text-xs font-semibold transition-all border",
                        visitDateFilter === f
                          ? lightTheme
                            ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                            : "bg-blue-500/30 text-blue-300 border-blue-500/40"
                          : lightTheme
                            ? "bg-white text-gray-600 border-gray-300 hover:bg-gray-100 hover:text-gray-900 hover:border-gray-400"
                            : "bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200 border-gray-700/50"
                      )}
                    >
                      {f === 'all' ? 'Todos' : f === 'today' ? 'Hoje' : f === 'yesterday' ? 'Ontem' : f === '7d' ? '7 dias' : f === '30d' ? '30 dias' : 'Período'}
                    </button>
                  ))}
                </div>

                {/* Custom date inputs */}
                {visitDateFilter === 'custom' && (
                  <div className="flex flex-col gap-1.5">
                    <input
                      type="date"
                      value={visitCustomStart}
                      onChange={e => setVisitCustomStart(e.target.value)}
                      className={cn("w-full px-2 py-1 text-xs rounded-lg border focus:outline-none focus:border-blue-500", lightTheme ? "bg-white text-gray-800 border-gray-300" : "bg-gray-950 text-gray-200 border-gray-800")}
                    />
                    <input
                      type="date"
                      value={visitCustomEnd}
                      onChange={e => setVisitCustomEnd(e.target.value)}
                      className={cn("w-full px-2 py-1 text-xs rounded-lg border focus:outline-none focus:border-blue-500", lightTheme ? "bg-white text-gray-800 border-gray-300" : "bg-gray-950 text-gray-200 border-gray-800")}
                    />
                  </div>
                )}

                {/* Active filter indicator */}
                {visitDateFilter !== 'all' && (
                  <p className={cn("text-xs -mt-1 font-medium", lightTheme ? "text-blue-600" : "text-blue-400/80")}>
                    Grade filtrada pelo período selecionado
                  </p>
                )}

                {/* Stats — counts come from server via cadastroTotalItems */}
                <div className="flex flex-col gap-2 mt-1">
                  <div className={cn("rounded-xl p-3 border", lightTheme ? "bg-blue-50 border-blue-100" : "bg-gray-800/50 border-gray-700/30")}>
                    <p className={cn("text-xs mb-1 font-medium", lightTheme ? "text-blue-600" : "text-gray-500")}>
                      {showLiberacoes
                        ? 'Pessoas liberadas'
                        : visitDateFilter === 'all' ? 'Total de Visitantes' : 'Visitantes no período'}
                    </p>
                    {isLoadingCadastro ? (
                      <Loader2 className={cn("h-5 w-5 animate-spin", lightTheme ? "text-blue-500" : "text-blue-400")} />
                    ) : (
                      <p className={cn("text-2xl font-bold", lightTheme ? "text-blue-700" : "text-blue-400")}>{(cadastroTotalItems ?? 0).toLocaleString('pt-BR')}</p>
                    )}
                  </div>
                  <div className={cn("rounded-xl p-3 border", lightTheme ? "bg-gray-50 border-gray-200" : "bg-gray-800/50 border-gray-700/30")}>
                    <p className={cn("text-xs mb-1 font-medium", lightTheme ? "text-gray-500" : "text-gray-500")}>Página atual</p>
                    <p className={cn("text-lg font-bold", lightTheme ? "text-gray-800" : "text-gray-300")}>
                      {showLiberacoes
                        ? `${liberacoesPage.rows.length} ${liberacoesPage.rows.length === 1 ? 'liberação' : 'liberações'}`
                        : `${paginatedCadastroData.length} visitantes`}
                    </p>
                    <p className={cn("text-xs mt-0.5", lightTheme ? "text-gray-500" : "text-gray-600")}>pág. {cadastroPage} de {cadastroTotalPages}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          )} {/* end list-only wrapper */}
        </>
      )}
      </div>

      {/* Photo Zoom Modal - Centered, compact with update option */}
      {zoomedPhoto && (
        <div
          onClick={() => setZoomedPhoto(null)}
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-200 cursor-zoom-out"
        >
          <div
            className="relative w-[500px] max-w-[90vw] max-h-[90vh] bg-gray-900 rounded-xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Name header */}
            <div className="px-4 py-3 flex items-center justify-center border-b border-gray-700">
              <p style={forceUpper ? { textTransform: 'uppercase' } : undefined} className="text-white text-base font-semibold">{zoomedPhoto.name}</p>
            </div>

            {/* Zoomed photo */}
            <div className="w-full h-[500px] max-h-[70vh] flex items-center justify-center bg-black/30">
              <img
                src={zoomedPhoto.url}
                alt={zoomedPhoto.name}
                className="max-w-full max-h-full object-contain"
              />
            </div>

            {/* Action buttons */}
            <div className="p-4 bg-gray-800/90 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => {
                  setShowPhotoActionModal(true);
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-semibold transition-all duration-200 hover:scale-105"
              >
                <Camera className="h-4 w-4" />
                Atualizar Foto
              </button>
              <button
                onClick={() => setZoomedPhoto(null)}
                className="px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Prestador Modal */}
      {customerId && (
        <VisitorRegistrationModal
          open={showNewPrestadorModal}
          customerId={customerId}
          selectedCustomer={{ type: customerType, mobileAppEnabled }}
          onClose={() => setShowNewPrestadorModal(false)}
          onSuccess={() => {
            setShowNewPrestadorModal(false);
            setCadastroPage(1);
          }}
          darkMode={!lightTheme}
        />
      )}

      {/* Vehicle Detail Modal */}
      {customerId && (
        <VehicleDetailModal
          open={showVehicleDetailModal}
          vehicle={editingVehicle}
          customerId={customerId}
          customerType={customerType}
          condoType={condoType}
          onClose={() => { setShowVehicleDetailModal(false); setEditingVehicle(null); }}
          onEdit={(v) => { setShowVehicleDetailModal(false); setShowVehicleModal(true); }}
          onSync={(v) => handleSyncVehicle(v as any)}
          isSyncing={!!(editingVehicle && syncingVehicleId === editingVehicle.id)}
          onRevoke={(v) => handleRevokeVehicle(v as any)}
          isRevoking={!!(editingVehicle && revokingVehicleId === editingVehicle.id)}
          onDelete={(v) => { setShowVehicleDetailModal(false); handleDeleteVehicle(v.id, v.plate); }}
          onPersonClick={onPersonClick}
          onUnitClick={onLocationClick ? (unitId) => { setShowVehicleDetailModal(false); onLocationClick(unitId, 'unit'); } : undefined}
          lightTheme={lightTheme}
        />
      )}

      {/* Vehicle Form Modal */}
      {customerId && customerType && (
        <VehicleFormModal
          open={showVehicleModal}
          onClose={() => {
            setShowVehicleModal(false);
            setEditingVehicle(null);
          }}
          onSuccess={() => {
            setVehicleRefreshTrigger(prev => prev + 1);
          }}
          customerId={customerId}
          customerType={customerType}
          condoType={condoType}
          editingVehicle={editingVehicle}
          darkMode={!lightTheme}
        />
      )}

      {/* Pet Detail Modal — opened when clicking an existing pet */}
      {customerId && viewingPet && (
        <PetDetailModal
          pet={viewingPet}
          customerId={customerId}
          condoType={condoType}
          onClose={() => setViewingPet(null)}
          onPetUpdated={() => { setPetRefreshTrigger(prev => prev + 1); }}
          onPetDeleted={() => { setViewingPet(null); setPetRefreshTrigger(prev => prev + 1); }}
          lightTheme={lightTheme}
        />
      )}

      {/* Pet Form Modal — only for creating new pets */}
      {customerId && showPetModal && (
        <PetFormModal
          customerId={customerId}
          pet={editingPet}
          onSuccess={() => {
            setPetRefreshTrigger(prev => prev + 1);
            setShowPetModal(false);
            setEditingPet(null);
          }}
          onClose={() => {
            setShowPetModal(false);
            setEditingPet(null);
          }}
          lightTheme={lightTheme}
        />
      )}

      {/* Tag Form Modal */}
      {customerId && customerType && (
        <TagFormModal
          open={showTagModal}
          onClose={() => {
            setShowTagModal(false);
            setEditingTag(null);
          }}
          onSuccess={() => {
            setTagRefreshTrigger(prev => prev + 1);
            setShowTagModal(false);
            setEditingTag(null);
          }}
          customerId={customerId}
          customerType={customerType}
          editingTag={editingTag}
          condoType={condoType}
          darkMode={!lightTheme}
        />
      )}

      {/* Block/Quadra Form Modal */}
      {showBlockForm && customerId && (
        <BlockFormModal
          customerId={customerId}
          condoType={condoType}
          onClose={() => setShowBlockForm(false)}
          onCreated={() => setBlockRefreshTrigger(prev => prev + 1)}
        />
      )}

      {/* Photo Action Modal - Choose photo source */}
      {zoomedPhoto && (
        <PhotoActionModal
          open={showPhotoActionModal}
          onClose={() => setShowPhotoActionModal(false)}
          onUpload={() => {
            setShowPhotoActionModal(false);
            setShowPhotoUploadModal(true);
          }}
          onWebcam={() => {
            setShowPhotoActionModal(false);
            setShowPhotoWebcamModal(true);
          }}
          onIPCamera={() => {
            setShowPhotoActionModal(false);
            setShowPhotoIPCameraModal(true);
          }}
          title="Atualizar Foto do Visitante"
          darkMode={true}
        />
      )}

      {/* Photo Upload Modal (File Upload) */}
      {zoomedPhoto && (
        <PhotoUploadModal
          open={showPhotoUploadModal}
          onClose={() => setShowPhotoUploadModal(false)}
          onConfirm={async (result) => {
            try {
              // Get existing photos and delete primary
              const photosResponse = await api.get(`/people/${zoomedPhoto.personId}/photos`);
              const existingPhotos = photosResponse.data?.photos || photosResponse.data || [];
              const primaryPhoto = existingPhotos.find((p: any) => p.is_primary);
              if (primaryPhoto) {
                await api.delete(`/people/${zoomedPhoto.personId}/photos/${primaryPhoto.id}`);
              }

              // Upload new photo
              const formData = new FormData();
              appendIdentityPhoto(formData, result, 'identity_photo');
              formData.append('customer_id', customerId);
              formData.append('is_primary', 'true');

              await api.post(`/people/${zoomedPhoto.personId}/photos`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });

              toast.success('Foto atualizada com sucesso!');
              setShowPhotoUploadModal(false);
              setZoomedPhoto(null);
              if (onRefreshVisitors) onRefreshVisitors();
            } catch (error: any) {
              console.error('Error updating photo:', error);
              toast.error(error.response?.data?.message || 'Erro ao atualizar foto');
            }
          }}
          title="Atualizar Foto do Visitante"
          cropShape="rect"
          darkMode={true}
        />
      )}

      {/* Photo Webcam Modal */}
      {zoomedPhoto && (
        <PhotoWebcamModal
          open={showPhotoWebcamModal}
          onClose={() => setShowPhotoWebcamModal(false)}
          onConfirm={async (result) => {
            try {
              // Get existing photos and delete primary
              const photosResponse = await api.get(`/people/${zoomedPhoto.personId}/photos`);
              const existingPhotos = photosResponse.data?.photos || photosResponse.data || [];
              const primaryPhoto = existingPhotos.find((p: any) => p.is_primary);
              if (primaryPhoto) {
                await api.delete(`/people/${zoomedPhoto.personId}/photos/${primaryPhoto.id}`);
              }

              // Upload new photo
              const formData = new FormData();
              appendIdentityPhoto(formData, result, 'identity_photo');
              formData.append('customer_id', customerId);
              formData.append('is_primary', 'true');

              await api.post(`/people/${zoomedPhoto.personId}/photos`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });

              toast.success('Foto atualizada com sucesso!');
              setShowPhotoWebcamModal(false);
              setZoomedPhoto(null);
              if (onRefreshVisitors) onRefreshVisitors();
            } catch (error: any) {
              console.error('Error updating photo:', error);
              toast.error(error.response?.data?.message || 'Erro ao atualizar foto');
            }
          }}
          title="Atualizar Foto do Visitante"
          cropShape="rect"
          darkMode={true}
        />
      )}

      {/* Photo IP Camera Modal */}
      {zoomedPhoto && customerId && (
        <PhotoIPCameraModal
          open={showPhotoIPCameraModal}
          onClose={() => setShowPhotoIPCameraModal(false)}
          onConfirm={async (result) => {
            try {
              // Get existing photos and delete primary
              const photosResponse = await api.get(`/people/${zoomedPhoto.personId}/photos`);
              const existingPhotos = photosResponse.data?.photos || photosResponse.data || [];
              const primaryPhoto = existingPhotos.find((p: any) => p.is_primary);
              if (primaryPhoto) {
                await api.delete(`/people/${zoomedPhoto.personId}/photos/${primaryPhoto.id}`);
              }

              // Upload new photo
              const formData = new FormData();
              appendIdentityPhoto(formData, result, 'identity_photo');
              formData.append('customer_id', customerId);
              formData.append('is_primary', 'true');

              await api.post(`/people/${zoomedPhoto.personId}/photos`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });

              toast.success('Foto atualizada com sucesso!');
              setShowPhotoIPCameraModal(false);
              setZoomedPhoto(null);
              if (onRefreshVisitors) onRefreshVisitors();
            } catch (error: any) {
              console.error('Error updating photo:', error);
              toast.error(error.response?.data?.message || 'Erro ao atualizar foto');
            }
          }}
          title="Atualizar Foto do Visitante"
          customerId={customerId}
          cropShape="rect"
          darkMode={true}
        />
      )}

      {/* Prestador window / empresa edit modal */}
      {prestadorEditState && customerId && (
        <WindowEditModal
          state={prestadorEditState}
          customerId={customerId}
          onClose={() => setPrestadorEditState(null)}
          onSave={handlePrestadorWindowSave}
        />
      )}
    </>
  );
};


// Wrap in React.memo for performance
export const MainContentArea = React.memo(MainContentAreaComponent);
